import { SidebarNavButton } from "@/components/ui/sidebar-nav-button";
import { UserPermissionsField } from "@/components/ui/user-permissions-field";
import { CategoryFormSelect } from "@/components/ui/category-form-select";
import { CategoryLabeledFilterSelect } from "@/components/ui/category-labeled-filter-select";
import { FilterSelect } from "@/components/ui/filter-select";
import { LabeledFilterSelect } from "@/components/ui/labeled-filter-select";
import { PeriodFilterSelect } from "@/components/ui/period-filter-select";
import { Select } from "@/components/ui/select";
import { Autocomplete } from "@/components/ui/autocomplete";
import { TableSearchInput } from "@/components/ui/table-search-input";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { MoneyInput } from "@/components/ui/money-input";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { AlertCircle, ArrowDown, ArrowUp, ShoppingCart, Package, LogOut, Plus, Clock, User, Calendar, Loader2, Trash2, X, Trash, Box, Layers, Users, Receipt, MoreVertical, Eye, EyeOff, Pencil, ChevronDown, ChevronRight, ChevronLeft, ClipboardList, Megaphone, Menu, Printer, Home as HomeIcon, Upload, Download, Copy, Settings, Key, UtensilsCrossed, Building2, Briefcase, BookOpen, ConciergeBell, Check } from "lucide-react";
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, Fragment } from "react";
import InputMask from "react-input-mask";
import { useLocation } from "wouter";
import { api, isAbortError, type Insumo, type Conferencia, type ConferenciaAlteracao, type Produto, type ProdutoComposicaoItem, type Venda, type DesperdicioListItem, type Despesa, type Promocao, type FornecedorLancamento } from "@/lib/api";
import {
  buildDashboardCustoContext,
  calcularCustoProdutosVendidos,
  calcularLucroDashboard,
} from "@/lib/dashboardMetrics";
import { loadDashboardData } from "@/lib/dashboardLoad";
import { getDashboardPeriodoRange, type DashboardPeriodo } from "@/lib/dashboardPeriod";
import { allowedTabsToDisplayLabels } from "@/lib/userPermissions";
import { savePostLoginRedirect } from "@/lib/authRedirect";
import { LoginScreen } from "@/components/auth/LoginScreen";
import { isDescricaoRegistroCompras, normalizeDescricaoDespesa } from "@/lib/financeiroDespesaUtils";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { vendaMatchesAtendimento } from "@/lib/vendaAtendimento";
import { textIncludesSearch } from "@/lib/searchText";
import {
  DEFAULT_FINANCEIRO_SUB,
  getFallbackTab,
  normalizeAppPath,
  pathForTab,
  tabFromPath,
  parseProdutoFormPath,
  DEFAULT_CONFIG_SUB,
  type ConfigSubTab,
  type FinanceiroSubTab,
  type HomeActiveTab,
  type PathForTabOptions,
} from "@/lib/homeRoutes";
import ProdutoFormPage from "@/pages/ProdutoFormPage";
import { ParceirosTab } from "@/pages/ParceirosTab";
import AlterarSenhaPage from "@/pages/AlterarSenhaPage";
import MeuPerfilPage from "@/pages/MeuPerfilPage";
import { parseNumeroBR } from "@/lib/formatBrl";
import { formatQuantidadeInput, parseQuantidadeInput } from "@/lib/quantidadeInput";
import { getVisibleSelectionState, toggleVisibleSelection } from "@/lib/reposicaoSelection";
import { parseMoneyMask, numberToMoneyMask } from "@/lib/moneyMask";
import {
  FORNECEDOR_MAX_PARCELAS,
  FORNECEDOR_MIN_PARCELAS,
  criarParcelasFormEstrutura,
  diffCentavosParcelas,
  parseQuantidadeParcelas,
  quantidadeParcelasValida,
  somaParcelasValor,
  valoresParcelasIguaisTotal,
  type ParcelaFormRow,
} from "@/lib/fornecedorParcelamento";
import { BTN_PAGE_PRIMARY_ACTION, BTN_PAGE_PRIMARY_ACTION_MARKETING, BTN_PAGE_PRIMARY_ACTION_ROW, BTN_PAGE_PRIMARY_APPEARANCE } from "@/lib/buttonStyles";
import { gerarCupomHtml, imprimirCupom } from "@/lib/cupom";
import { ClientLogo } from "@/components/ClientLogo";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { receiptToEmpresaCupom } from "@/lib/clientConfig";
import { buildFormDraftKey, clearFormDraft, loadFormDraft, saveFormDraft } from "@/lib/formDraft";
import {
  parseMySQLDateLocal,
  formatarDataLocal,
  formatarHoraLocal,
  parseDateTimeLocal,
  getTodaySaoPaulo,
  addDaysIso,
  diffCalendarDaysVencimentoHojeSP,
} from "@/lib/dateUtils";
import {
  CLIENT_META_ORANGE,
  CLIENT_META_TRACK,
  calcularBarraMetaMensal,
  montarLinhasModal,
  totalFaturamentoVendas,
  getMesAtualRangeIsoSP,
} from "@/lib/monthlyGoalFaturamento";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/appToast";
import { ModalImportarOfxFinanceiro } from "@/components/ModalImportarOfxFinanceiro";
import { ModalImportarNfFornecedor, type FornecedorNfPrefill } from "@/components/ModalImportarNfFornecedor";
import { AiStockEntradaFlow } from "@/components/estoque/AiStockEntradaFlow";
import { ModalBackdrop } from "@/components/ModalBackdrop";
import { ModalPeriodoPersonalizado } from "@/components/ModalPeriodoPersonalizado";
import { ModalExportarVendasFiscal, type ExportarVendasFiscalParams } from "@/components/ModalExportarVendasFiscal";
import { StandardModal } from "@/components/StandardModal";
import { PageContentLoader } from "@/components/PageContentLoader";
import { ResponsiveChartFrame } from "@/components/ResponsiveChartFrame";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import {
  MODAL_BODY_CLASS,
  MODAL_HEADER_ROW_CLASS,
  MODAL_OVERLAY_CLASS,
  MODAL_PANEL_CLASS,
  MODAL_PANEL_MAX_HEIGHT_CLASS,
  MODAL_TITLE_CLASS,
  PAGE_TITLE_CLASS,
  TABLE_HEAD_CLASS,
  TABLE_TH_CLASS,
} from "@/lib/modalStyles";
import { ActionMenuPortal } from "@/components/ActionMenuPortal";
import { AreaChart, Area, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  baixarBlob,
  buildVendasFiscalXlsxBlob,
  buildVendasFiscalXmlBlob,
  nomeArquivoVendasFiscal,
} from "@/lib/vendasFiscalExport";
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value ?? 0);
}

function formatProdutoCustoExibicao(valorAtual: number | null | undefined): string {
  if (valorAtual == null || !Number.isFinite(valorAtual) || valorAtual <= 0) return "—";
  return formatBRL(valorAtual);
}

function formatValorEmEstoque(custoUnitario: number | null | undefined, quantidade: number): string {
  if (custoUnitario == null || !Number.isFinite(custoUnitario) || !Number.isFinite(quantidade)) return "—";
  return formatBRL(quantidade * custoUnitario);
}

/** Marcações do eixo X do card Produtos Mais Vendidos (0 até o maior valor, sem arredondar). */
function dashboardProdutosXAxisTicks(max: number): number[] {
  if (max <= 0) return [0];
  if (max <= 5) return Array.from({ length: max + 1 }, (_, i) => i);
  const step = Math.max(1, Math.round(max / 4));
  const ticks = new Set<number>([0, max]);
  for (let v = step; v < max; v += step) {
    if (max - v >= step) ticks.add(v);
  }
  return Array.from(ticks).sort((a, b) => a - b);
}

const DASHBOARD_PRODUTOS_TOOLTIP_OFFSET_PX = 12;

function DashboardProdutosBarWithTooltip({
  nome,
  quantidade,
  pct,
  animationDelayMs,
}: {
  nome: string;
  quantidade: number;
  pct: number;
  animationDelayMs: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const updateTooltipPos = useCallback((e: React.MouseEvent) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const barWidthPct = Math.max(pct, 0);

  return (
    <div ref={trackRef} className="relative min-w-0 h-[10px] w-full">
      <div className="h-[10px] w-full overflow-hidden bg-transparent">
        <div
          className="dashboard-produtos-bar h-[10px] rounded-none bg-client-primary transition-opacity hover:opacity-90"
          style={
            {
              ["--bar-width" as string]: `${barWidthPct}%`,
              animationDelay: `${animationDelayMs}ms`,
            } as React.CSSProperties
          }
          onMouseEnter={(e) => {
            setHover(true);
            updateTooltipPos(e);
          }}
          onMouseMove={(e) => {
            setHover(true);
            updateTooltipPos(e);
          }}
          onMouseLeave={() => setHover(false)}
        />
      </div>
      {hover ? (
        <div
          className="pointer-events-none absolute z-10 whitespace-nowrap rounded-sm border border-[#ccc] bg-white px-2.5 py-2.5 text-xs shadow-sm"
          style={{
            left: pos.x + DASHBOARD_PRODUTOS_TOOLTIP_OFFSET_PX,
            top: pos.y - DASHBOARD_PRODUTOS_TOOLTIP_OFFSET_PX,
            transform: "translateY(-100%)",
          }}
          role="tooltip"
        >
          <p className="m-0 text-[14px] font-normal leading-snug text-[#000]">{nome}</p>
          <p className="m-0 pt-1 text-[14px] font-normal leading-snug text-[#000]">Vendas: {quantidade}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Horário inicial/final (inclusivo) padrão do gráfico "Vendas por Hora" no Dashboard. */
const DASHBOARD_VENDAS_HORA_INICIO = 6;
const DASHBOARD_VENDAS_HORA_FIM = 21;

function parseHorarioHora(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):00$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  if (!Number.isInteger(h) || h < 0 || h > 23) return null;
  return h;
}

function buildVendasHoraTicks(inicio: number, fim: number, step: number): string[] {
  const ticks: string[] = [];
  for (let h = inicio; h <= fim; h += step) {
    ticks.push(`${String(h).padStart(2, "0")}:00`);
  }
  const last = `${String(fim).padStart(2, "0")}:00`;
  if (!ticks.includes(last)) ticks.push(last);
  return ticks;
}

const DESKTOP_SIDEBAR_STORAGE_KEY = "monalu_desktop_sidebar_collapsed";

const DESKTOP_SIDEBAR_DOTTED_BORDER_STYLE: React.CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0, rgba(0, 0, 0, 0.15) 4px, transparent 4px, transparent 8px)",
  backgroundSize: "1px 100%",
  backgroundPosition: "right top",
  backgroundRepeat: "no-repeat",
};

/** Espaçamento vertical uniforme entre cards do menu lateral. */
const SIDEBAR_NAV_LIST_GAP = "erp-sidebar-nav flex flex-col gap-1";
const SIDEBAR_NAV_SUBMENU_GAP = "mt-1 flex shrink-0 flex-col gap-1";
/** Item lateral inativo: hover igual à aba ativa (secondary + primary). */
const SIDEBAR_NAV_ITEM_ACTIVE = "bg-client-secondary text-client-primary font-semibold";
const SIDEBAR_NAV_ITEM_INACTIVE =
  "group text-gray-600/90 hover:bg-client-secondary hover:text-client-primary";
const SIDEBAR_NAV_ICON_INACTIVE = "opacity-70 group-hover:opacity-100";

function readDesktopSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(DESKTOP_SIDEBAR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Layout compartilhado dos cards de valor monetário / KPI
 * (Dashboard, Desperdícios, Financeiro, Pagamentos).
 * Borda lateral usa CLIENT_PRIMARY_COLOR (--client-primary).
 */
const KPI_VALUE_CARD = "border-l-4 border-l-client-primary bg-white min-h-[7.75rem] flex flex-col shadow-sm";
const KPI_VALUE_CARD_HEADER = "space-y-0 px-5 pt-5 pb-2";
const KPI_VALUE_CARD_TITLE = "text-sm font-medium";
const KPI_VALUE_CARD_CONTENT = "px-5 pt-0 pb-5 flex flex-1 items-center justify-between gap-3";
const KPI_VALUE_TEXT = "text-3xl font-bold tabular-nums text-foreground";
const FINANCEIRO_KPI_CARD = "border-l-4 border-l-client-primary bg-white min-h-[6rem] flex flex-col shadow-sm";
const FINANCEIRO_KPI_CARD_HEADER = "space-y-0 px-4 pt-3 pb-1";
const FINANCEIRO_KPI_CARD_CONTENT = "px-4 pt-0 pb-3 flex flex-1 items-center justify-between gap-3";
const FINANCEIRO_KPI_VALUE_TEXT = "text-[28px] font-bold tabular-nums text-foreground min-[768px]:text-[25px]";
const KPI_VALUE_EYE_BTN = "shrink-0 p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground";

/** Variante ~12% mais compacta — somente nos 5 KPIs do Dashboard. */
const DASHBOARD_KPI_VALUE_CARD = "border-l-4 border-l-client-primary bg-white min-h-[6.75rem] flex flex-col shadow-sm";
const DASHBOARD_KPI_VALUE_CARD_HEADER = "space-y-0 px-5 pt-4 pb-1.5";
const DASHBOARD_KPI_VALUE_CARD_CONTENT = "px-5 pt-0 pb-4 flex flex-1 items-center gap-3";

const DASHBOARD_VALOR_OCULTO_NUMERICO = "•••••";
const DASHBOARD_VALOR_OCULTO_MONETARIO = "R$ •••••";

const DASHBOARD_MEIOS_PAGAMENTO_OPACIDADES = [0.2, 0.4, 0.6, 1] as const;
const DASHBOARD_MEIOS_PAGAMENTO_EMPTY_DONUT_ROWS = [{ key: "sem-dados", valor: 1, fill: "#E5E7EB" }] as const;

function formatDashboardValorMonetario(valor: number, oculto: boolean): string {
  if (oculto) return DASHBOARD_VALOR_OCULTO_MONETARIO;
  return formatBRL(valor);
}

function formatDashboardLucroMonetario(valor: number, oculto: boolean): string {
  if (oculto) return DASHBOARD_VALOR_OCULTO_MONETARIO;
  if (valor < 0 || Object.is(valor, -0)) {
    return formatBRL(Math.abs(valor)).replace(/^R\$\s*/, "R$ -");
  }
  return formatBRL(valor);
}

function formatDashboardValorNumerico(valor: number, oculto: boolean): string {
  if (oculto) return DASHBOARD_VALOR_OCULTO_NUMERICO;
  return String(valor);
}

function getDashboardPagamentoOpacity(rank: number, total: number): number {
  if (total <= 1) return 1;

  if (total === 2) return rank === 0 ? 0.2 : 1;
  if (total === 3) return [0.2, 0.6, 1][rank] ?? 1;
  if (total === DASHBOARD_MEIOS_PAGAMENTO_OPACIDADES.length) {
    return DASHBOARD_MEIOS_PAGAMENTO_OPACIDADES[rank] ?? 1;
  }

  const distributed = 0.2 + (0.8 * rank) / (total - 1);
  return Math.max(0.2, Math.min(1, distributed));
}

function getDashboardColorWithOpacity(color: string, opacity: number): string {
  const alpha = Math.max(0, Math.min(1, opacity));
  const trimmed = color.trim();
  const shortHex = /^#([0-9a-f]{3})$/i.exec(trimmed);
  if (shortHex) {
    const [r, g, b] = shortHex[1].split("").map((part) => parseInt(part + part, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const longHex = /^#([0-9a-f]{6})$/i.exec(trimmed);
  if (longHex) {
    const value = longHex[1];
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const rgb = /^rgb\(\s*(.+)\s*\)$/i.exec(trimmed);
  if (rgb) return `rgba(${rgb[1]}, ${alpha})`;
  if (/^rgba\(/i.test(trimmed)) return trimmed;
  return `color-mix(in srgb, ${trimmed} ${Math.round(alpha * 100)}%, transparent)`;
}

type DashboardKpiComparacao = {
  direction: "up" | "down";
  percentual: number;
  label: string;
};

type DashboardMeioPagamentoKey = "pix" | "credito" | "debito" | "dinheiro";

type DashboardMeioPagamentoBaseRow = {
  key: DashboardMeioPagamentoKey;
  label: string;
  valor: number;
};

type DashboardMeioPagamentoRow = DashboardMeioPagamentoBaseRow & {
  fill: string;
};

type DashboardMeioPagamentoChartRow = DashboardMeioPagamentoRow & {
  percentual: number;
};

type DashboardMeiosPagamentoTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: DashboardMeioPagamentoChartRow;
  }>;
};

function DashboardMeiosPagamentoTooltip({ active, payload }: DashboardMeiosPagamentoTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  return (
    <div className="rounded-sm border border-[#ccc] bg-white px-2.5 py-2.5 text-xs shadow-sm" role="tooltip">
      <p className="m-0 text-[14px] font-normal leading-snug text-[#000]">{row.label}</p>
      <p className="m-0 pt-1 text-[14px] font-normal leading-snug text-[#000]">
        {formatComparacaoPercentual(row.percentual)}
      </p>
    </div>
  );
}


type DashboardComparativoRange = {
  fetchStart: string;
  fetchEnd: string;
  atualStart: string;
  atualEnd: string;
  anteriorStart: string;
  anteriorEnd: string;
  label: string;
};

function isoDatePart(value: string | null | undefined): string {
  const m = String(value ?? "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m?.[1] ?? "";
}

function isoDateInRange(value: string | null | undefined, start: string, end: string): boolean {
  const date = isoDatePart(value);
  return date !== "" && date >= start && date <= end;
}

function calcularComparacaoPercentual(atual: number, anterior: number, label: string): DashboardKpiComparacao | null {
  if (!Number.isFinite(atual) || !Number.isFinite(anterior) || anterior === 0 || atual === anterior) {
    return null;
  }
  const percentual = ((atual - anterior) / anterior) * 100;
  if (!Number.isFinite(percentual) || Math.abs(percentual) < 0.05) return null;
  return {
    direction: atual > anterior ? "up" : "down",
    percentual: Math.abs(percentual),
    label,
  };
}

function dashboardComparativoVazio() {
  return {
    faturamento: null,
    qtdVendas: null,
    ticketMedio: null,
    lucro: null,
  };
}

function getDashboardComparativoRange(periodo: DashboardPeriodo): DashboardComparativoRange | null {
  const today = getTodaySaoPaulo();

  switch (periodo) {
    case "hoje": {
      const yesterday = addDaysIso(today, -1);
      return {
        fetchStart: yesterday,
        fetchEnd: today,
        atualStart: today,
        atualEnd: today,
        anteriorStart: yesterday,
        anteriorEnd: yesterday,
        label: "vs ontem",
      };
    }
    case "ontem": {
      const yesterday = addDaysIso(today, -1);
      const beforeYesterday = addDaysIso(today, -2);
      return {
        fetchStart: beforeYesterday,
        fetchEnd: yesterday,
        atualStart: yesterday,
        atualEnd: yesterday,
        anteriorStart: beforeYesterday,
        anteriorEnd: beforeYesterday,
        label: "vs anteontem",
      };
    }
    case "7":
      return {
        fetchStart: addDaysIso(today, -13),
        fetchEnd: today,
        atualStart: addDaysIso(today, -6),
        atualEnd: today,
        anteriorStart: addDaysIso(today, -13),
        anteriorEnd: addDaysIso(today, -7),
        label: "vs 7 dias anteriores",
      };
    case "30":
      return {
        fetchStart: addDaysIso(today, -59),
        fetchEnd: today,
        atualStart: addDaysIso(today, -29),
        atualEnd: today,
        anteriorStart: addDaysIso(today, -59),
        anteriorEnd: addDaysIso(today, -30),
        label: "vs 30 dias anteriores",
      };
    case "personalizado":
    default:
      return null;
  }
}

function dashboardTemPeriodoAnteriorCompleto(
  range: DashboardComparativoRange,
  vendas: Venda[],
  despesas: Despesa[],
): boolean {
  const datasComRegistro = new Set<string>();

  for (const venda of vendas) {
    const data = isoDatePart(venda.createdAt);
    if (data >= range.anteriorStart && data <= range.anteriorEnd) datasComRegistro.add(data);
  }

  for (const despesa of despesas) {
    const data = isoDatePart(despesa.data ?? despesa.createdAt);
    if (data >= range.anteriorStart && data <= range.anteriorEnd) datasComRegistro.add(data);
  }

  for (let data = range.anteriorStart; data <= range.anteriorEnd; data = addDaysIso(data, 1)) {
    if (!datasComRegistro.has(data)) return false;
  }

  return true;
}

function formatComparacaoPercentual(valor: number): string {
  return `${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function DashboardKpiComparacaoLine({
  comparacao,
  oculto,
}: {
  comparacao: DashboardKpiComparacao | null;
  oculto: boolean;
}) {
  if (!comparacao || oculto) return null;
  const Icon = comparacao.direction === "up" ? ArrowUp : ArrowDown;
  const tone = comparacao.direction === "up" ? "text-emerald-600" : "text-red-600";

  return (
    <div className="mt-1 flex items-center gap-1.5 text-xs leading-none">
      <span className={`inline-flex items-center gap-0.5 font-semibold ${tone}`}>
        <Icon className="h-3 w-3" aria-hidden />
        {formatComparacaoPercentual(comparacao.percentual)}
      </span>
      <span className="text-muted-foreground">{comparacao.label}</span>
    </div>
  );
}

const CATEGORIA_INSUMO_TAG_PALETTE = [
  "bg-sky-100 text-sky-900 border border-sky-200",
  "bg-emerald-100 text-emerald-900 border border-emerald-200",
  "bg-violet-100 text-violet-900 border border-violet-200",
  "bg-rose-100 text-rose-900 border border-rose-200",
  "bg-orange-100 text-orange-900 border border-orange-200",
  "bg-cyan-100 text-cyan-900 border border-cyan-200",
  "bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-200",
  "bg-lime-100 text-lime-900 border border-lime-200",
];

function matchCategoriasFiltro(categoria: string | undefined, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return selected.includes((categoria || "").trim());
}

function pruneCategoriasFiltro(selected: string[], available: string[]): string[] {
  const allowed = new Set(available);
  const next = selected.filter((c) => allowed.has(c));
  return next.length === selected.length ? selected : next;
}

function limparCategoriaEmLista<T extends { categoria?: string | null }>(lista: T[], nomeCategoria: string): T[] {
  const alvo = nomeCategoria.trim().toLowerCase();
  if (!alvo) return lista;
  return lista.map((item) => {
    if ((item.categoria || "").trim().toLowerCase() !== alvo) return item;
    return { ...item, categoria: "" };
  });
}

function categoriaInsumoTagClass(categoria: string): string {
  const t = (categoria || "").trim();
  if (!t) return "bg-muted text-muted-foreground border border-border";
  const lower = t.toLowerCase();
  if (lower === "doce" || lower === "doces") {
    return "bg-amber-100 text-amber-900 border border-amber-200";
  }
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return CATEGORIA_INSUMO_TAG_PALETTE[h % CATEGORIA_INSUMO_TAG_PALETTE.length];
}

/** Campo telefone no cadastro/edição de usuário: máscara (99) 9 9999-9999. */
function UsuarioTelefoneMaskInput({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  // react-input-mask: não passar disabled={false} só no Input interno — conflita com restProps do mask e quebra (invariant).
  const safeValue = value ?? "";
  return (
    <InputMask
      mask="(99) 9 9999-9999"
      value={safeValue}
      disabled={!!disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    >
      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
        <Input
          {...inputProps}
          id={id}
          disabled={!!disabled}
          placeholder="(00) 0 0000-0000"
          className="w-full"
        />
      )}
    </InputMask>
  );
}

const FRACAO_MULT: Record<string, number> = { un: 1, "1/2": 0.5, "1/4": 0.25, "1/8": 0.125 };
const OPCOES_UN_UNIDADE = ["un", "1/2", "1/4", "1/8"];
/** Valor do `<option>` para criar categoria nova (alinhado ao fluxo de insumos/categorias). */
const OPCAO_CATEGORIA_OUTRA = "__outra__";

/** Opções do cadastro de insumo (valores canônicos para Estoque > Entradas). */
const INSUMO_UNIDADES_OPCOES = [
  { value: "un", label: "Unidade" },
  { value: "L", label: "Litro" },
  { value: "ml", label: "Mililitro" },
  { value: "kg", label: "Quilos" },
  { value: "g", label: "Gramas" },
] as const;

function insumoUnidadeParaSelect(unidadeCadastro: string): string {
  const canon = unidadeCanonicaParaBase(unidadeCadastro.trim());
  const allowed = INSUMO_UNIDADES_OPCOES.map((o) => o.value);
  return (allowed as readonly string[]).includes(canon) ? canon : "un";
}

function unidadeCanonicaParaBase(base: string): string {
  const b = base.trim().toLowerCase();
  if (b === "g" || b === "grama" || b === "gramas") return "g";
  if (b === "kg" || b === "quilo" || b === "quilograma" || b === "quilogramas") return "kg";
  if (b === "l" || b === "litro" || b === "litros") return "L";
  if (b === "ml" || b === "mililitro" || b === "mililitros") return "ml";
  if (b === "un" || b === "unidade") return "un";
  return base.trim() || "un";
}

function opcoesUnidadePorBase(base: string): string[] {
  const b = base.trim().toLowerCase();
  if (b === "l" || b === "litro" || b === "litros") return ["L", "ml"];
  if (b === "ml" || b === "mililitro" || b === "mililitros") return ["ml", "L"];
  if (b === "kg" || b === "quilo" || b === "quilograma" || b === "quilogramas" || b === "g" || b === "grama" || b === "gramas") return ["kg", "g"];
  return OPCOES_UN_UNIDADE;
}

/** Rótulos para exibição do perfil (sem sufixos do tipo "não-admin"). */
const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  gerente: "Gerente",
  funcionario: "Funcionário",
  cozinha: "Cozinha",
  marketing: "Marketing",
  user: "Usuário",
};

type UsuarioRowResumo = {
  role: string;
  allowed_tabs: string[] | null;
};

function usuarioNomeExibicao(u: { username: string; name: string | null }): string {
  const nome = (u.name ?? "").trim();
  return nome !== "" ? nome : u.username;
}

function usuarioAcessoResumo(u: UsuarioRowResumo): string {
  if (u.role === "admin" || u.allowed_tabs === null) {
    return "Acesso: Todas";
  }
  if (u.allowed_tabs.length === 0) {
    return "Acesso: Nenhuma";
  }
  const labels = allowedTabsToDisplayLabels(u.allowed_tabs);
  return `Acesso: ${labels.join(", ")}`;
}

type FinanceiroSaida = "despesa" | "compra" | "outro";
type FinanceiroResumoKey = "vendas" | "despesas" | "compras" | "outros";

function financeiroTabForSaida(saida: FinanceiroSaida): FinanceiroSubTab {
  if (saida === "compra") return "compras";
  if (saida === "outro") return "outros";
  return "despesas";
}

function FinanceiroSaidaModalSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: FinanceiroSaida;
  onChange: (value: FinanceiroSaida) => void;
}) {
  return (
    <div className="min-[767px]:hidden">
      <Label htmlFor={id}>Saída</Label>
      <FilterSelect
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as FinanceiroSaida)}
        mobileSheet={false}
        className="mt-1 h-10 w-full !rounded-md !border-input bg-white px-3 py-2 !shadow-none"
      >
        <option value="despesa">Despesas</option>
        <option value="compra">Compras</option>
        <option value="outro">Outros</option>
      </FilterSelect>
    </div>
  );
}

function ModalAddDespesaFinanceiro({ onClose, onSuccess }: { onClose: () => void; onSuccess: (saida: FinanceiroSaida) => void }) {
  const [nome, setNome] = useState("");
  const [valor, setValor] = useState("");
  const [data, setData] = useState(() => getTodaySaoPaulo());
  const classificacao: FinanceiroSaida = "despesa";
  const [saving, setSaving] = useState(false);
  const valorNum = parseMoneyMask(valor);
  const canSave = nome.trim() !== "" && valorNum > 0 && data.length === 10;
  const showAction = canSave;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const nomeFinal = normalizeDescricaoDespesa(nome.trim());
      await api.criarDespesa({ nome: nomeFinal, valor: valorNum, tipo: "unica", classificacao, data });
      onSuccess(classificacao);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao criar despesa");
    } finally {
      setSaving(false);
    }
  };
  return (
    <StandardModal open title="Adicionar despesa" onClose={onClose} panelClassName="max-w-md">
      <form onSubmit={handleSubmit} className={MODAL_BODY_CLASS}>
        <div>
          <Label htmlFor="fin-add-nome">Descrição / Motivo</Label>
          <Input id="fin-add-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Aluguel" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-valor">Valor (R$)</Label>
          <MoneyInput id="fin-add-valor" value={valor} onChange={setValor} placeholder="0,00" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-data">Data</Label>
          <DatePicker id="fin-add-data" value={data} onChange={setData} className="mt-1" />
        </div>
        <ModalPrimaryAction show={showAction} type="submit" loading={saving} disabled={!canSave}>
          Salvar Despesa
        </ModalPrimaryAction>
      </form>
    </StandardModal>
  );
}

function ModalEditDespesaFinanceiro({ despesa, onClose, onSuccess }: { despesa: Despesa; onClose: () => void; onSuccess: (saida: FinanceiroSaida) => void }) {
  const [nome, setNome] = useState(despesa.nome);
  const [valor, setValor] = useState(numberToMoneyMask(despesa.valor));
  const [data, setData] = useState(despesa.data ?? getTodaySaoPaulo());
  const classificacaoOriginal = classificacaoDespesaSegura(despesa.classificacao);
  const [classificacao, setClassificacao] = useState<FinanceiroSaida>(classificacaoOriginal);
  useEffect(() => {
    setClassificacao(classificacaoOriginal);
  }, [despesa.id, classificacaoOriginal]);
  const [saving, setSaving] = useState(false);
  const valorNum = parseMoneyMask(valor);
  const canSave = nome.trim() !== "" && valorNum > 0 && data.length === 10;
  const hasChanges =
    normalizeDescricaoDespesa(nome.trim()) !== normalizeDescricaoDespesa(despesa.nome.trim()) ||
    valorNum !== Number(despesa.valor) ||
    data !== (despesa.data ?? getTodaySaoPaulo()) ||
    classificacao !== classificacaoOriginal;
  const showAction = canSave && hasChanges;
  const titulo = classificacaoOriginal === "compra" ? "Editar Compra" : classificacaoOriginal === "outro" ? "Editar Outro" : "Editar Despesa";
  const descricaoLabel = classificacao === "despesa" ? "Descrição / Motivo" : "Descrição";
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const nomeFinal = normalizeDescricaoDespesa(nome.trim());
      await api.atualizarDespesa({
        id: despesa.id,
        nome: nomeFinal,
        valor: valorNum,
        tipo: "unica",
        classificacao,
        data,
      });
      onSuccess(classificacao);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao atualizar despesa");
    } finally {
      setSaving(false);
    }
  };
  return (
    <StandardModal open title={titulo} onClose={onClose} panelClassName="max-w-md" closeDisabled={saving}>
      <form onSubmit={handleSubmit} className={MODAL_BODY_CLASS}>
        <div>
          <Label htmlFor="fin-edit-nome">{descricaoLabel}</Label>
          <Input id="fin-edit-nome" value={nome} onChange={(e) => setNome(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-edit-valor">Valor (R$)</Label>
          <MoneyInput id="fin-edit-valor" value={valor} onChange={setValor} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-edit-data">Data</Label>
          <DatePicker id="fin-edit-data" value={data} onChange={setData} className="mt-1" />
        </div>
        <FinanceiroSaidaModalSelect id="fin-edit-saida" value={classificacao} onChange={setClassificacao} />
        <ModalPrimaryAction show={showAction} type="submit" loading={saving} disabled={!canSave}>
          {classificacao === "compra"
            ? "Salvar Compra"
            : classificacao === "outro"
              ? "Salvar Outro"
              : "Salvar Despesa"}
        </ModalPrimaryAction>
      </form>
    </StandardModal>
  );
}

function ModalExcluirDespesaFinanceiro({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => Promise<void> }) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao excluir");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={MODAL_OVERLAY_CLASS}>
      <Card className={`w-full max-w-sm ${MODAL_PANEL_CLASS}`}>
        <CardHeader className="space-y-0 border-0 bg-transparent p-0">
          <div className={MODAL_HEADER_ROW_CLASS}>
            <CardTitle className={MODAL_TITLE_CLASS}>Excluir despesa</CardTitle>
            <Button type="button" variant="ghost" size="icon" className="shrink-0 h-9 w-9" onClick={onClose} aria-label="Fechar">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="bg-white space-y-4">
          <CardDescription className="text-gray-600">Esta ação não pode ser desfeita. Deseja continuar?</CardDescription>
          <Button variant="destructive" onClick={handleConfirm} disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Excluir"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/** Garante classificação válida para abas Financeiro (registros antigos / API). */
function classificacaoDespesaSegura(c: string | undefined | null): "despesa" | "compra" | "outro" {
  if (c === "compra" || c === "outro" || c === "despesa") return c;
  return "despesa";
}

function normalizarDespesasDashboard(despesas: Despesa[]): Despesa[] {
  return despesas.map((d) => ({
    ...d,
    classificacao: classificacaoDespesaSegura(d.classificacao),
  }));
}

type InsumoFormSnapshot = {
  nome: string;
  categoria: string;
  unidade: string;
  estoqueMinimo: string;
  custo: string;
};

function insumoFormCanSave(snapshot: InsumoFormSnapshot): boolean {
  return snapshot.nome.trim() !== "" && snapshot.categoria.trim() !== "" && snapshot.unidade.trim() !== "";
}

function insumoFormHasChanges(current: InsumoFormSnapshot, baseline: InsumoFormSnapshot | null): boolean {
  if (!baseline) return true;
  return (
    current.nome.trim() !== baseline.nome.trim() ||
    current.categoria.trim() !== baseline.categoria.trim() ||
    current.unidade.trim() !== baseline.unidade.trim() ||
    current.estoqueMinimo.trim() !== baseline.estoqueMinimo.trim() ||
    current.custo.trim() !== baseline.custo.trim()
  );
}

function buildPromocaoFormSnapshot(
  nome: string,
  valor: string,
  tipo: "fixa" | "categoria",
  categorias: Array<{ categoria: string; quantidade: string }>,
  itens: Array<{ produto_id: number; nome: string; quantidade: number }>,
): string {
  return JSON.stringify({ nome: nome.trim(), valor, tipo, categorias, itens });
}

function promocaoFormIsValid(
  nome: string,
  valor: string,
  tipo: "fixa" | "categoria",
  categorias: Array<{ categoria: string; quantidade: string }>,
  itens: Array<{ produto_id: number; nome: string; quantidade: number }>,
): boolean {
  if (!nome.trim() || !valor || parseMoneyMask(String(valor)) <= 0) return false;
  if (tipo === "fixa") return itens.length > 0;
  return categorias.some((row) => row.categoria.trim() !== "" && (parseInt(row.quantidade, 10) || 0) >= 1);
}

/** Subaba Entradas: Qtd > 0 e valor pago preenchido (≥ 0) — ambos obrigatórios para mostrar Salvar na linha. */
function entradasLinhaQtdEValorCompletos(
  key: string,
  quantidades: Record<string, string>,
  valores: Record<string, string>,
): boolean {
  const qRaw = (quantidades[key] ?? "").trim();
  const vRaw = (valores[key] ?? "").trim();
  if (qRaw === "" || vRaw === "") return false;
  const qty = parseFloat(qRaw.replace(",", "."));
  const vVal = parseMoneyMask(vRaw);
  return !Number.isNaN(qty) && qty > 0 && vRaw !== "" && !Number.isNaN(vVal) && vVal >= 0;
}

function entradasUnidadeEhUn(unidadeCadastro: string): boolean {
  const u = unidadeCadastro.trim().toLowerCase();
  return u === "un" || u === "unidade";
}

type EntradaUnidadeOpcao = { value: string; label: string };

/** Opções do select compacto de unidade em Entradas; null = unidade fixa (sem dropdown). */
function entradasOpcoesUnidade(unidadeCadastro: string): EntradaUnidadeOpcao[] | null {
  const u = unidadeCadastro.trim().toLowerCase();
  if (entradasUnidadeEhUn(unidadeCadastro)) return null;

  if (u === "g" || u === "grama" || u === "gramas") {
    return [
      { value: "g", label: "g" },
      { value: "kg", label: "kg" },
    ];
  }
  if (u === "kg" || u === "quilo" || u === "quilograma" || u === "quilogramas") {
    return [
      { value: "kg", label: "kg" },
      { value: "g", label: "g" },
    ];
  }
  if (u === "ml" || u === "mililitro" || u === "mililitros") {
    return [
      { value: "ml", label: "ml" },
      { value: "L", label: "L" },
    ];
  }
  if (u === "l" || u === "litro" || u === "litros") {
    return [
      { value: "L", label: "L" },
      { value: "ml", label: "ml" },
    ];
  }

  return null;
}

function entradasUnidadeValorSelect(
  key: string,
  unidadeCadastro: string,
  unidadesEscolhidas: Record<string, string>,
): string {
  const opcoes = entradasOpcoesUnidade(unidadeCadastro);
  if (!opcoes) return unidadeCanonicaParaBase(unidadeCadastro);
  const salvo = unidadesEscolhidas[key];
  if (salvo && opcoes.some((o) => o.value === salvo)) return salvo;
  return opcoes[0].value;
}

function entradasLinhaUnidadeSatisfeita(
  key: string,
  row: { tipo: "insumo"; insumo: Insumo } | { tipo: "produto"; produto: Produto },
  _unidadeInteragida: Record<string, boolean>,
): boolean {
  void key;
  void _unidadeInteragida;
  if (row.tipo === "produto") return true;
  return true;
}

function FinanceiroSaidaSelect({
  despesa,
  disabled,
  onChange,
}: {
  despesa: Despesa;
  disabled: boolean;
  onChange: (d: Despesa, v: "despesa" | "compra" | "outro") => void;
}) {
  const v = classificacaoDespesaSegura(despesa.classificacao);
  return (
    <FilterSelect
      value={v}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value as "despesa" | "compra" | "outro";
        onChange(despesa, next);
      }}
      className="h-9 w-[6.5rem] max-w-full shrink-0 px-1.5 text-sm sm:w-[7rem]"
      aria-label="Saída — mover entre Despesas, Compras ou Outros"
    >
      <option value="despesa">Despesa</option>
      <option value="compra">Compras</option>
      <option value="outro">Outros</option>
    </FilterSelect>
  );
}

function FinanceiroResumoCard({
  title,
  loading,
  value,
  ocultarValores,
  onToggleOcultar,
  className = "w-full max-w-full shrink-0 lg:max-w-[50%]",
  compact = false,
}: {
  title: string;
  loading: boolean;
  value: number;
  ocultarValores: boolean;
  onToggleOcultar: () => void;
  className?: string;
  compact?: boolean;
}) {
  return (
    <Card className={`${className} ${compact ? FINANCEIRO_KPI_CARD : KPI_VALUE_CARD}`}>
      <CardHeader className={compact ? FINANCEIRO_KPI_CARD_HEADER : KPI_VALUE_CARD_HEADER}>
        <CardTitle className={KPI_VALUE_CARD_TITLE}>{title}</CardTitle>
      </CardHeader>
      <CardContent className={compact ? FINANCEIRO_KPI_CARD_CONTENT : KPI_VALUE_CARD_CONTENT}>
        <div className={compact ? FINANCEIRO_KPI_VALUE_TEXT : KPI_VALUE_TEXT}>
          {loading ? "—" : ocultarValores ? "••••••" : formatBRL(value)}
        </div>
        <button
          type="button"
          className={`${KPI_VALUE_EYE_BTN} ${compact ? "min-[767px]:hidden" : ""}`}
          onClick={onToggleOcultar}
          title={ocultarValores ? "Mostrar valores" : "Ocultar valores"}
          aria-label={ocultarValores ? `Mostrar ${title}` : `Ocultar ${title}`}
        >
          {ocultarValores ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </CardContent>
    </Card>
  );
}

function FinanceiroEmptyRow({ colSpan, className = "" }: { colSpan: number; className?: string }) {
  return (
    <tr className={className}>
      <td colSpan={colSpan} className="p-0">
        <div className="flex flex-col items-center justify-center px-4 py-4 text-center min-[768px]:py-6">
          <img
            src="/imagens/erro.svg"
            alt=""
            className="mb-5 h-auto w-[180px] max-w-full object-contain min-[768px]:w-[240px]"
          />
          <p className="gs-empty-state-message text-base font-semibold text-foreground">Nenhum registro localizado</p>
        </div>
      </td>
    </tr>
  );
}

function ReposicaoCheckbox({
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  ariaLabel,
  inputRef,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  ariaLabel: string;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <label className={`inline-flex h-8 w-auto shrink-0 items-center ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      <input
        ref={inputRef}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={ariaLabel}
        aria-checked={indeterminate ? "mixed" : checked}
      />
      <span
        aria-hidden="true"
        className={`inline-flex h-4 w-4 items-center justify-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-client-primary/40 ${
          checked || indeterminate
            ? "border-client-primary bg-client-primary text-white"
            : "border-[#111827] bg-white text-transparent"
        } ${disabled ? "opacity-50" : ""}`}
      >
        {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : indeterminate ? <span className="h-0.5 w-2.5 rounded bg-white" /> : null}
      </span>
    </label>
  );
}

/** Tag de origem ao lado da descrição (Financeiro). */
function DespesaOrigemInlineBadge({ origem }: { origem: Despesa["origem"] }) {
  if (origem === "caixa_retirada") {
    return (
      <span className="inline-flex shrink-0 items-center rounded-md border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-xs font-medium leading-none text-amber-800">
        Caixa
      </span>
    );
  }
  if (origem === "extrato_ofx") {
    return (
      <span className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-xs font-medium leading-none text-slate-800">
        Extrato
      </span>
    );
  }
  return null;
}

function FinanceiroDescricaoComOrigem({ despesa, onSaved }: { despesa: Despesa; onSaved: () => void }) {
  return (
    <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden">
      <div className="min-w-0 flex-1 overflow-hidden">
        <EditableDescricaoDespesaCell despesa={despesa} onSaved={onSaved} financeiroTruncate />
      </div>
      <DespesaOrigemInlineBadge origem={despesa.origem} />
    </div>
  );
}

/** Descrição editável na lista Financeiro; descrição nunca altera a classificação. */
function EditableDescricaoDespesaCell({
  despesa,
  onSaved,
  financeiroTruncate,
}: {
  despesa: Despesa;
  onSaved: () => void;
  /** Quando true: ellipsis fora do foco; ao focar permite rolar/editar o texto completo. */
  financeiroTruncate?: boolean;
}) {
  const [val, setVal] = useState(despesa.nome);
  const [editing, setEditing] = useState(false);
  const [focused, setFocused] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);

  useEffect(() => {
    if (!editing) setVal(despesa.nome);
  }, [despesa.nome, despesa.id, editing]);

  const commit = async () => {
    setEditing(false);
    const nomeFinal = normalizeDescricaoDespesa(val.trim());
    if (nomeFinal === "") {
      toast.error("Descrição é obrigatória.");
      setVal(despesa.nome);
      return;
    }
    if (nomeFinal === despesa.nome) return;
    setLocalSaving(true);
    try {
      const classificacao = isDescricaoRegistroCompras(nomeFinal) ? "compra" : classificacaoDespesaSegura(despesa.classificacao);
      await api.atualizarDespesa({
        id: despesa.id,
        nome: nomeFinal,
        valor: Number(despesa.valor),
        tipo: despesa.tipo,
        classificacao,
        data: despesa.data ?? undefined,
      });
      toast.success("Descrição salva");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar descrição");
      setVal(despesa.nome);
    } finally {
      setLocalSaving(false);
    }
  };

  const truncateClasses =
    financeiroTruncate && !focused
      ? "overflow-hidden text-ellipsis whitespace-nowrap"
      : "min-w-0 overflow-x-auto whitespace-nowrap";

  const displayValue = focused || editing ? val : formatDisplayText(val);
  const handleDescriptionChange = (value: string) => {
    setEditing(true);
    setVal(value);
  };
  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <>
      <input
        type="text"
        title={financeiroTruncate ? formatDisplayText(val) : undefined}
        className={`block w-full min-w-0 max-w-full rounded border border-transparent bg-transparent py-1 px-1 text-sm font-medium text-foreground hover:border-muted-foreground/25 focus:border-accent focus:bg-background focus:outline-none disabled:opacity-50 ${financeiroTruncate ? "hidden min-[767px]:block" : ""} ${truncateClasses}`}
        value={displayValue}
        disabled={localSaving}
        onChange={(e) => handleDescriptionChange(e.target.value)}
        onFocus={() => {
          setEditing(true);
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
          void commit();
        }}
        onKeyDown={handleDescriptionKeyDown}
      />
      {financeiroTruncate && (
        <textarea
          rows={1}
          title={formatDisplayText(val)}
          className="block w-full min-w-0 max-w-full resize-none rounded border border-transparent bg-transparent py-1 px-1 text-sm font-medium leading-5 text-foreground whitespace-normal break-words hover:border-muted-foreground/25 focus:border-accent focus:bg-background focus:outline-none disabled:opacity-50 min-[767px]:hidden"
          value={displayValue}
          disabled={localSaving}
          onChange={(e) => {
            handleDescriptionChange(e.target.value);
            e.currentTarget.style.height = "auto";
            e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
          }}
          onInput={(e) => {
            e.currentTarget.style.height = "auto";
            e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
          }}
          onFocus={() => {
            setEditing(true);
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
            void commit();
          }}
          onKeyDown={handleDescriptionKeyDown}
          ref={(el) => {
            if (el) {
              el.style.height = "auto";
              el.style.height = `${el.scrollHeight}px`;
            }
          }}
        />
      )}
    </>
  );
}

function ModalAddCompraFinanceiro({ onClose, onSuccess }: { onClose: () => void; onSuccess: (saida: FinanceiroSaida) => void }) {
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [data, setData] = useState(() => getTodaySaoPaulo());
  const classificacao: FinanceiroSaida = "compra";
  const [saving, setSaving] = useState(false);
  const valorNum = parseMoneyMask(valor);
  const canSave = descricao.trim() !== "" && valorNum > 0 && data.length === 10;
  const showAction = canSave;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await api.criarDespesa({ nome: descricao.trim(), valor: valorNum, tipo: "unica", classificacao, data });
      onSuccess(classificacao);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar compra");
    } finally {
      setSaving(false);
    }
  };
  return (
    <StandardModal open title="Adicionar compra" onClose={onClose} panelClassName="max-w-md" closeDisabled={saving}>
      <form onSubmit={handleSubmit} className={MODAL_BODY_CLASS}>
        <div>
          <Label htmlFor="fin-add-compra-desc">Descrição</Label>
          <Input id="fin-add-compra-desc" value={descricao} onChange={(e) => setDescricao(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-compra-valor">Valor (R$)</Label>
          <MoneyInput id="fin-add-compra-valor" value={valor} onChange={setValor} placeholder="0,00" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-compra-data">Data</Label>
          <DatePicker id="fin-add-compra-data" value={data} onChange={setData} className="mt-1" />
        </div>
        <ModalPrimaryAction show={showAction} type="submit" loading={saving} disabled={!canSave}>
          Salvar Compra
        </ModalPrimaryAction>
      </form>
    </StandardModal>
  );
}

function ModalAddOutrosFinanceiro({ onClose, onSuccess }: { onClose: () => void; onSuccess: (saida: FinanceiroSaida) => void }) {
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [data, setData] = useState(() => getTodaySaoPaulo());
  const classificacao: FinanceiroSaida = "outro";
  const [saving, setSaving] = useState(false);
  const valorNum = parseMoneyMask(valor);
  const canSave = descricao.trim() !== "" && valorNum > 0 && data.length === 10;
  const showAction = canSave;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await api.criarDespesa({ nome: descricao.trim(), valor: valorNum, tipo: "unica", classificacao, data });
      onSuccess(classificacao);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao criar lançamento");
    } finally {
      setSaving(false);
    }
  };
  return (
    <StandardModal open title="Adicionar Outros" onClose={onClose} panelClassName="max-w-md" closeDisabled={saving}>
      <form onSubmit={handleSubmit} className={MODAL_BODY_CLASS}>
        <div>
          <Label htmlFor="fin-add-outros-desc">Descrição</Label>
          <Input id="fin-add-outros-desc" value={descricao} onChange={(e) => setDescricao(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-outros-valor">Valor (R$)</Label>
          <MoneyInput id="fin-add-outros-valor" value={valor} onChange={setValor} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="fin-add-outros-data">Data</Label>
          <DatePicker id="fin-add-outros-data" value={data} onChange={setData} className="mt-1" />
        </div>
        <ModalPrimaryAction show={showAction} type="submit" loading={saving} disabled={!canSave}>
          Salvar
        </ModalPrimaryAction>
      </form>
    </StandardModal>
  );
}

function ModalFornecedorLancamento({
  initial,
  prefill,
  onClose,
  onSuccess,
}: {
  initial?: FornecedorLancamento | null;
  prefill?: FornecedorNfPrefill | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isEdit = Boolean(initial?.id);
  const [formaPagamento, setFormaPagamento] = useState<"a_vista" | "parcelado">("a_vista");
  const [fornecedor, setFornecedor] = useState(initial?.fornecedor ?? prefill?.fornecedor ?? "");
  const [valor, setValor] = useState(
    initial
      ? numberToMoneyMask(initial.valor)
      : prefill?.valor != null && String(prefill.valor).trim() !== ""
        ? numberToMoneyMask(Number(prefill.valor))
        : "",
  );
  const [dataLancamento, setDataLancamento] = useState(initial?.data_lancamento ?? prefill?.dataLancamento ?? getTodaySaoPaulo());
  const [dataVencimento, setDataVencimento] = useState(initial?.data_vencimento ?? prefill?.dataVencimento ?? "");
  const [quantidadeParcelas, setQuantidadeParcelas] = useState("");
  const [parcelas, setParcelas] = useState<ParcelaFormRow[]>([]);
  const [status, setStatus] = useState<"pendente" | "pago">(initial?.status ?? "pendente");
  const [saving, setSaving] = useState(false);
  const parcelasStructKeyRef = useRef("");

  const valorNum = parseMoneyMask(valor);
  const qtdParcelasNum = parseQuantidadeParcelas(quantidadeParcelas);
  const qtdParcelasOk = quantidadeParcelasValida(qtdParcelasNum);
  const somaParcelas = somaParcelasValor(parcelas);
  const diffCentavos = diffCentavosParcelas(valorNum, parcelas);

  const sincronizarParcelas = useCallback(
    (qtd: number, prevParcelas: ParcelaFormRow[]) => {
      const structKey = `${valorNum}|${qtd}`;
      if (structKey === parcelasStructKeyRef.current) return prevParcelas;
      parcelasStructKeyRef.current = structKey;
      return criarParcelasFormEstrutura(valorNum, qtd, prevParcelas);
    },
    [valorNum],
  );

  useEffect(() => {
    if (isEdit || formaPagamento !== "parcelado") return;
    if (!qtdParcelasOk || qtdParcelasNum == null) {
      setParcelas([]);
      parcelasStructKeyRef.current = "";
      return;
    }
    setParcelas((prev) => sincronizarParcelas(qtdParcelasNum, prev));
  }, [isEdit, formaPagamento, qtdParcelasNum, qtdParcelasOk, sincronizarParcelas]);

  const parcelasValidas =
    formaPagamento !== "parcelado" ||
    (qtdParcelasOk &&
      parcelas.length === qtdParcelasNum &&
      parcelas.every(
        (parcela) => parseMoneyMask(parcela.valor) > 0 && parcela.data_vencimento.length === 10,
      ) &&
      valoresParcelasIguaisTotal(valorNum, parcelas));

  const canSave =
    fornecedor.trim() !== "" &&
    valorNum > 0 &&
    dataLancamento.length === 10 &&
    (formaPagamento === "a_vista" || isEdit
      ? dataVencimento.length === 10
      : parcelasValidas);

  const hasChanges = initial
    ? fornecedor.trim() !== initial.fornecedor.trim() ||
      valorNum !== Number(initial.valor) ||
      dataLancamento !== initial.data_lancamento ||
      dataVencimento !== initial.data_vencimento ||
      status !== initial.status
    : true;

  const showAction = isEdit ? canSave && hasChanges : canSave;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      if (initial?.id) {
        await api.atualizarFornecedorLancamento({
          id: initial.id,
          fornecedor: fornecedor.trim(),
          valor: valorNum,
          data_lancamento: dataLancamento,
          data_vencimento: dataVencimento,
          status,
        });
      } else if (formaPagamento === "parcelado") {
        await api.criarFornecedorLancamentoParcelado({
          fornecedor: fornecedor.trim(),
          valor: valorNum,
          data_lancamento: dataLancamento,
          forma_pagamento: "parcelado",
          parcelas: parcelas.map((parcela) => ({
            valor: parseMoneyMask(parcela.valor),
            data_vencimento: parcela.data_vencimento,
          })),
        });
      } else {
        await api.criarFornecedorLancamento({
          fornecedor: fornecedor.trim(),
          valor: valorNum,
          data_lancamento: dataLancamento,
          data_vencimento: dataVencimento,
          status: "pendente",
        });
      }
      onSuccess();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar lançamento.");
    } finally {
      setSaving(false);
    }
  };

  const atualizarParcelaValor = (numero: number, nextValor: string) => {
    setParcelas((prev) => prev.map((parcela) => (parcela.numero === numero ? { ...parcela, valor: nextValor } : parcela)));
  };

  const atualizarParcelaVencimento = (numero: number, nextVencimento: string) => {
    setParcelas((prev) =>
      prev.map((parcela) => (parcela.numero === numero ? { ...parcela, data_vencimento: nextVencimento } : parcela)),
    );
  };

  const handleFormaPagamentoChange = (next: "a_vista" | "parcelado") => {
    setFormaPagamento(next);
    if (next === "a_vista") {
      setParcelas([]);
      setQuantidadeParcelas("");
      parcelasStructKeyRef.current = "";
      return;
    }
    parcelasStructKeyRef.current = "";
  };

  const handleQuantidadeParcelasChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    setQuantidadeParcelas(digits);
    const qtd = parseQuantidadeParcelas(digits);
    if (!quantidadeParcelasValida(qtd)) {
      setParcelas([]);
      parcelasStructKeyRef.current = "";
      return;
    }
    setParcelas((prev) => sincronizarParcelas(qtd, prev));
  };

  return (
    <ModalBackdrop open>
      <div
        className={`gs-modal-panel-max relative flex w-full min-w-0 max-w-md max-h-[calc(100dvh-32px)] flex-col overflow-y-auto overflow-x-hidden max-sm:min-h-0 max-sm:overflow-hidden ${MODAL_PANEL_CLASS} p-0`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-6 py-4">
          <h2 className={MODAL_TITLE_CLASS}>{initial ? "Editar pagamento" : "Lançar pagamento"}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            aria-label="Fechar"
            onClick={onClose}
            disabled={saving}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className={`${MODAL_BODY_CLASS} max-sm:min-h-0 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain`}>
          <div>
            <Label htmlFor="forn-nome">Descrição</Label>
            <Input id="forn-nome" value={fornecedor} onChange={(e) => setFornecedor(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="forn-valor">Valor (R$)</Label>
            <MoneyInput id="forn-valor" value={valor} onChange={setValor} className="mt-1" />
          </div>
          {!isEdit ? (
            <div>
              <Label htmlFor="forn-forma-pagamento">Forma de pagamento</Label>
              <Select
                id="forn-forma-pagamento"
                value={formaPagamento}
                onChange={(e) => handleFormaPagamentoChange(e.target.value as "a_vista" | "parcelado")}
                className="mt-1"
              >
                <option value="a_vista">À vista</option>
                <option value="parcelado">Parcelado</option>
              </Select>
            </div>
          ) : null}
          <div>
            <Label htmlFor="forn-data-lancamento">Data de lançamento</Label>
            <DatePicker
              id="forn-data-lancamento"
              value={dataLancamento}
              onChange={setDataLancamento}
              className="mt-1"
            />
          </div>
          {formaPagamento === "a_vista" || isEdit ? (
            <div>
              <Label htmlFor="forn-data-vencimento">Data de vencimento</Label>
              <DatePicker
                id="forn-data-vencimento"
                value={dataVencimento}
                onChange={setDataVencimento}
                className="mt-1"
              />
            </div>
          ) : (
            <>
              <div>
                <Label htmlFor="forn-qtd-parcelas">Quantidade de parcelas</Label>
                <Input
                  id="forn-qtd-parcelas"
                  inputMode="numeric"
                  value={quantidadeParcelas}
                  onChange={(e) => handleQuantidadeParcelasChange(e.target.value)}
                  className="mt-1"
                  placeholder={`${FORNECEDOR_MIN_PARCELAS} a ${FORNECEDOR_MAX_PARCELAS}`}
                />
              </div>
              {parcelas.length > 0 ? (
                <div className="space-y-3 overflow-visible">
                  {parcelas.map((parcela) => (
                    <div
                      key={parcela.numero}
                      className="shrink-0 space-y-2 overflow-visible rounded-lg border border-gray-200 bg-muted/20 p-3"
                    >
                      <p className="text-sm font-medium text-foreground">
                        Parcela {parcela.numero} de {parcelas.length}
                      </p>
                      <div>
                        <Label htmlFor={`forn-parcela-valor-${parcela.numero}`}>Valor (R$)</Label>
                        <MoneyInput
                          id={`forn-parcela-valor-${parcela.numero}`}
                          value={parcela.valor}
                          onChange={(nextValor) => atualizarParcelaValor(parcela.numero, nextValor)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`forn-parcela-venc-${parcela.numero}`}>Data de vencimento</Label>
                        <DatePicker
                          id={`forn-parcela-venc-${parcela.numero}`}
                          value={parcela.data_vencimento}
                          onChange={(value) => atualizarParcelaVencimento(parcela.numero, value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Total informado: {formatBRL(valorNum)} · Soma das parcelas: {formatBRL(somaParcelas)}
                    {diffCentavos !== 0 ? (
                      <>
                        {" "}
                        · Diferença: {formatBRL(diffCentavos / 100)}
                      </>
                    ) : null}
                  </p>
                </div>
              ) : null}
            </>
          )}
          {initial ? (
            <div>
              <Label htmlFor="forn-status">Status</Label>
              <Select id="forn-status" value={status} onChange={(e) => setStatus(e.target.value as "pendente" | "pago")} className="mt-1">
                <option value="pendente">Pendente</option>
                <option value="pago">Pago</option>
              </Select>
            </div>
          ) : null}
          <ModalPrimaryAction show={showAction} type="submit" loading={saving} disabled={!canSave}>
            {isEdit ? "Salvar" : "Lançar pagamento"}
          </ModalPrimaryAction>
        </form>
      </div>
    </ModalBackdrop>
  );
}

/** Pendente: mais de 7 dias verde; de 3 a 7 dias laranja; menos de 3 (ou atrasado) vermelho. */
function fornecedorCorTagDiasRestantes(dias: number): string {
  if (dias > 7) return "border border-emerald-300 bg-emerald-100 text-emerald-800";
  if (dias >= 3) return "border border-orange-300 bg-orange-100 text-orange-800";
  return "border border-red-300 bg-red-100 text-red-800";
}

/** Copy da tag de prazo (dias relativos a hoje em São Paulo). */
function fornecedorLabelTagDiasRestantes(dias: number): string {
  if (dias > 1) return `Vence em ${dias} dias`;
  if (dias === 1) return "Vence amanhã";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Venceu ontem";
  return `${Math.abs(dias)} dias vencidos`;
}

/** Formatação numérica pt-BR para colunas e cópia na aba Reposição. */
function formatReposicaoDecimal(n: number): string {
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Exibe “Faltam” sem zeros desnecessários à direita. */
function formatReposicaoFaltam(n: number): string {
  const x = Math.round(n * 10000) / 10000;
  if (Math.abs(x - Math.round(x)) < 1e-8) return String(Math.round(x));
  return x.toLocaleString("pt-BR", { maximumFractionDigits: 4, minimumFractionDigits: 0 });
}

const FINANCEIRO_SEARCH_WIDTH_CLASS = "w-full max-w-full min-[768px]:w-[360px]";

export default function Home() {
  const { config: clientConfig } = useClientConfig();
  const estoqueFabIconColor = useMemo(() => {
    const raw = clientConfig.primary_color.trim().replace(/^#/, "");
    const hex = raw.length === 3 ? raw.split("").map((part) => `${part}${part}`).join("") : raw;
    if (!/^[\da-f]{6}$/i.test(hex)) return "var(--primary-foreground)";
    const channels = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
    const linear = channels.map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    const luminance = linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
    const whiteContrast = 1.05 / (luminance + 0.05);
    const blackContrast = (luminance + 0.05) / 0.05;
    return whiteContrast >= blackContrast ? "#ffffff" : "#111111";
  }, [clientConfig.primary_color]);
  const { user, loading, isAuthenticated, logout, setUser, loginWithCredentials } = useAuth();
  const [headerScrolled, setHeaderScrolled] = useState(false);
  useEffect(() => {
    const updateHeaderSize = () => {
      const scrolled = window.scrollY > 4;
      setHeaderScrolled((current) => current === scrolled ? current : scrolled);
    };
    window.addEventListener("scroll", updateHeaderSize, { passive: true });
    updateHeaderSize();
    return () => window.removeEventListener("scroll", updateHeaderSize);
  }, []);
  const isAdmin = user?.role === "admin";
  const allowedTabs = user?.allowed_tabs ?? null;
  const canViewTab = (tabName: string) => {
    if (isAdmin) return true;
    if (tabName === "config") return true;
    const tabs = allowedTabs;
    if (!tabs || tabs.length === 0) return false;
    if (tabName === "financeiro") {
      return tabs.includes("financeiro") || tabs.includes("vendas") || tabs.includes("despesas");
    }
    if (tabName === "dashboard") {
      return tabs.includes("dashboard") || tabs.includes("financeiro") || tabs.includes("vendas");
    }
    if (tabName === "fornecedores") {
      return tabs.includes("fornecedores") || tabs.includes("compras");
    }
    if (tabName === "itens") return tabs.includes("produtos") || tabs.includes("insumos");
    if (tabName === "cadastros") return tabs.includes("parceiros");
    if (tabName === "conferencia") return tabs.includes("conferencia");
    if (tabName === "marketing") return tabs.includes("marketing");
    if (tabName === "estoque") return tabs.includes("inventario") || tabs.includes("entradas");
    return tabs.includes(tabName);
  };

  const [location, setLocationRaw] = useLocation();
  const produtoFormRoute = useMemo(() => parseProdutoFormPath(location), [location]);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [configSubTab, setConfigSubTab] = useState<ConfigSubTab>(DEFAULT_CONFIG_SUB);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => {
    if (!mobileSidebarOpen || !window.matchMedia("(max-width: 766px)").matches) return;

    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const previousRootOverflow = root.style.overflow;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    root.style.overflow = "hidden";

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      root.style.overflow = previousRootOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [mobileSidebarOpen]);
  const avatarMenuRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [estoqueSortOrder, setEstoqueSortOrder] = useState<"name-asc" | "name-desc" | "stock-asc" | "stock-desc">("name-asc");
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [estoqueQuantityDrafts, setEstoqueQuantityDrafts] = useState<Record<string, string>>({});
  /** Subtabs dentro da aba Estoque: ajuste manual (ex-Inventário) vs entradas. */
  const [estoqueSubTab, setEstoqueSubTab] = useState<"estoque" | "entradas">("estoque");
  const [estoqueConferenciaModalOpen, setEstoqueConferenciaModalOpen] = useState(false);
  const estoqueHasPendingChangesRef = useRef(false);
  const estoquePendingNavigationRef = useRef<{ to: string; options?: { replace?: boolean }; browserHistory?: boolean } | null>(null);
  const estoqueCurrentUrlRef = useRef("");

  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [insumosLoading, setInsumosLoading] = useState(false);
  const [insumosEstoqueBaixo, setInsumosEstoqueBaixo] = useState<Insumo[]>([]);
  const [conferenciasLista, setConferenciasLista] = useState<Conferencia[]>([]);
  const [loadingConferencias, setLoadingConferencias] = useState(false);
  const [expandedConferenciaId, setExpandedConferenciaId] = useState<number | null>(null);
  const [conferenciaActionMenu, setConferenciaActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [deletingConferenciaId, setDeletingConferenciaId] = useState<number | null>(null);
  const [conferenciaPeriodo, setConferenciaPeriodo] = useState<'hoje' | 'ontem' | '7' | '30' | 'personalizado'>('hoje');
  const [conferenciaPersonalizadoInicio, setConferenciaPersonalizadoInicio] = useState("");
  const [conferenciaPersonalizadoFim, setConferenciaPersonalizadoFim] = useState("");
  const [showModalPeriodoPersonalizadoConferencia, setShowModalPeriodoPersonalizadoConferencia] = useState(false);
  const [promocaoNome, setPromocaoNome] = useState("");
  const [promocaoValor, setPromocaoValor] = useState("");
  const [promocaoTipo, setPromocaoTipo] = useState<"fixa" | "categoria">("fixa");
  /** Linhas categoria + quantidade (promo tipo Categoria) */
  const [promocaoCategoriasLinhas, setPromocaoCategoriasLinhas] = useState<Array<{ categoria: string; quantidade: string }>>([]);
  const [promocaoItens, setPromocaoItens] = useState<Array<{ produto_id: number; nome: string; quantidade: number }>>([]);
  const [promocaoProdutoBusca, setPromocaoProdutoBusca] = useState("");
  const [savingPromocao, setSavingPromocao] = useState(false);
  const [showFormPromocao, setShowFormPromocao] = useState(false);
  const [promocoesLista, setPromocoesLista] = useState<Promocao[]>([]);
  const [loadingPromocoes, setLoadingPromocoes] = useState(false);
  const [expandedPromocaoId, setExpandedPromocaoId] = useState<number | null>(null);
  const [editingPromocaoId, setEditingPromocaoId] = useState<number | null>(null);
  const [promocaoFormBaseline, setPromocaoFormBaseline] = useState<string | null>(null);
  const [deletingPromocaoId, setDeletingPromocaoId] = useState<number | null>(null);
  const [promocaoActionMenu, setPromocaoActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const marketingPromocoesRef = useRef<HTMLDivElement>(null);
  const [savingConferencia, setSavingConferencia] = useState(false);
  const promocaoDraftKey = useMemo(
    () => buildFormDraftKey("promocao", editingPromocaoId ?? "new"),
    [editingPromocaoId],
  );

  // Estados para aba Entradas (entrada de estoque)
  const [entradasProdutos, setEntradasProdutos] = useState<Produto[]>([]);
  const [entradasInsumos, setEntradasInsumos] = useState<Insumo[]>([]);
  const [loadingEntradas, setLoadingEntradas] = useState(false);
  const [savingEntradasPendentes, setSavingEntradasPendentes] = useState(false);
  /** Última linha editada: um único Salvar visível nela até salvar (persiste todas as linhas válidas). */
  const [entradasUltimaLinhaInteracao, setEntradasUltimaLinhaInteracao] = useState<string | null>(null);
  /** Insumos kg/g ou L/ml: usuário precisa alterar o select de unidade pelo menos uma vez. */
  const [entradasUnidadeInteragida, setEntradasUnidadeInteragida] = useState<Record<string, boolean>>({});
  const [entradasQuantities, setEntradasQuantities] = useState<Record<string, string>>({});
  const [entradasValores, setEntradasValores] = useState<Record<string, string>>({});
  const [entradasUnidades, setEntradasUnidades] = useState<Record<string, string>>({});

  const [activeTab, setActiveTab] = useState<HomeActiveTab>("dashboard");

  // Estados para Desperdícios (apenas listagem e excluir; cadastro é feito pelo caixa)
  const [desperdicios, setDesperdicios] = useState<DesperdicioListItem[]>([]);
  const [loadingDesperdicios, setLoadingDesperdicios] = useState(false);
  const [desperdiciosError, setDesperdiciosError] = useState<string | null>(null);
  const [desperdiciosPeriodo, setDesperdiciosPeriodo] = useState<'hoje' | 'ontem' | '7' | '30' | 'personalizado'>('hoje');
  const [desperdiciosPersonalizadoInicio, setDesperdiciosPersonalizadoInicio] = useState("");
  const [desperdiciosPersonalizadoFim, setDesperdiciosPersonalizadoFim] = useState("");
  const [showModalDesperdiciosPeriodo, setShowModalDesperdiciosPeriodo] = useState(false);
  const [desperdiciosTipo, setDesperdiciosTipo] = useState<'todos' | 'consumo_interno' | 'vencimento' | 'brinde_propaganda' | 'cortesia_avaliacao' | 'avaria'>('todos');
  const [ocultarValoresDesperdicios, setOcultarValoresDesperdicios] = useState(false);
  /** Busca por nome do responsável (filtro client-side + autocomplete). */
  const [desperdiciosBuscaNome, setDesperdiciosBuscaNome] = useState("");
  const [desperdiciosProdutoSelecionado, setDesperdiciosProdutoSelecionado] = useState<number | null>(null);
  const [expandedDesperdicioId, setExpandedDesperdicioId] = useState<number | null>(null);
  const [desperdicioActionMenu, setDesperdicioActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);

  // Estados para gerenciamento de usuários
  type UsuarioRow = {
    id: number;
    username: string;
    name: string | null;
    email?: string | null;
    telefone?: string | null;
    role: string;
    allowed_tabs: string[] | null;
  };
  const [usuarios, setUsuarios] = useState<UsuarioRow[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newTelefone, setNewTelefone] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin" | "gerente" | "funcionario" | "cozinha" | "marketing">("funcionario");
  const [newUserTabs, setNewUserTabs] = useState<string[]>([]);
  const [savingNewUser, setSavingNewUser] = useState(false);
  const [editUserModal, setEditUserModal] = useState<UsuarioRow | null>(null);
  const [editUserName, setEditUserName] = useState("");
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editUserTelefone, setEditUserTelefone] = useState("");
  const [editUserRole, setEditUserRole] = useState<typeof newRole>("funcionario");
  const [editUserPassword, setEditUserPassword] = useState("");
  const [editUserTabs, setEditUserTabs] = useState<string[]>([]);
  const [savingEditUser, setSavingEditUser] = useState(false);
  const newUserDraftKey = useMemo(() => buildFormDraftKey("usuario", "new"), []);
  const editUserDraftKey = useMemo(
    () => (editUserModal ? buildFormDraftKey("usuario", editUserModal.id) : null),
    [editUserModal],
  );

  // Estados para Produtos (Inventário / cadastro)
  const [produtosSemComposicao, setProdutosSemComposicao] = useState<Produto[]>([]);
  const [produtoQuantities, setProdutoQuantities] = useState<Record<number, number>>({});
  const [showProdutoModal, setShowProdutoModal] = useState(false);
  const [produtoNome, setProdutoNome] = useState("");
  const [produtoPossuiComposicao, setProdutoPossuiComposicao] = useState(true);
  const [produtoComposicaoItems, setProdutoComposicaoItems] = useState<{ insumo_id: number; insumo_nome: string; unidade: string; unidade_base: string; quantidade: string }[]>([]);
  const [savingProduto, setSavingProduto] = useState(false);
  const [produtoInsumoToAdd, setProdutoInsumoToAdd] = useState<number | "">("");
  const [produtoPrecoVenda, setProdutoPrecoVenda] = useState("");
  const [produtoQuantidadeMinima, setProdutoQuantidadeMinima] = useState("");
  const [produtoCategoriaSelect, setProdutoCategoriaSelect] = useState("");
  const [produtoCategoriaOutra, setProdutoCategoriaOutra] = useState("");
  const [produtosLista, setProdutosLista] = useState<Produto[]>([]);
  const [produtosLoading, setProdutosLoading] = useState(false);
  const [editingProdutoId, setEditingProdutoId] = useState<number | null>(null);
  const [produtosBaixoEstoque, setProdutosBaixoEstoque] = useState<Produto[]>([]);

  // Estados para aba Insumos (lista + modal adicionar/editar)
  const [insumosTabList, setInsumosTabList] = useState<Insumo[]>([]);
  const [loadingInsumosTab, setLoadingInsumosTab] = useState(false);
  const [showInsumoModal, setShowInsumoModal] = useState(false);
  const [editingInsumoId, setEditingInsumoId] = useState<number | null>(null);
  const [insumoNome, setInsumoNome] = useState("");
  const [insumoCategoria, setInsumoCategoria] = useState("");
  const [insumoCategoriaOutra, setInsumoCategoriaOutra] = useState("");
  const [insumoUnidade, setInsumoUnidade] = useState("");
  const [insumoEstoqueMinimo, setInsumoEstoqueMinimo] = useState("");
  const [insumoCusto, setInsumoCusto] = useState("");
  const [insumoFormBaseline, setInsumoFormBaseline] = useState<InsumoFormSnapshot | null>(null);
  const [categoriasList, setCategoriasList] = useState<{ id: number; nome: string }[]>([]);
  const [savingInsumo, setSavingInsumo] = useState(false);
  const [deletingInsumoId, setDeletingInsumoId] = useState<number | null>(null);
  const insumoDraftKey = useMemo(
    () => buildFormDraftKey("insumo", editingInsumoId ?? "new"),
    [editingInsumoId],
  );

  // Estados para aba Vendas
  const [vendasLista, setVendasLista] = useState<Venda[]>([]);
  const [searchFinanceiroVendas, setSearchFinanceiroVendas] = useState("");
  const vendasListaFiltradas = useMemo(
    () => vendasLista.filter((v) => vendaMatchesAtendimento(v.atendimento, searchFinanceiroVendas)),
    [vendasLista, searchFinanceiroVendas],
  );
  const [loadingVendas, setLoadingVendas] = useState(false);
  const [expandedVendaId, setExpandedVendaId] = useState<number | string | null>(null);
  const [deletingVendaId, setDeletingVendaId] = useState<number | null>(null);
  // Estados para aba Financeiro (vendasLista/loadingVendas usados no Financeiro) (unifica Vendas + Despesas)
  const [financeiroPeriodo, setFinanceiroPeriodo] = useState<'hoje' | 'ontem' | '7' | '30' | 'personalizado'>('hoje');
  const [financeiroPersonalizadoInicio, setFinanceiroPersonalizadoInicio] = useState("");
  const [financeiroPersonalizadoFim, setFinanceiroPersonalizadoFim] = useState("");
  const [showModalPeriodoPersonalizadoFinanceiro, setShowModalPeriodoPersonalizadoFinanceiro] = useState(false);
  const [showModalExportarVendasFiscal, setShowModalExportarVendasFiscal] = useState(false);
  const [exportarVendasInicio, setExportarVendasInicio] = useState("");
  const [exportarVendasFim, setExportarVendasFim] = useState("");
  const [financeiroSubTab, setFinanceiroSubTab] = useState<FinanceiroSubTab>(DEFAULT_FINANCEIRO_SUB);
  const [despesasFinanceiro, setDespesasFinanceiro] = useState<Despesa[]>([]);
  const [financeiroSaidaUpdatingId, setFinanceiroSaidaUpdatingId] = useState<number | null>(null);
  const [loadingDespesasFinanceiro, setLoadingDespesasFinanceiro] = useState(false);
  const [ocultarValoresFinanceiro, setOcultarValoresFinanceiro] = useState<Record<FinanceiroResumoKey, boolean>>({
    vendas: false,
    despesas: false,
    compras: false,
    outros: false,
  });
  const [vendaActionMenu, setVendaActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [despesaActionMenu, setDespesaActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const financeiroVendasListRef = useRef<HTMLDivElement>(null);
  const [editingDespesaFinanceiro, setEditingDespesaFinanceiro] = useState<Despesa | null>(null);
  const [modalAddDespesaFinanceiro, setModalAddDespesaFinanceiro] = useState(false);
  const [modalImportOfxFinanceiro, setModalImportOfxFinanceiro] = useState(false);
  const [modalAddCompraFinanceiro, setModalAddCompraFinanceiro] = useState(false);
  const [modalAddOutrosFinanceiro, setModalAddOutrosFinanceiro] = useState(false);
  const [searchFinanceiroDespesas, setSearchFinanceiroDespesas] = useState("");
  const [searchFinanceiroCompras, setSearchFinanceiroCompras] = useState("");
  const [searchFinanceiroOutros, setSearchFinanceiroOutros] = useState("");
  const [deleteConfirmDespesaId, setDeleteConfirmDespesaId] = useState<number | null>(null);
  const [submenuItensAberto, setSubmenuItensAberto] = useState(false);
  const [submenuCadastrosAberto, setSubmenuCadastrosAberto] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(readDesktopSidebarCollapsed);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(DESKTOP_SIDEBAR_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  /** Aba Reposição (id permissão `compras`): filtro e seleção para cópia. */
  const [reposicaoCategoriasFiltro, setReposicaoCategoriasFiltro] = useState<string[]>([]);
  const [reposicaoSelecionados, setReposicaoSelecionados] = useState<string[]>([]);
  const reposicaoHeaderCheckboxRef = useRef<HTMLInputElement>(null);

  const setLocation = useCallback((to: string, options?: { replace?: boolean }) => {
    const routePath = (path: string) => path.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
    const currentPath = routePath(location);
    const targetPath = routePath(to);
    const isStockRoute = (path: string) => path === "/estoque" || path === "/entradas";

    if (estoqueHasPendingChangesRef.current && isStockRoute(currentPath) && !isStockRoute(targetPath)) {
      estoquePendingNavigationRef.current = { to, options };
      setEstoqueConferenciaModalOpen(true);
      return false;
    }

    setLocationRaw(to, options);
    return true;
  }, [location, setLocationRaw]);

  const goToTab = useCallback(
    (tab: HomeActiveTab, opts?: PathForTabOptions & { replace?: boolean }) => {
      const { replace, ...pathOpts } = opts ?? {};
      if (tab === "config") {
        let configSub: ConfigSubTab = pathOpts.configSubTab ?? DEFAULT_CONFIG_SUB;
        if (configSub === "usuarios" && !isAdmin) {
          configSub = "geral";
        }
        pathOpts.configSubTab = configSub;
      }
      if (!setLocation(pathForTab(tab, pathOpts), replace ? { replace: true } : {})) return;
      setActiveTab(tab);
      if (tab === "produtos" || tab === "insumos") {
        setSubmenuItensAberto(true);
      }
      if (tab === "parceiros") {
        setSubmenuCadastrosAberto(true);
      }
      if (tab === "financeiro") {
        setFinanceiroSubTab(pathOpts.financeiroSubTab ?? DEFAULT_FINANCEIRO_SUB);
      }
      if (tab === "estoque") {
        setEstoqueSubTab(pathOpts.estoqueSubTab ?? "estoque");
      }
      if (tab === "config") {
        setConfigSubTab(pathOpts.configSubTab ?? DEFAULT_CONFIG_SUB);
      }
    },
    [isAdmin, setLocation],
  );

  // Estados para Dashboard
  const [dashboardPeriodo, setDashboardPeriodo] = useState<DashboardPeriodo>('hoje');
  const [dashboardPersonalizadoInicio, setDashboardPersonalizadoInicio] = useState("");
  const [dashboardPersonalizadoFim, setDashboardPersonalizadoFim] = useState("");
  const [showModalPeriodoPersonalizadoDashboard, setShowModalPeriodoPersonalizadoDashboard] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardErro, setDashboardErro] = useState(false);
  const [dashboardFalhas, setDashboardFalhas] = useState<string[]>([]);
  const [dashboardPersonalizadoAplicado, setDashboardPersonalizadoAplicado] = useState<{ start: string; end: string } | null>(null);
  const dashboardRequest = useRef<AbortController | null>(null);
  const [dashboardVendas, setDashboardVendas] = useState<Venda[]>([]);
  const [dashboardDespesas, setDashboardDespesas] = useState<Despesa[]>([]);
  const [dashboardComparativoVendas, setDashboardComparativoVendas] = useState<Venda[]>([]);
  const [dashboardComparativoDespesas, setDashboardComparativoDespesas] = useState<Despesa[]>([]);
  const [dashboardProdutosCusto, setDashboardProdutosCusto] = useState<Produto[]>([]);
  const [dashboardInsumosCusto, setDashboardInsumosCusto] = useState<Insumo[]>([]);
  const [dashboardComposicaoPorProduto, setDashboardComposicaoPorProduto] = useState<
    Record<number, ProdutoComposicaoItem[]>
  >({});
  /** Vendas do mês corrente (SP) — meta de faturamento no topo. */
  const [vendasMetaMes, setVendasMetaMes] = useState<Venda[]>([]);
  const [metaMesModalOpen, setMetaMesModalOpen] = useState(false);
  const [dashboardModalProdutosOpen, setDashboardModalProdutosOpen] = useState(false);
  const [dashboardModalProdutosBusca, setDashboardModalProdutosBusca] = useState("");
  const [dashboardModalProdutosCategoria, setDashboardModalProdutosCategoria] = useState("__todos__");
  /** Horários configurados da empresa para o eixo X de Vendas por Hora (null = padrão 06–21). */
  const [dashboardHoraAbertura, setDashboardHoraAbertura] = useState<number | null>(null);
  const [dashboardHoraEncerramento, setDashboardHoraEncerramento] = useState<number | null>(null);
  const [dashboardHoraTickStep, setDashboardHoraTickStep] = useState(1);

  // Estados para aba Pagamentos (contas a pagar)
  const [fornecedorLancamentos, setFornecedorLancamentos] = useState<FornecedorLancamento[]>([]);
  const [loadingFornecedorLancamentos, setLoadingFornecedorLancamentos] = useState(false);
  const [searchFornecedorLancamentos, setSearchFornecedorLancamentos] = useState("");
  const [fornecedorStatusFiltro, setFornecedorStatusFiltro] = useState<"pendente" | "pago">("pendente");
  const [modalFornecedorOpen, setModalFornecedorOpen] = useState(false);
  const [modalFornecedorNfOpen, setModalFornecedorNfOpen] = useState(false);
  const [fornecedorEditing, setFornecedorEditing] = useState<FornecedorLancamento | null>(null);
  const [fornecedorNfPrefill, setFornecedorNfPrefill] = useState<FornecedorNfPrefill | null>(null);
  const [fornecedorDeleteId, setFornecedorDeleteId] = useState<number | null>(null);
  const [fornecedorActionMenu, setFornecedorActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [ocultarValoresDashboard, setOcultarValoresDashboard] = useState(false);
  const [ocultarValoresFornecedorCard, setOcultarValoresFornecedorCard] = useState(false);

  /** Lista de produtos do caixa (Marketing — promoções). */
  const [produtosCaixa, setProdutosCaixa] = useState<Array<{ id: number; nome: string; preco_venda: number | null; possui_composicao: boolean }>>([]);

  // Busca nas abas Produtos e Insumos (placeholder "Pesquisar" no layout)
  const [searchProdutosTab, setSearchProdutosTab] = useState("");
  const [searchInsumosTab, setSearchInsumosTab] = useState("");
  const [produtosCategoriasFiltro, setProdutosCategoriasFiltro] = useState<string[]>([]);
  type ProdutosOrdenacao = "nome-az" | "nome-za" | "mais-vendidos" | "menos-vendidos";
  const [produtosOrdenacao, setProdutosOrdenacao] = useState<ProdutosOrdenacao>("nome-az");
  const [produtosVendasQuantidade, setProdutosVendasQuantidade] = useState<Record<number, number>>({});
  const [insumosTabCategoriasFiltro, setInsumosTabCategoriasFiltro] = useState<string[]>([]);
  const [produtoActionMenu, setProdutoActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [insumoActionMenu, setInsumoActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [usuarioActionMenu, setUsuarioActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);

  // Estado de login simples (PHP)
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const carregarDados = async () => {
    if (!isAuthenticated) return;
    if (user && user.role !== "admin" && (!user.allowed_tabs || user.allowed_tabs.length === 0)) return;
    try {
      setInsumosLoading(true);
      const [listaCats, lista, baixo, produtosSemComp, produtosBaixo] = await Promise.all([
        api.listarCategorias(),
        api.listarInsumos(),
        api.listarBaixoEstoque(),
        api.listarProdutosSemComposicao(),
        api.listarProdutosBaixoEstoque(),
      ]);
      setCategoriasList(Array.isArray(listaCats) ? listaCats : []);
      const listaInsumos = Array.isArray(lista) ? lista : [];
      const baixoLista = Array.isArray(baixo) ? baixo : [];
      const produtosBaixoLista = Array.isArray(produtosBaixo) ? produtosBaixo : [];
      const produtosSemCompLista = Array.isArray(produtosSemComp) ? produtosSemComp : [];
      setInsumos(listaInsumos);
      setInsumosEstoqueBaixo(baixoLista);
      setProdutosSemComposicao(produtosSemCompLista);
      setProdutosBaixoEstoque(produtosBaixoLista);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar dados do estoque");
    } finally {
      setInsumosLoading(false);
    }
  };

  useEffect(() => {
    void carregarDados();
  }, [isAuthenticated, user?.id, user?.role, user?.allowed_tabs]);

  /** Sincroniza pathname → aba (deep link, F5, botões Voltar/Avançar). */
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const norm = normalizeAppPath(location);
    if (norm === "/") {
      setLocation("/dashboard", { replace: true });
      return;
    }
    const parsed = tabFromPath(location, canViewTab);
    if (parsed === null) {
      const fb = getFallbackTab(canViewTab);
      setLocation(pathForTab(fb), { replace: true });
      setActiveTab(fb);
      if (fb === "produtos" || fb === "insumos") setSubmenuItensAberto(true);
      if (fb === "parceiros") setSubmenuCadastrosAberto(true);
      if (fb === "financeiro") setFinanceiroSubTab(DEFAULT_FINANCEIRO_SUB);
      if (fb === "estoque") setEstoqueSubTab("estoque");
      return;
    }
    const allowed =
      parsed.tab === "produtos"
        ? canViewTab("produtos")
        : parsed.tab === "insumos"
          ? canViewTab("insumos")
          : canViewTab(parsed.tab);
    if (!allowed) {
      const fb = getFallbackTab(canViewTab);
      setLocation(pathForTab(fb), { replace: true });
      setActiveTab(fb);
      if (fb === "produtos" || fb === "insumos") setSubmenuItensAberto(true);
      if (fb === "parceiros") setSubmenuCadastrosAberto(true);
      if (fb === "financeiro") setFinanceiroSubTab(DEFAULT_FINANCEIRO_SUB);
      if (fb === "estoque") setEstoqueSubTab("estoque");
      return;
    }

    if (parsed.tab === "estoque" && canViewTab("estoque")) {
      const inv = canViewTab("inventario");
      const ent = canViewTab("entradas");
      // Subaba Entradas removida: /entradas redireciona para listagem de Estoque.
      if (norm === "/entradas") {
        setLocation("/estoque", { replace: true });
        setEstoqueSubTab("estoque");
        return;
      }
      if (norm === "/estoque" && !inv && ent) {
        // Só permissão de entradas: mantém rota estoque (botão IA) sem listagem.
        setEstoqueSubTab("estoque");
      }
    }

    setActiveTab(parsed.tab);
    setSubmenuItensAberto(parsed.openItensSubmenu);
    setSubmenuCadastrosAberto(parsed.openCadastrosSubmenu);
    if (parsed.financeiroSubTab) {
      setFinanceiroSubTab(parsed.financeiroSubTab);
    } else if (parsed.tab === "financeiro") {
      setFinanceiroSubTab(DEFAULT_FINANCEIRO_SUB);
    }
    if (parsed.estoqueSubTab) {
      setEstoqueSubTab(parsed.estoqueSubTab);
    } else if (parsed.tab === "estoque") {
      setEstoqueSubTab("estoque");
    }
    if (parsed.tab === "config") {
      if (parsed.configSubTab === "usuarios" && !isAdmin) {
        setLocation("/config", { replace: true });
        setConfigSubTab("geral");
        setActiveTab("config");
        return;
      }
      setConfigSubTab(parsed.configSubTab ?? DEFAULT_CONFIG_SUB);
    }
  }, [location, isAuthenticated, user?.id, user?.role, setLocation, isAdmin, user?.allowed_tabs]);

  /** Se a aba atual deixar de ser permitida (ex.: permissões alteradas), volta para uma aba válida. */
  useEffect(() => {
    if (!isAuthenticated || !user || isAdmin) return;
    const tabs = user.allowed_tabs;
    if (!tabs || tabs.length === 0) return;
    if (!canViewTab(activeTab)) {
      const fb = getFallbackTab(canViewTab);
      setActiveTab(fb);
      setLocation(pathForTab(fb), { replace: true });
      if (fb === "financeiro") setFinanceiroSubTab(DEFAULT_FINANCEIRO_SUB);
      if (fb === "estoque") setEstoqueSubTab("estoque");
    }
  }, [isAuthenticated, user?.id, user?.allowed_tabs, isAdmin, activeTab, setLocation]);

  /** Retorna { start, end } para o período de desperdícios (America/Sao_Paulo). */
  const getDesperdiciosPeriodoRange = (): { start?: string; end?: string } => {
    const today = getTodaySaoPaulo();
    switch (desperdiciosPeriodo) {
      case 'hoje':
        return { start: today, end: today };
      case 'ontem': {
        const yesterday = addDaysIso(today, -1);
        return { start: yesterday, end: yesterday };
      }
      case '7':
        return { start: addDaysIso(today, -6), end: today };
      case '30':
        return { start: addDaysIso(today, -29), end: today };
      case 'personalizado':
        if (desperdiciosPersonalizadoInicio && desperdiciosPersonalizadoFim) {
          return { start: desperdiciosPersonalizadoInicio, end: desperdiciosPersonalizadoFim };
        }
        return {};
      default:
        return { start: today, end: today };
    }
  };

  const carregarDesperdicios = async () => {
    try {
      setLoadingDesperdicios(true);
      setDesperdiciosError(null);
      const range = getDesperdiciosPeriodoRange();
      const params = {
        start: range.start ?? range.end,
        end: range.end ?? range.start,
        tipo: desperdiciosTipo !== 'todos' ? desperdiciosTipo : undefined,
      };
      const lista = await api.listarDesperdicios(
        (params.start || params.end)
          ? { start: params.start, end: params.end, tipo: params.tipo }
          : { tipo: params.tipo }
      );
      setDesperdicios(Array.isArray(lista) ? lista : []);
    } catch (e) {
      console.error(e);
      setDesperdicios([]);
      setDesperdiciosError("Erro ao carregar. Tente novamente.");
      toast.error("Erro ao carregar desperdícios");
    } finally {
      setLoadingDesperdicios(false);
    }
  };

  const aplicarPeriodoDesperdicios = () => {
    setShowModalDesperdiciosPeriodo(false);
    void carregarDesperdicios();
  };

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'desperdicios') return;
    void carregarProdutos();
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'desperdicios') return;
    const t = setTimeout(() => void carregarDesperdicios(), 300);
    return () => clearTimeout(t);
  }, [isAuthenticated, activeTab, desperdiciosPeriodo, desperdiciosTipo]);

  const desperdiciosBuscaOpcoes = useMemo(() => {
    const responsaveis = new Map<string, string>();
    const produtosPorId = new Map<number, { nome: string; unidade: string; quantidade: number }>();
    for (const d of desperdicios) {
      const responsavel = (d.responsavel_nome || "").trim();
      if (responsavel) {
        const key = responsavel.toLocaleLowerCase("pt-BR");
        if (!responsaveis.has(key)) responsaveis.set(key, formatDisplayText(responsavel));
      }
      for (const item of d.itens ?? []) {
        if (!item.produto_id || !item.nome_produto.trim()) continue;
        const produto = produtosPorId.get(item.produto_id) ?? {
          nome: formatDisplayText(item.nome_produto),
          unidade: item.unidade || "un",
          quantidade: 0,
        };
        produto.quantidade += Number(item.quantidade) || 0;
        produtosPorId.set(item.produto_id, produto);
      }
    }

    const opcoesResponsaveis = Array.from(responsaveis.entries()).map(([key, nome]) => ({
      value: `responsavel:${key}`,
      label: nome,
      meta: "ResponsÃ¡vel",
    }));
    const opcoesProdutosRaw = Array.from(produtosPorId.entries()).map(([id, produto]) => {
      const unidadeNormalizada = produto.unidade.trim().toLocaleLowerCase("pt-BR");
      const unidade = ({
        unidade: "un", unidades: "un", quilo: "kg", quilograma: "kg", quilogramas: "kg",
        grama: "g", gramas: "g", litro: "L", litros: "L", mililitro: "ml", mililitros: "ml",
      } as Record<string, string>)[unidadeNormalizada] ?? produto.unidade;
      const quantidade = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(produto.quantidade);
      return {
        value: `produto:${id}`,
        label: produto.nome,
        meta: `Total desperdiÃ§ado: ${quantidade} ${unidade}`,
      };
    });
    const prefixoQuantidadeProduto = `Total desperdi${String.fromCharCode(231)}ado`;
    const opcoesProdutos = opcoesProdutosRaw.map((option) => {
      const detalhe = option.meta?.slice(option.meta.indexOf(":") + 1).trim() ?? "";
      return { ...option, meta: `${prefixoQuantidadeProduto}: ${detalhe}` };
    });
    return [...opcoesResponsaveis, ...opcoesProdutos].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [desperdicios]);

  const desperdiciosBuscaTrim = desperdiciosBuscaNome.trim();
  const desperdiciosFiltrados = useMemo(() => {
    if (desperdiciosProdutoSelecionado !== null) {
      return desperdicios.filter((d) => d.itens?.some((item) => item.produto_id === desperdiciosProdutoSelecionado));
    }
    const q = desperdiciosBuscaTrim.toLocaleLowerCase("pt-BR");
    if (!q) return desperdicios;
    return desperdicios.filter((d) =>
      (d.responsavel_nome || "").trim().toLocaleLowerCase("pt-BR").includes(q)
    );
  }, [desperdicios, desperdiciosBuscaTrim, desperdiciosProdutoSelecionado]);

  const totalDesperdiciosVisivel = useMemo(
    () => desperdiciosFiltrados.reduce((s, d) => s + Number(d.total || 0), 0),
    [desperdiciosFiltrados]
  );

  useEffect(() => {
    if (isAuthenticated && isAdmin && activeTab === "config" && configSubTab === "usuarios") {
      void carregarUsuarios();
    }
  }, [isAuthenticated, isAdmin, activeTab, configSubTab]);

  useEffect(() => {
    const el = avatarMenuRef.current;
    if (!el) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (el.contains(target)) return;
      setAvatarMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showCreateUserForm) return;
    const draft = {
      username: newUsername,
      name: newName,
      email: newEmail,
      telefone: newTelefone,
      role: newRole,
      allowed_tabs: newUserTabs,
    };
    const isEmpty =
      draft.username.trim() === "" &&
      draft.name.trim() === "" &&
      draft.email.trim() === "" &&
      draft.telefone.trim() === "" &&
      draft.role === "funcionario" &&
      draft.allowed_tabs.length === 0;
    if (isEmpty) {
      clearFormDraft(newUserDraftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(newUserDraftKey, draft);
  }, [showCreateUserForm, newUserDraftKey, newUsername, newName, newEmail, newTelefone, newRole, newUserTabs]);

  useEffect(() => {
    if (!editUserModal || !editUserDraftKey) return;
    const origTabs =
      editUserModal.role === "admin"
        ? []
        : (editUserModal.allowed_tabs || []).filter((t) => t !== "config");
    const currentTabs = editUserRole === "admin" ? [] : editUserTabs;
    const tabsChanged =
      JSON.stringify([...origTabs].sort()) !== JSON.stringify([...currentTabs].sort());
    const hasChanges =
      editUserName.trim() !== (editUserModal.name || "").trim() ||
      editUserEmail.trim() !== (editUserModal.email || "").trim() ||
      editUserTelefone.trim() !== (editUserModal.telefone || "").trim() ||
      editUserRole !== editUserModal.role ||
      tabsChanged;
    if (!hasChanges) {
      clearFormDraft(editUserDraftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(editUserDraftKey, {
      name: editUserName,
      email: editUserEmail,
      telefone: editUserTelefone,
      role: editUserRole,
      allowed_tabs: editUserTabs,
    });
  }, [editUserModal, editUserDraftKey, editUserName, editUserEmail, editUserTelefone, editUserRole, editUserTabs]);

  useEffect(() => {
    if (!showInsumoModal) return;
    const draft = {
      nome: insumoNome,
      categoria: insumoCategoria,
      categoriaOutra: insumoCategoriaOutra,
      unidade: insumoUnidade,
      estoqueMinimo: insumoEstoqueMinimo,
      custo: insumoCusto,
    };
    const snapshot: InsumoFormSnapshot = {
      nome: draft.nome,
      categoria: draft.categoria === OPCAO_CATEGORIA_OUTRA ? draft.categoriaOutra.trim() : draft.categoria.trim(),
      unidade: draft.unidade,
      estoqueMinimo: draft.estoqueMinimo,
      custo: draft.custo,
    };
    const isNewEmpty =
      editingInsumoId == null &&
      draft.nome.trim() === "" &&
      draft.categoria.trim() === "" &&
      draft.categoriaOutra.trim() === "" &&
      draft.unidade.trim() === "" &&
      (draft.estoqueMinimo.trim() === "" || draft.estoqueMinimo.trim() === "0") &&
      draft.custo.trim() === "";
    if (isNewEmpty || !insumoFormHasChanges(snapshot, insumoFormBaseline)) {
      clearFormDraft(insumoDraftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(insumoDraftKey, draft);
  }, [
    showInsumoModal,
    insumoDraftKey,
    insumoNome,
    insumoCategoria,
    insumoCategoriaOutra,
    insumoUnidade,
    insumoEstoqueMinimo,
    insumoCusto,
    editingInsumoId,
    insumoFormBaseline,
  ]);

  const carregarProdutos = async () => {
    try {
      setProdutosLoading(true);
      const lista = await api.listarProdutos();
      setProdutosLista(lista);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar produtos");
    } finally {
      setProdutosLoading(false);
    }
  };

  const carregarProdutosVendasQuantidade = async () => {
    try {
      const totais = await api.obterProdutosVendasQuantidade();
      setProdutosVendasQuantidade(totais);
    } catch (e) {
      console.error(e);
      setProdutosVendasQuantidade({});
    }
  };

  /** Produtos alimentam a aba Produtos, contagens e o dropdown de categorias em Marketing → Promoções (mesma base: api.listarProdutos). */
  useEffect(() => {
    if (
      isAuthenticated &&
      (activeTab === "produtos" || activeTab === "marketing" || dashboardModalProdutosOpen)
    ) {
      void carregarProdutos();
    }
  }, [isAuthenticated, activeTab, dashboardModalProdutosOpen]);

  useEffect(() => {
    if (isAuthenticated && activeTab === "produtos") {
      void carregarProdutosVendasQuantidade();
    }
  }, [isAuthenticated, activeTab]);

  /** Garante categorias no modal Nova/Editar promoção (tipo Categoria): refetch ao abrir com esse tipo ou ao mudar para ele — evita dropdown vazio por lista ainda não carregada. */
  useEffect(() => {
    if (!isAuthenticated || !showFormPromocao || promocaoTipo !== "categoria") return;
    void carregarProdutos();
  }, [isAuthenticated, showFormPromocao, promocaoTipo]);

  const carregarInsumosTab = async () => {
    try {
      setLoadingInsumosTab(true);
      const [lista, cats] = await Promise.all([api.listarInsumos(), api.listarCategorias()]);
      setInsumosTabList(lista);
      setCategoriasList(cats);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar insumos");
    } finally {
      setLoadingInsumosTab(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && canViewTab('insumos') && activeTab === 'insumos') {
      void carregarInsumosTab();
    }
  }, [isAuthenticated, activeTab]);

  /** Categorias que possuem ao menos 1 produto cadastrado (lista dinâmica; sem categorias “órfãs” da tabela). */
  const produtosCategoriasOpcoes = useMemo(() => {
    const names = new Set<string>();
    for (const p of produtosLista) {
      const cat = (p.categoria || "").trim();
      if (cat) names.add(cat);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [produtosLista]);

  const insumoCategoriaResolvida = useMemo(() => {
    return insumoCategoria === OPCAO_CATEGORIA_OUTRA ? insumoCategoriaOutra.trim() : insumoCategoria.trim();
  }, [insumoCategoria, insumoCategoriaOutra]);

  const insumoModalShowAction = useMemo(() => {
    const snapshot: InsumoFormSnapshot = {
      nome: insumoNome,
      categoria: insumoCategoriaResolvida,
      unidade: insumoUnidade,
      estoqueMinimo: insumoEstoqueMinimo,
      custo: insumoCusto,
    };
    const canSave = insumoFormCanSave(snapshot);
    const hasChanges = insumoFormHasChanges(snapshot, insumoFormBaseline);
    return canSave && (editingInsumoId != null ? hasChanges : true);
  }, [
    insumoNome,
    insumoCategoriaResolvida,
    insumoUnidade,
    insumoEstoqueMinimo,
    insumoCusto,
    insumoFormBaseline,
    editingInsumoId,
  ]);

  const promocaoModalShowAction = useMemo(() => {
    const canSave = promocaoFormIsValid(
      promocaoNome,
      promocaoValor,
      promocaoTipo,
      promocaoCategoriasLinhas,
      promocaoItens,
    );
    const current = buildPromocaoFormSnapshot(
      promocaoNome,
      promocaoValor,
      promocaoTipo,
      promocaoCategoriasLinhas,
      promocaoItens,
    );
    const hasChanges = promocaoFormBaseline === null || current !== promocaoFormBaseline;
    return canSave && (editingPromocaoId != null ? hasChanges : true);
  }, [
    promocaoNome,
    promocaoValor,
    promocaoTipo,
    promocaoCategoriasLinhas,
    promocaoItens,
    promocaoFormBaseline,
    editingPromocaoId,
  ]);

  useEffect(() => {
    if (!showFormPromocao) return;
    const current = buildPromocaoFormSnapshot(
      promocaoNome,
      promocaoValor,
      promocaoTipo,
      promocaoCategoriasLinhas,
      promocaoItens,
    );
    if (promocaoFormBaseline !== null && current === promocaoFormBaseline) {
      clearFormDraft(promocaoDraftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(promocaoDraftKey, {
      nome: promocaoNome,
      valor: promocaoValor,
      tipo: promocaoTipo,
      categoriasLinhas: promocaoCategoriasLinhas,
      itens: promocaoItens,
    });
  }, [
    showFormPromocao,
    promocaoDraftKey,
    promocaoNome,
    promocaoValor,
    promocaoTipo,
    promocaoCategoriasLinhas,
    promocaoItens,
    promocaoFormBaseline,
  ]);

  const newUserModalShowAction = useMemo(() => {
    return (
      newUsername.trim() !== "" &&
      newPassword.length >= 4 &&
      (newRole === "admin" || newUserTabs.length > 0)
    );
  }, [newUsername, newPassword, newRole, newUserTabs]);

  const editUserModalShowAction = useMemo(() => {
    if (!editUserModal) return false;
    const origTabs =
      editUserModal.role === "admin"
        ? []
        : (editUserModal.allowed_tabs || []).filter((t) => t !== "config");
    const currentTabs = editUserRole === "admin" ? [] : editUserTabs;
    const tabsChanged =
      JSON.stringify([...origTabs].sort()) !== JSON.stringify([...currentTabs].sort());
    const hasChanges =
      editUserName.trim() !== (editUserModal.name || "").trim() ||
      editUserEmail.trim() !== (editUserModal.email || "").trim() ||
      editUserTelefone.trim() !== (editUserModal.telefone || "").trim() ||
      editUserRole !== editUserModal.role ||
      editUserPassword.trim() !== "" ||
      tabsChanged;
    const canSave = editUserRole === "admin" || editUserTabs.length > 0;
    return canSave && hasChanges;
  }, [
    editUserModal,
    editUserName,
    editUserEmail,
    editUserTelefone,
    editUserRole,
    editUserPassword,
    editUserTabs,
  ]);

  const produtoModalShowAction = useMemo(() => {
    const categoriaFinal =
      produtoCategoriaSelect === OPCAO_CATEGORIA_OUTRA ? produtoCategoriaOutra.trim() : produtoCategoriaSelect.trim();
    const canSave =
      produtoNome.trim() !== "" &&
      categoriaFinal !== "" &&
      (produtoPossuiComposicao || produtoQuantidadeMinima.trim() !== "");
    return canSave;
  }, [
    produtoNome,
    produtoCategoriaSelect,
    produtoCategoriaOutra,
    produtoPossuiComposicao,
    produtoQuantidadeMinima,
  ]);

  const produtosTabFiltrados = useMemo(() => {
    const list = produtosLista
      .filter((p) => {
        if (!matchCategoriasFiltro(p.categoria, produtosCategoriasFiltro)) return false;
        return true;
      })
      .filter((p) => textIncludesSearch(p.nome, searchProdutosTab));

    const vendasDe = (id: number) => produtosVendasQuantidade[id] ?? 0;

    switch (produtosOrdenacao) {
      case "nome-za":
        list.sort((a, b) => b.nome.localeCompare(a.nome, "pt-BR"));
        break;
      case "mais-vendidos":
        list.sort((a, b) => {
          const diff = vendasDe(b.id) - vendasDe(a.id);
          return diff !== 0 ? diff : a.nome.localeCompare(b.nome, "pt-BR");
        });
        break;
      case "menos-vendidos":
        list.sort((a, b) => {
          const diff = vendasDe(a.id) - vendasDe(b.id);
          return diff !== 0 ? diff : a.nome.localeCompare(b.nome, "pt-BR");
        });
        break;
      case "nome-az":
      default:
        list.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
        break;
    }

    return list;
  }, [produtosLista, produtosCategoriasFiltro, searchProdutosTab, produtosOrdenacao, produtosVendasQuantidade]);

  const insumosTabFiltrados = useMemo(() => {
    return insumosTabList
      .slice()
      .filter((i) => {
        return matchCategoriasFiltro(i.categoria, insumosTabCategoriasFiltro);
      })
      .filter((i) => textIncludesSearch(i.nome, searchInsumosTab))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [insumosTabList, insumosTabCategoriasFiltro, searchInsumosTab]);

  /** Categorias que possuem ao menos 1 insumo cadastrado (lista dinâmica). */
  const insumosCategoriasOpcoes = useMemo(() => {
    const names = new Set<string>();
    for (const i of insumosTabList) {
      const cat = (i.categoria || "").trim();
      if (cat) names.add(cat);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [insumosTabList]);

  useEffect(() => {
    setProdutosCategoriasFiltro((prev) => pruneCategoriasFiltro(prev, produtosCategoriasOpcoes));
  }, [produtosCategoriasOpcoes]);

  useEffect(() => {
    setInsumosTabCategoriasFiltro((prev) => pruneCategoriasFiltro(prev, insumosCategoriasOpcoes));
  }, [insumosCategoriasOpcoes]);

  useEffect(() => {
    if (!showProdutoModal && !showInsumoModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [showProdutoModal, showInsumoModal]);

  const carregarEntradas = async () => {
    try {
      setLoadingEntradas(true);
      const [prods, ins] = await Promise.all([api.listarProdutosSemComposicao(), api.listarInsumos()]);
      setEntradasProdutos(prods);
      setEntradasInsumos(ins);
      // Removida inicialização de valoresAtuais aqui para que o campo de "Valor pago" comece vazio para o novo lançamento
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar lista para entradas");
    } finally {
      setLoadingEntradas(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "estoque") {
      void carregarEntradas();
    }
  }, [isAuthenticated, activeTab]);

  const carregarVendas = async (params?: { start?: string; end?: string }, signal?: AbortSignal) => {
    try {
      setLoadingVendas(true);
      const lista = await api.listarVendas(params, { signal });
      if (signal?.aborted) return;
      setVendasLista(lista);
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) return;
      console.error(e);
      toast.error("Erro ao carregar vendas");
    } finally {
      if (!signal?.aborted) setLoadingVendas(false);
    }
  };

  const carregarDashboard = async (signal?: AbortSignal) => {
    if (loading || !isAuthenticated || !user || signal?.aborted) return;
    const params = getDashboardPeriodoRange(dashboardPeriodo, dashboardPersonalizadoAplicado);
    if (!params) return;
    dashboardRequest.current?.abort();
    const controller = new AbortController();
    dashboardRequest.current = controller;
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const comparativoRange = getDashboardComparativoRange(dashboardPeriodo);
    setDashboardLoading(true);
    setDashboardErro(false);
    try {
      const data = await loadDashboardData(
        params,
        comparativoRange ? { start: comparativoRange.fetchStart, end: comparativoRange.fetchEnd } : null,
        controller.signal,
      );
      if (controller.signal.aborted || dashboardRequest.current !== controller) return;
      setDashboardVendas(data.vendas);
      setDashboardDespesas(normalizarDespesasDashboard(data.despesas));
      setDashboardComparativoVendas(data.vendasComparativo);
      setDashboardComparativoDespesas(normalizarDespesasDashboard(data.despesasComparativo));
      setDashboardProdutosCusto(data.produtos);
      setDashboardInsumosCusto(data.insumos);
      setDashboardComposicaoPorProduto(data.composicaoPorProduto);
      setDashboardFalhas(data.falhas);
    } catch (e) {
      if (isAbortError(e) || controller.signal.aborted || dashboardRequest.current !== controller) return;
      setDashboardErro(true);
      console.error(e);
      toast.error("Erro ao carregar dashboard");
    } finally {
      signal?.removeEventListener("abort", abort);
      if (!controller.signal.aborted && dashboardRequest.current === controller) setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setVendasMetaMes([]);
      return;
    }
    let activeController: AbortController | null = null;
    const loadMetaMes = async () => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      try {
        const { start, end } = getMesAtualRangeIsoSP();
        const lista = await api.listarVendas({ start, end }, { signal: controller.signal });
        if (!controller.signal.aborted) setVendasMetaMes(lista);
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        setVendasMetaMes([]);
      }
    };
    void loadMetaMes();
    const onVis = () => {
      if (document.visibilityState === "visible") void loadMetaMes();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      activeController?.abort();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isAuthenticated]);

  /** Período para aba Financeiro (mesmo padrão de vendas). */
  const getFinanceiroPeriodoRange = (): { start?: string; end?: string } => {
    const today = getTodaySaoPaulo();
    switch (financeiroPeriodo) {
      case 'hoje':
        return { start: today, end: today };
      case 'ontem': {
        const yesterday = addDaysIso(today, -1);
        return { start: yesterday, end: yesterday };
      }
      case '7':
        return { start: addDaysIso(today, -6), end: today };
      case '30':
        return { start: addDaysIso(today, -29), end: today };
      case 'personalizado':
        if (financeiroPersonalizadoInicio && financeiroPersonalizadoFim) {
          return { start: financeiroPersonalizadoInicio, end: financeiroPersonalizadoFim };
        }
        return {};
      default:
        return { start: today, end: today };
    }
  };

  const carregarDespesasFinanceiro = async (signal?: AbortSignal) => {
    const range = getFinanceiroPeriodoRange();
    if (!range.start && !range.end) return;
    setLoadingDespesasFinanceiro(true);
    try {
      const res = await api.listarDespesasPorPeriodo(
        { start: range.start ?? range.end, end: range.end ?? range.start },
        { signal },
      );
      if (signal?.aborted) return;
      setDespesasFinanceiro(
        res.despesas.map((d) => ({
          ...d,
          classificacao: classificacaoDespesaSegura(d.classificacao),
        })),
      );
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) return;
      console.error(e);
      toast.error("Erro ao carregar despesas");
      setDespesasFinanceiro([]);
    } finally {
      if (!signal?.aborted) setLoadingDespesasFinanceiro(false);
    }
  };

  const handleFinanceiroSaidaChange = async (d: Despesa, next: "despesa" | "compra" | "outro") => {
    const cur = classificacaoDespesaSegura(d.classificacao);
    if (next === cur) return;
    setFinanceiroSaidaUpdatingId(d.id);
    try {
      const updated = await api.atualizarDespesa({
        id: d.id,
        nome: d.nome,
        valor: Number(d.valor),
        tipo: d.tipo,
        classificacao: next,
        data: d.data ?? undefined,
      });
      const cls = classificacaoDespesaSegura(updated.classificacao);
      setDespesasFinanceiro((prev) =>
        prev.map((x) =>
          x.id === d.id
            ? {
                ...x,
                nome: updated.nome,
                valor: Number(updated.valor),
                tipo: updated.tipo,
                classificacao: cls,
                data: updated.data ?? x.data,
                inicio_mes: updated.inicio_mes !== undefined ? updated.inicio_mes : x.inicio_mes,
                updatedAt: updated.updatedAt ?? x.updatedAt,
              }
            : x,
        ),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao atualizar saída");
    } finally {
      setFinanceiroSaidaUpdatingId(null);
    }
  };

  const aplicarPeriodoFinanceiro = (signal?: AbortSignal) => {
    const range = getFinanceiroPeriodoRange();
    if (range.start || range.end) {
      void carregarVendas(
        range.start || range.end ? { start: range.start ?? range.end, end: range.end ?? range.start } : undefined,
        signal,
      );
      void carregarDespesasFinanceiro(signal);
    }
  };

  const abrirModalExportarVendasFiscal = () => {
    const range = getFinanceiroPeriodoRange();
    const today = getTodaySaoPaulo();
    const inicio = range.start ?? range.end ?? today;
    const fim = range.end ?? range.start ?? today;
    setExportarVendasInicio(inicio);
    setExportarVendasFim(fim);
    setShowModalExportarVendasFiscal(true);
  };

  const handleExportarVendasFiscal = async ({
    inicio,
    fim,
    formato,
  }: ExportarVendasFiscalParams): Promise<boolean> => {
    try {
      const vendas = await api.listarVendasFiscais({ start: inicio, end: fim });
      if (vendas.length === 0) {
        toast.message("Nenhuma venda encontrada no período selecionado.");
        return false;
      }
      const periodo = { inicio, fim };
      const blob =
        formato === "xml"
          ? buildVendasFiscalXmlBlob(vendas, periodo)
          : buildVendasFiscalXlsxBlob(vendas);
      baixarBlob(blob, nomeArquivoVendasFiscal(periodo, formato));
      toast.success("Exportacao gerada com sucesso.");
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao exportar vendas");
      return false;
    }
  };

  const sortDespesasByDataDesc = (a: Despesa, b: Despesa) => {
    const da = (a.data ?? "").trim();
    const db = (b.data ?? "").trim();
    if (db !== da) return db.localeCompare(da);
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  };

  const despesasFinanceiroSoDespesasTab = useMemo(
    () =>
      despesasFinanceiro
        .filter((d) => d.classificacao === "despesa")
        .sort(sortDespesasByDataDesc),
    [despesasFinanceiro],
  );
  const despesasFinanceiroSoComprasTab = useMemo(
    () =>
      despesasFinanceiro
        .filter((d) => d.classificacao === "compra")
        .sort(sortDespesasByDataDesc),
    [despesasFinanceiro],
  );
  const despesasFinanceiroSoOutrosTab = useMemo(
    () =>
      despesasFinanceiro
        .filter((d) => d.classificacao === "outro")
        .sort(sortDespesasByDataDesc),
    [despesasFinanceiro],
  );
  const despesasFinanceiroSoDespesasFiltradas = useMemo(() => {
    const q = searchFinanceiroDespesas.trim().toLowerCase();
    if (!q) return despesasFinanceiroSoDespesasTab;
    return despesasFinanceiroSoDespesasTab.filter((d) => d.nome.toLowerCase().includes(q));
  }, [despesasFinanceiroSoDespesasTab, searchFinanceiroDespesas]);
  const despesasFinanceiroSoComprasFiltradas = useMemo(() => {
    const q = searchFinanceiroCompras.trim().toLowerCase();
    if (!q) return despesasFinanceiroSoComprasTab;
    return despesasFinanceiroSoComprasTab.filter((d) => d.nome.toLowerCase().includes(q));
  }, [despesasFinanceiroSoComprasTab, searchFinanceiroCompras]);
  const despesasFinanceiroSoOutrosFiltradas = useMemo(() => {
    const q = searchFinanceiroOutros.trim().toLowerCase();
    if (!q) return despesasFinanceiroSoOutrosTab;
    return despesasFinanceiroSoOutrosTab.filter((d) => d.nome.toLowerCase().includes(q));
  }, [despesasFinanceiroSoOutrosTab, searchFinanceiroOutros]);
  const financeiroTotalDespesasCard = useMemo(
    () => despesasFinanceiroSoDespesasFiltradas.reduce((s, d) => s + Number(d.valor || 0), 0),
    [despesasFinanceiroSoDespesasFiltradas],
  );
  const financeiroTotalComprasCard = useMemo(
    () => despesasFinanceiroSoComprasFiltradas.reduce((s, d) => s + Number(d.valor || 0), 0),
    [despesasFinanceiroSoComprasFiltradas],
  );
  const financeiroTotalOutrosCard = useMemo(
    () => despesasFinanceiroSoOutrosFiltradas.reduce((s, d) => s + Number(d.valor || 0), 0),
    [despesasFinanceiroSoOutrosFiltradas],
  );
  const financeiroTotalVendasCard = useMemo(
    () => vendasLista.reduce((s, v) => s + Number(v.total || 0), 0),
    [vendasLista],
  );

  const dashboardTotalVendas = useMemo(
    () => dashboardVendas.reduce((s, v) => s + Number(v.total ?? 0), 0),
    [dashboardVendas],
  );
  const dashboardQtdVendas = useMemo(() => dashboardVendas.length, [dashboardVendas]);
  /** Total do caixa no período ÷ quantidade de vendas. */
  const dashboardTicketMedio = useMemo(() => {
    if (dashboardQtdVendas === 0) return 0;
    return dashboardTotalVendas / dashboardQtdVendas;
  }, [dashboardTotalVendas, dashboardQtdVendas]);
  const dashboardLucro = useMemo(
    () => dashboardFalhas.includes("despesas") ? null : calcularLucroDashboard(dashboardTotalVendas, dashboardDespesas),
    [dashboardTotalVendas, dashboardDespesas, dashboardFalhas],
  );
  const dashboardCustoProdutosVendidos = useMemo(() => {
    if (dashboardFalhas.some((f) => f === "produtos" || f === "insumos" || f.startsWith("composicao:"))) return null;
    const ctx = buildDashboardCustoContext(
      dashboardProdutosCusto,
      dashboardInsumosCusto,
      dashboardComposicaoPorProduto,
    );
    return calcularCustoProdutosVendidos(dashboardVendas, ctx);
  }, [dashboardVendas, dashboardProdutosCusto, dashboardInsumosCusto, dashboardComposicaoPorProduto, dashboardFalhas]);
  const dashboardComparativoPeriodo = useMemo(() => {
    if (dashboardFalhas.some((f) => f === "vendasComparativo" || f === "despesasComparativo")) return dashboardComparativoVazio();
    const comparativoRange = getDashboardComparativoRange(dashboardPeriodo);
    if (!comparativoRange) {
      return dashboardComparativoVazio();
    }

    if (
      !dashboardTemPeriodoAnteriorCompleto(
        comparativoRange,
        dashboardComparativoVendas,
        dashboardComparativoDespesas,
      )
    ) {
      return dashboardComparativoVazio();
    }

    const vendasAtuais = dashboardComparativoVendas.filter((v) =>
      isoDateInRange(v.createdAt, comparativoRange.atualStart, comparativoRange.atualEnd),
    );
    const vendasAnteriores = dashboardComparativoVendas.filter((v) =>
      isoDateInRange(v.createdAt, comparativoRange.anteriorStart, comparativoRange.anteriorEnd),
    );
    const despesasAtuais = dashboardComparativoDespesas.filter((d) =>
      isoDateInRange(d.data ?? d.createdAt, comparativoRange.atualStart, comparativoRange.atualEnd),
    );
    const despesasAnteriores = dashboardComparativoDespesas.filter((d) =>
      isoDateInRange(d.data ?? d.createdAt, comparativoRange.anteriorStart, comparativoRange.anteriorEnd),
    );

    const faturamentoAtual = vendasAtuais.reduce((s, v) => s + Number(v.total ?? 0), 0);
    const faturamentoAnterior = vendasAnteriores.reduce((s, v) => s + Number(v.total ?? 0), 0);
    const qtdAtual = vendasAtuais.length;
    const qtdAnterior = vendasAnteriores.length;
    const ticketAtual = qtdAtual > 0 ? faturamentoAtual / qtdAtual : 0;
    const ticketAnterior = qtdAnterior > 0 ? faturamentoAnterior / qtdAnterior : 0;
    const lucroAtual = calcularLucroDashboard(faturamentoAtual, despesasAtuais);
    const lucroAnterior = calcularLucroDashboard(faturamentoAnterior, despesasAnteriores);

    return {
      faturamento: calcularComparacaoPercentual(faturamentoAtual, faturamentoAnterior, comparativoRange.label),
      qtdVendas: calcularComparacaoPercentual(qtdAtual, qtdAnterior, comparativoRange.label),
      ticketMedio: calcularComparacaoPercentual(ticketAtual, ticketAnterior, comparativoRange.label),
      lucro: dashboardFalhas.includes("despesas") ? null : calcularComparacaoPercentual(lucroAtual, lucroAnterior, comparativoRange.label),
    };
  }, [dashboardPeriodo, dashboardComparativoVendas, dashboardComparativoDespesas, dashboardFalhas]);
  const dashboardMeiosPagamento = useMemo(() => {
    const out = { pix: 0, credito: 0, debito: 0, dinheiro: 0 };
    for (const v of dashboardVendas) {
      out.pix += Number(v.pagamento?.pix ?? 0);
      out.credito += Number(v.pagamento?.credito ?? 0);
      out.debito += Number(v.pagamento?.debito ?? 0);
      out.dinheiro += Number(v.pagamento?.dinheiro ?? 0);
    }
    return out;
  }, [dashboardVendas]);
  const dashboardMeiosPagamentoRows = useMemo<DashboardMeioPagamentoRow[]>(() => {
    const rows: DashboardMeioPagamentoBaseRow[] = [
      { key: "pix", label: "Pix", valor: dashboardMeiosPagamento.pix },
      { key: "credito", label: "Cartão de crédito", valor: dashboardMeiosPagamento.credito },
      { key: "debito", label: "Cartão de débito", valor: dashboardMeiosPagamento.debito },
      { key: "dinheiro", label: "Dinheiro", valor: dashboardMeiosPagamento.dinheiro },
    ];
    const opacityByKey = new Map<DashboardMeioPagamentoKey, number>();

    rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .sort(
        (a, b) =>
          Math.max(0, a.row.valor) - Math.max(0, b.row.valor) || a.originalIndex - b.originalIndex,
      )
      .forEach(({ row }, rank) => {
        opacityByKey.set(row.key, getDashboardPagamentoOpacity(rank, rows.length));
      });

    return rows.map((row) => ({
      ...row,
      fill: getDashboardColorWithOpacity(clientConfig.primary_color, opacityByKey.get(row.key) ?? 1),
    }));
  }, [clientConfig.primary_color, dashboardMeiosPagamento]);
  const dashboardMeiosPagamentoTotal = useMemo(
    () => dashboardMeiosPagamentoRows.reduce((total, row) => total + Math.max(0, row.valor), 0),
    [dashboardMeiosPagamentoRows],
  );
  const dashboardMeiosPagamentoChartRows = useMemo<DashboardMeioPagamentoChartRow[]>(
    () =>
      dashboardMeiosPagamentoTotal > 0
        ? dashboardMeiosPagamentoRows
            .filter((row) => row.valor > 0)
            .map((row) => ({
              ...row,
              percentual: (row.valor / dashboardMeiosPagamentoTotal) * 100,
            }))
        : [],
    [dashboardMeiosPagamentoRows, dashboardMeiosPagamentoTotal],
  );
  const dashboardMeiosPagamentoTemDados = dashboardMeiosPagamentoChartRows.length > 0;
  const dashboardProdutosVendidos = useMemo(() => {
    const map = new Map<string, { nome: string; quantidade: number; produtoId: number | null }>();
    for (const v of dashboardVendas) {
      for (const it of v.itens ?? []) {
        const nome = String(it.nome_produto ?? "").trim();
        if (!nome) continue;
        const produtoId = Number(it.produto_id ?? 0) || null;
        const key = produtoId ? `id:${produtoId}` : `nome:${nome.toLowerCase()}`;
        const prev = map.get(key);
        if (prev) {
          prev.quantidade += Number(it.quantidade ?? 0);
        } else {
          map.set(key, { nome, quantidade: Number(it.quantidade ?? 0), produtoId });
        }
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.quantidade - a.quantidade || a.nome.localeCompare(b.nome, "pt-BR"));
  }, [dashboardVendas]);
  const dashboardProdutosTop = useMemo(() => dashboardProdutosVendidos.slice(0, 3), [dashboardProdutosVendidos]);
  const dashboardProdutosTopChart = useMemo(() => {
    const rows = dashboardProdutosTop.map((p) => ({
      nome: formatDisplayText(p.nome),
      quantidade: p.quantidade,
    }));
    const xMax = Math.max(...rows.map((r) => r.quantidade), 0) || 1;
    return { rows, xMax, xTicks: dashboardProdutosXAxisTicks(xMax) };
  }, [dashboardProdutosTop]);
  const dashboardVendasHoraRange = useMemo(() => {
    const inicioCfg = dashboardHoraAbertura;
    const fimCfg = dashboardHoraEncerramento;
    if (inicioCfg != null && fimCfg != null && inicioCfg <= fimCfg) {
      return { inicio: inicioCfg, fim: fimCfg };
    }
    return { inicio: DASHBOARD_VENDAS_HORA_INICIO, fim: DASHBOARD_VENDAS_HORA_FIM };
  }, [dashboardHoraAbertura, dashboardHoraEncerramento]);

  const dashboardVendasPorHora = useMemo(() => {
    const { inicio, fim } = dashboardVendasHoraRange;
    const buckets: { hora: string; vendas: number }[] = [];
    for (let h = inicio; h <= fim; h++) {
      buckets.push({ hora: `${String(h).padStart(2, "0")}:00`, vendas: 0 });
    }
    for (const v of dashboardVendas) {
      const d = parseDateTimeLocal(v.createdAt);
      const h = d.getHours();
      if (h >= inicio && h <= fim) {
        buckets[h - inicio].vendas += 1;
      }
    }
    return buckets;
  }, [dashboardVendas, dashboardVendasHoraRange]);

  const dashboardVendasPorHoraTicks = useMemo(
    () =>
      buildVendasHoraTicks(
        dashboardVendasHoraRange.inicio,
        dashboardVendasHoraRange.fim,
        dashboardHoraTickStep,
      ),
    [dashboardVendasHoraRange, dashboardHoraTickStep],
  );

  const dashboardVendasPorHoraYMax = useMemo(
    () => Math.max(0, ...dashboardVendasPorHora.map((b) => b.vendas)),
    [dashboardVendasPorHora],
  );
  /** Escala mínima quando tudo é 0: mantém ticks legíveis e desenha a série na base (eixo X). */
  const dashboardVendasPorHoraYAxisMax = dashboardVendasPorHoraYMax > 0 ? dashboardVendasPorHoraYMax : 1;
  const dashboardVendasPorHoraAllZero = dashboardVendasPorHoraYMax === 0;

  const faturamentoMesMeta = useMemo(() => totalFaturamentoVendas(vendasMetaMes), [vendasMetaMes]);
  const barraMetaMes = useMemo(() => calcularBarraMetaMensal(faturamentoMesMeta), [faturamentoMesMeta]);
  const linhasMetaMesModal = useMemo(() => montarLinhasModal(vendasMetaMes), [vendasMetaMes]);

  const produtosCategoriaPorId = useMemo(() => {
    const byId = new Map<number, string>();
    for (const p of produtosLista) {
      const cat = (p.categoria ?? "").trim();
      if (p.id > 0 && cat) byId.set(p.id, cat);
    }
    return byId;
  }, [produtosLista]);

  const produtosCategoriaPorNome = useMemo(() => {
    const byName = new Map<string, string>();
    for (const p of produtosLista) {
      const n = p.nome.trim().toLowerCase();
      const cat = (p.categoria ?? "").trim();
      if (n && cat && !byName.has(n)) byName.set(n, cat);
    }
    return byName;
  }, [produtosLista]);

  /** Categorias dos produtos que aparecem em “mais vendidos” (via cadastro de produtos). */
  const dashboardModalCategoriasOpcoes = useMemo(() => {
    const names = new Set<string>();
    for (const row of dashboardProdutosVendidos) {
      const c = row.produtoId
        ? (produtosCategoriaPorId.get(row.produtoId) ?? "").trim()
        : (produtosCategoriaPorNome.get(row.nome.trim().toLowerCase()) ?? "").trim();
      if (c) names.add(c);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [dashboardProdutosVendidos, produtosCategoriaPorId, produtosCategoriaPorNome]);

  const dashboardProdutosVendidosModalFiltrados = useMemo(() => {
    let list = dashboardProdutosVendidos;
    const cat = dashboardModalProdutosCategoria.trim();
    if (cat && cat !== "__todos__") {
      list = list.filter((p) => {
        const categoria = p.produtoId
          ? (produtosCategoriaPorId.get(p.produtoId) ?? "").trim()
          : (produtosCategoriaPorNome.get(p.nome.trim().toLowerCase()) ?? "").trim();
        return categoria === cat;
      });
    }
    return list.filter((p) => textIncludesSearch(p.nome, dashboardModalProdutosBusca));
  }, [
    dashboardProdutosVendidos,
    dashboardModalProdutosBusca,
    dashboardModalProdutosCategoria,
    produtosCategoriaPorId,
    produtosCategoriaPorNome,
  ]);

  useEffect(() => {
    if (!(isAuthenticated && canViewTab("financeiro") && activeTab === "financeiro")) return;
    if (financeiroPeriodo === "personalizado") return;
    const controller = new AbortController();
    aplicarPeriodoFinanceiro(controller.signal);
    return () => controller.abort();
  }, [isAuthenticated, activeTab, financeiroPeriodo]);

  useEffect(() => {
    if (!(isAuthenticated && canViewTab("financeiro") && activeTab === "financeiro")) return;
    if (financeiroPeriodo !== "personalizado") return;
    const controller = new AbortController();
    aplicarPeriodoFinanceiro(controller.signal);
    return () => controller.abort();
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (loading || !(isAuthenticated && canViewTab("dashboard") && activeTab === "dashboard")) return;
    const controller = new AbortController();
    void carregarDashboard(controller.signal);
    return () => controller.abort();
  }, [loading, isAuthenticated, user?.id, activeTab, dashboardPeriodo, dashboardPersonalizadoAplicado]);

  useEffect(() => {
    if (!(isAuthenticated && canViewTab("dashboard") && activeTab === "dashboard")) return;
    let cancelled = false;
    void (async () => {
      try {
        const h = await api.obterHorarioFuncionamento();
        if (cancelled) return;
        setDashboardHoraAbertura(parseHorarioHora(h.horario_abertura));
        setDashboardHoraEncerramento(parseHorarioHora(h.horario_encerramento));
      } catch {
        if (cancelled) return;
        setDashboardHoraAbertura(null);
        setDashboardHoraEncerramento(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mqMobile = window.matchMedia("(max-width: 767px)");
    const mqTablet = window.matchMedia("(min-width: 768px) and (max-width: 1023px)");
    const update = () => {
      if (mqMobile.matches) setDashboardHoraTickStep(3);
      else if (mqTablet.matches) setDashboardHoraTickStep(2);
      else setDashboardHoraTickStep(1);
    };
    update();
    mqMobile.addEventListener("change", update);
    mqTablet.addEventListener("change", update);
    return () => {
      mqMobile.removeEventListener("change", update);
      mqTablet.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    setVendaActionMenu(null);
    setDespesaActionMenu(null);
  }, [financeiroSubTab]);

  useEffect(() => {
    const el = financeiroVendasListRef.current;
    if (!el || activeTab !== 'financeiro') return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-action-menu-backdrop]")) {
        setVendaActionMenu(null);
        return;
      }
      if (t.closest("[data-action-menu-portal]")) return;
      const target = e.target as Node;
      if (!el.contains(target)) {
        setVendaActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeTab]);

  const handleImprimirVenda = async (v: Venda) => {
    setVendaActionMenu(null);
    const itens = v.itens ?? [];
    const subtotalGeral = itens.reduce((s, i) => s + Number(i.subtotal ?? 0), 0);
    const totalGeral = Number(v.total);
    const desconto = Math.max(0, subtotalGeral - totalGeral);
    const vendaCupom = {
      venda_id: Number(v.id),
      data_hora: v.createdAt,
      vendedor: "Sistema",
      itens: itens.map((i) => ({
        produto_id: Number(i.produto_id ?? 0),
        nome_produto: i.nome_produto ?? "",
        quantidade: Number(i.quantidade),
        preco_unitario: Number(i.preco_unitario),
        subtotal: Number(i.subtotal),
      })),
      total_pecas: itens.reduce((s, i) => s + Number(i.quantidade ?? 0), 0),
      subtotal_geral: subtotalGeral,
      total_geral: totalGeral,
      ...(desconto > 0.005 ? { desconto } : {}),
      pagamento: {
        pix: Number(v.pagamento?.pix ?? 0),
        dinheiro: Number(v.pagamento?.dinheiro ?? 0),
        credito: Number(v.pagamento?.credito ?? 0),
        debito: Number(v.pagamento?.debito ?? 0),
      },
    };
    try {
      const html = gerarCupomHtml(vendaCupom, receiptToEmpresaCupom(clientConfig.receipt));
      await imprimirCupom(html);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao abrir impressão do cupom");
    }
  };

  useEffect(() => {
    if (activeTab === 'produtos' || activeTab === 'insumos') setSubmenuItensAberto(true);
  }, [activeTab]);

  const carregarFornecedorLancamentos = async () => {
    try {
      setLoadingFornecedorLancamentos(true);
      const res = await api.listarFornecedorLancamentos();
      setFornecedorLancamentos(res.lancamentos);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar lançamentos.");
    } finally {
      setLoadingFornecedorLancamentos(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'fornecedores') {
      void carregarFornecedorLancamentos();
    }
  }, [isAuthenticated, activeTab]);

  const fornecedorValorPendenteSoma = useMemo(
    () =>
      fornecedorLancamentos
        .filter((x) => x.status === "pendente")
        .reduce((s, x) => s + Number(x.valor), 0),
    [fornecedorLancamentos],
  );
  const fornecedorValorPagoSoma = useMemo(
    () =>
      fornecedorLancamentos
        .filter((x) => x.status === "pago")
        .reduce((s, x) => s + Number(x.valor), 0),
    [fornecedorLancamentos],
  );
  const fornecedorLancamentosFiltrados = useMemo(() => {
    const filtered = fornecedorLancamentos.filter(
      (x) => x.status === fornecedorStatusFiltro && textIncludesSearch(x.fornecedor, searchFornecedorLancamentos),
    );
    if (fornecedorStatusFiltro === "pendente") {
      return [...filtered].sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento));
    }
    return filtered;
  }, [fornecedorLancamentos, fornecedorStatusFiltro, searchFornecedorLancamentos]);

  const carregarConferencias = async () => {
    const today = getTodaySaoPaulo();
    let start: string | undefined;
    let end: string | undefined;
    switch (conferenciaPeriodo) {
      case 'hoje':
        start = today;
        end = today;
        break;
      case 'ontem': {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - 1);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const da = String(d.getDate()).padStart(2, "0");
        start = `${y}-${m}-${da}`;
        end = start;
        break;
      }
      case '7': {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - 6);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const da = String(d.getDate()).padStart(2, "0");
        start = `${y}-${m}-${da}`;
        end = today;
        break;
      }
      case '30': {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - 29);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const da = String(d.getDate()).padStart(2, "0");
        start = `${y}-${m}-${da}`;
        end = today;
        break;
      }
      case 'personalizado':
        if (conferenciaPersonalizadoInicio && conferenciaPersonalizadoFim) {
          start = conferenciaPersonalizadoInicio;
          end = conferenciaPersonalizadoFim;
        }
        break;
    }
    setLoadingConferencias(true);
    try {
      const lista = await api.listarConferencias(start || end ? { start: start ?? end, end: end ?? start } : undefined);
      setConferenciasLista(Array.isArray(lista) ? lista : []);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar conferências");
      setConferenciasLista([]);
    } finally {
      setLoadingConferencias(false);
    }
  };

  const handleExcluirConferencia = async (id: number) => {
    if (!confirm("Deseja apagar esta conferência?")) return;
    try {
      setDeletingConferenciaId(id);
      await api.excluirConferencia(id);
      setConferenciasLista((prev) => prev.filter((c) => c.id !== id));
      if (expandedConferenciaId === id) setExpandedConferenciaId(null);
      toast.success("Conferência excluída");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir conferência");
    } finally {
      setDeletingConferenciaId(null);
      setConferenciaActionMenu(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated && canViewTab('conferencia') && activeTab === 'conferencia') {
      if (conferenciaPeriodo === 'personalizado' && (!conferenciaPersonalizadoInicio || !conferenciaPersonalizadoFim)) return;
      void carregarConferencias();
    }
  }, [isAuthenticated, activeTab, conferenciaPeriodo, conferenciaPersonalizadoInicio, conferenciaPersonalizadoFim]);

  useEffect(() => {
    if (conferenciaPeriodo !== 'personalizado' || activeTab !== 'conferencia') return;
    if (!conferenciaPersonalizadoInicio || !conferenciaPersonalizadoFim) return;
    void carregarConferencias();
  }, [conferenciaPersonalizadoInicio, conferenciaPersonalizadoFim, conferenciaPeriodo, activeTab]);

  useEffect(() => {
    if (isAuthenticated && canViewTab('marketing') && activeTab === 'marketing' && produtosCaixa.length === 0) {
      api.listarProdutosCaixa().then(setProdutosCaixa).catch(() => toast.error("Erro ao carregar produtos"));
    }
  }, [isAuthenticated, activeTab]);

  const carregarPromocoes = async () => {
    setLoadingPromocoes(true);
    try {
      const lista = await api.listarPromocoes();
      setPromocoesLista(lista);
    } catch {
      toast.error("Erro ao carregar promoções");
    } finally {
      setLoadingPromocoes(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && canViewTab('marketing') && activeTab === 'marketing') {
      void carregarPromocoes();
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (editingPromocaoId != null) {
      api.obterPromocao(editingPromocaoId).then((p) => {
        if (p.tipo === "configuravel") {
          toast.error("Promoções configuráveis legadas não podem ser editadas nesta tela.");
          setEditingPromocaoId(null);
          return;
        }
        const tipoPromo: "fixa" | "categoria" = p.tipo === "categoria" ? "categoria" : "fixa";
        let categoriasLinhas: Array<{ categoria: string; quantidade: string }>;
        if (p.tipo === "categoria") {
          const cats = p.categorias;
          if (Array.isArray(cats) && cats.length > 0) {
            categoriasLinhas = cats.map((c) => ({ categoria: c.categoria, quantidade: String(c.quantidade) }));
          } else if (p.categoria_produto) {
            categoriasLinhas = [{ categoria: p.categoria_produto, quantidade: "1" }];
          } else {
            categoriasLinhas = [];
          }
        } else {
          categoriasLinhas = [];
        }
        const itensPromo = (p.itens ?? []).map((i) => ({
          produto_id: i.produto_id,
          nome: i.produto_nome,
          quantidade: i.quantidade,
        }));
        const draft = loadFormDraft<{
          nome?: string;
          valor?: string;
          tipo?: "fixa" | "categoria";
          categoriasLinhas?: Array<{ categoria: string; quantidade: string }>;
          itens?: Array<{ produto_id: number; nome: string; quantidade: number }>;
        }>(buildFormDraftKey("promocao", p.id));
        setPromocaoNome(draft?.nome ?? p.nome);
        setPromocaoValor(draft?.valor ?? String(p.valor));
        setPromocaoTipo(draft?.tipo ?? tipoPromo);
        setPromocaoCategoriasLinhas(Array.isArray(draft?.categoriasLinhas) ? draft.categoriasLinhas : categoriasLinhas);
        setPromocaoItens(Array.isArray(draft?.itens) ? draft.itens : itensPromo);
        setPromocaoFormBaseline(buildPromocaoFormSnapshot(p.nome, String(p.valor), tipoPromo, categoriasLinhas, itensPromo));
        setShowFormPromocao(true);
      }).catch(() => toast.error("Erro ao carregar promoção"));
    }
  }, [editingPromocaoId]);

  useEffect(() => {
    if (activeTab !== "marketing") return;
    if (showFormPromocao) return;
    if (expandedPromocaoId == null && promocaoActionMenu == null) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-action-menu-backdrop]")) {
        setPromocaoActionMenu(null);
        return;
      }
      if (t.closest("[data-action-menu-portal]")) return;
      const el = marketingPromocoesRef.current;
      if (el && !el.contains(e.target as Node)) {
        setExpandedPromocaoId(null);
        setPromocaoActionMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [activeTab, expandedPromocaoId, promocaoActionMenu, showFormPromocao]);

  const handleExcluirCategoria = async (category: { id: number; nome: string }) => {
    const snapshotCats = categoriasList;
    const nome = category.nome.trim();

    setCategoriasList((prev) => prev.filter((c) => c.nome.trim().toLowerCase() !== nome.toLowerCase()));
    if (produtoCategoriaSelect === category.nome) {
      setProdutoCategoriaSelect("");
      setProdutoCategoriaOutra("");
    }
    if (insumoCategoria === category.nome) {
      setInsumoCategoria("");
      setInsumoCategoriaOutra("");
    }
    setProdutosLista((prev) => limparCategoriaEmLista(prev, nome));
    setInsumosTabList((prev) => limparCategoriaEmLista(prev, nome));
    setInsumos((prev) => limparCategoriaEmLista(prev, nome));
    setProdutosSemComposicao((prev) => limparCategoriaEmLista(prev, nome));
    setProdutosBaixoEstoque((prev) => limparCategoriaEmLista(prev, nome));
    setInsumosEstoqueBaixo((prev) => limparCategoriaEmLista(prev, nome));

    try {
      const res = await api.excluirCategoria(category.id, category.nome);
      setCategoriasList(res.categorias);
      void carregarProdutos();
      void carregarInsumosTab();
    } catch (e) {
      setCategoriasList(snapshotCats);
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Erro ao remover categoria");
    }
  };

  const abrirModalNovoInsumo = async () => {
    setEditingInsumoId(null);
    setInsumoFormBaseline(null);
    const draft = loadFormDraft<{
      nome?: string;
      categoria?: string;
      categoriaOutra?: string;
      unidade?: string;
      estoqueMinimo?: string;
      custo?: string;
    }>(buildFormDraftKey("insumo", "new"));
    setInsumoNome(draft?.nome ?? "");
    setInsumoCategoria(draft?.categoria ?? "");
    setInsumoCategoriaOutra(draft?.categoriaOutra ?? "");
    setInsumoUnidade(draft?.unidade ?? "");
    setInsumoEstoqueMinimo(draft?.estoqueMinimo ?? "0");
    setInsumoCusto(draft?.custo ?? "");
    setShowInsumoModal(true);
    if (categoriasList.length === 0) {
      try {
        const cats = await api.listarCategorias();
        setCategoriasList(cats);
      } catch (_) { /* ignore */ }
    }
  };

  const carregarInsumoParaEdicao = (insumo: Insumo) => {
    const baseline: InsumoFormSnapshot = {
      nome: insumo.nome ?? "",
      categoria: insumo.categoria ?? "",
      unidade: insumoUnidadeParaSelect(insumo.unidade ?? ""),
      estoqueMinimo: insumo.estoqueMinimo != null ? String(insumo.estoqueMinimo) : "0",
      custo:
        insumo.valorAtual != null && Number.isFinite(insumo.valorAtual)
          ? numberToMoneyMask(insumo.valorAtual)
          : "",
    };
    setEditingInsumoId(insumo.id);
    setInsumoFormBaseline(baseline);
    const draft = loadFormDraft<{
      nome?: string;
      categoria?: string;
      categoriaOutra?: string;
      unidade?: string;
      estoqueMinimo?: string;
      custo?: string;
    }>(buildFormDraftKey("insumo", insumo.id));
    setInsumoNome(draft?.nome ?? baseline.nome);
    const cat = (draft?.categoria ?? baseline.categoria).trim();
    if (cat && categoriasList.some((c) => c.nome === cat)) {
      setInsumoCategoria(cat);
      setInsumoCategoriaOutra(draft?.categoriaOutra ?? "");
    } else if (cat) {
      setInsumoCategoria(OPCAO_CATEGORIA_OUTRA);
      setInsumoCategoriaOutra(draft?.categoriaOutra ?? cat);
    } else {
      setInsumoCategoria("");
      setInsumoCategoriaOutra(draft?.categoriaOutra ?? "");
    }
    setInsumoUnidade(draft?.unidade ?? baseline.unidade);
    setInsumoEstoqueMinimo(draft?.estoqueMinimo ?? baseline.estoqueMinimo);
    setInsumoCusto(draft?.custo ?? baseline.custo);
    setShowInsumoModal(true);
  };

  const fecharModalInsumo = () => {
    clearFormDraft(insumoDraftKey);
    setShowInsumoModal(false);
    setEditingInsumoId(null);
    setInsumoFormBaseline(null);
    setInsumoNome("");
    setInsumoCategoria("");
    setInsumoCategoriaOutra("");
    setInsumoUnidade("");
    setInsumoEstoqueMinimo("");
    setInsumoCusto("");
  };

  const handleSalvarInsumo = async (e: React.FormEvent) => {
    e.preventDefault();
    const nome = insumoNome.trim();
    const categoriaFinal =
      insumoCategoria === OPCAO_CATEGORIA_OUTRA ? insumoCategoriaOutra.trim() : insumoCategoria.trim();
    const unidade = insumoUnidade.trim();
    const estoqueMinimo = parseFloat(insumoEstoqueMinimo) || 0;
    const custoNum = parseNumeroBR(insumoCusto);
    if (!nome || !categoriaFinal || !unidade) {
      alert("Preencha nome, categoria e unidade.");
      return;
    }
    if (insumoCategoria === OPCAO_CATEGORIA_OUTRA && !isAdmin) {
      toast.error("Apenas administradores podem cadastrar uma categoria nova. Escolha uma categoria existente ou peça a um admin.");
      return;
    }
    if (insumoCusto.trim() !== "" && custoNum !== null && custoNum < 0) {
      toast.error("Custo inválido.");
      return;
    }
    const valorAtual = insumoCusto.trim() === "" ? null : custoNum;
    try {
      setSavingInsumo(true);
      if (editingInsumoId) {
        await api.atualizarInsumo(editingInsumoId, { nome, categoria: categoriaFinal, unidade, estoqueMinimo, valor_atual: valorAtual });
        await carregarInsumosTab();
        await carregarDados();
      } else {
        await api.criarInsumo({ nome, categoria: categoriaFinal, unidade, estoqueMinimo, valor_atual: valorAtual });
        await carregarInsumosTab();
        await carregarDados();
      }
      clearFormDraft(insumoDraftKey);
      fecharModalInsumo();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar insumo");
    } finally {
      setSavingInsumo(false);
    }
  };

  const handleExcluirInsumo = async (id: number) => {
    if (!confirm("Deseja realmente excluir este insumo?")) return;
    try {
      setDeletingInsumoId(id);
      await api.excluirInsumo(id);
      clearFormDraft(buildFormDraftKey("insumo", id));
      await carregarInsumosTab();
      await carregarDados();
    } catch (e) {
      console.error(e);
      alert("Erro ao excluir insumo");
    } finally {
      setDeletingInsumoId(null);
    }
  };

  const abrirModalNovoProduto = () => {
    setLocation("/itens/produtos/novo");
  };

  const fecharModalProduto = () => {
    setShowProdutoModal(false);
    setEditingProdutoId(null);
    setProdutoNome("");
    setProdutoPossuiComposicao(true);
    setProdutoPrecoVenda("");
    setProdutoQuantidadeMinima("");
    setProdutoCategoriaSelect("");
    setProdutoCategoriaOutra("");
    setProdutoComposicaoItems([]);
    setProdutoInsumoToAdd("");
  };

  const getMotivoLabel = (m: string) => {
    switch (m) {
      case 'consumo_interno': return 'Consumo interno';
      case 'vencimento': return 'Vencimento';
      case 'brinde_propaganda': return 'Brinde / propaganda';
      case 'cortesia_avaliacao': return 'Cortesia / avaliação';
      case 'avaria': return 'Avaria';
      default: return m;
    }
  };

  const carregarUsuarios = async () => {
    try {
      setLoadingUsuarios(true);
      const lista = await api.listarUsuarios();
      setUsuarios(lista);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar usuários");
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const openCreateUserModal = () => {
    const draft = loadFormDraft<{
      username?: string;
      name?: string;
      email?: string;
      telefone?: string;
      role?: typeof newRole;
      allowed_tabs?: string[];
    }>(newUserDraftKey);
    setNewUsername(draft?.username ?? "");
    setNewPassword("");
    setNewName(draft?.name ?? "");
    setNewEmail(draft?.email ?? "");
    setNewTelefone(draft?.telefone ?? "");
    setNewRole(draft?.role ?? "funcionario");
    setNewUserTabs(Array.isArray(draft?.allowed_tabs) ? draft.allowed_tabs : []);
    setShowCreateUserForm(true);
  };

  const openEditUserModal = (u: UsuarioRow) => {
    const draft = loadFormDraft<{
      name?: string;
      email?: string;
      telefone?: string;
      role?: typeof newRole;
      allowed_tabs?: string[];
    }>(buildFormDraftKey("usuario", u.id));
    setEditUserModal(u);
    setEditUserName(draft?.name ?? u.name ?? "");
    setEditUserEmail(draft?.email ?? u.email ?? "");
    setEditUserTelefone(draft?.telefone ?? u.telefone ?? "");
    setEditUserRole(draft?.role ?? (u.role as typeof newRole));
    setEditUserPassword("");
    const tabs = (u.allowed_tabs || []).filter((t) => t !== "config");
    setEditUserTabs(Array.isArray(draft?.allowed_tabs) ? draft.allowed_tabs : u.role === "admin" ? [] : [...tabs]);
  };

  const resetNewUserFormFields = () => {
    setNewUsername("");
    setNewPassword("");
    setNewName("");
    setNewEmail("");
    setNewTelefone("");
    setNewRole("funcionario");
    setNewUserTabs([]);
  };

  const resetEditUserFormFields = () => {
    setEditUserName("");
    setEditUserEmail("");
    setEditUserTelefone("");
    setEditUserRole("funcionario");
    setEditUserPassword("");
    setEditUserTabs([]);
  };

  const closeCreateUserModal = () => {
    if (savingNewUser) return;
    clearFormDraft(newUserDraftKey);
    setShowCreateUserForm(false);
    resetNewUserFormFields();
  };

  const closeEditUserModal = () => {
    if (savingEditUser) return;
    if (editUserDraftKey) clearFormDraft(editUserDraftKey);
    setEditUserModal(null);
    resetEditUserFormFields();
  };

  const handleCriarUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newPassword) {
      alert("Preencha usuário e senha");
      return;
    }
    if (newPassword.length < 4) {
      alert("Senha deve ter no mínimo 4 caracteres");
      return;
    }
    if (newRole !== "admin" && newUserTabs.length === 0) {
      alert("Selecione ao menos uma permissão (aba) para usuários que não são admin");
      return;
    }

    try {
      setSavingNewUser(true);
      await api.criarUsuario({
        username: newUsername,
        password: newPassword,
        name: newName.trim() || null,
        email: newEmail.trim() || null,
        telefone: newTelefone.replace(/\D/g, "").length > 0 ? newTelefone.trim() : null,
        role: newRole,
        allowed_tabs: newRole === "admin" ? null : newUserTabs,
      });
      clearFormDraft(newUserDraftKey);
      setShowCreateUserForm(false);
      resetNewUserFormFields();
      await carregarUsuarios();
      toast.success("Usuário criado com sucesso");
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message || "Erro ao criar usuário");
    } finally {
      setSavingNewUser(false);
    }
  };

  const handleSalvarEdicaoUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUserModal) return;
    if (editUserRole !== "admin" && editUserTabs.length === 0) {
      toast.error("Selecione ao menos uma permissão para usuários que não são admin");
      return;
    }
    if (editUserPassword !== "" && editUserPassword.length < 4) {
      toast.error("Senha deve ter no mínimo 4 caracteres");
      return;
    }
    try {
      setSavingEditUser(true);
      await api.atualizarUsuario({
        user_id: editUserModal.id,
        name: editUserName.trim() || null,
        email: editUserEmail.trim() || null,
        telefone: editUserTelefone.replace(/\D/g, "").length > 0 ? editUserTelefone.trim() : null,
        role: editUserRole,
        password: editUserPassword.trim() || undefined,
        allowed_tabs: editUserRole === "admin" ? null : editUserTabs,
      });
      if (user?.id === editUserModal.id) {
        const me = await api.me();
        if (me) setUser(me);
      }
      if (editUserDraftKey) clearFormDraft(editUserDraftKey);
      setEditUserModal(null);
      resetEditUserFormFields();
      await carregarUsuarios();
      toast.success("Usuário atualizado");
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message || "Erro ao atualizar usuário");
    } finally {
      setSavingEditUser(false);
    }
  };

  const handleExcluirUsuario = async (u: UsuarioRow) => {
    if (user?.id === u.id) {
      toast.error("Não é possível excluir o próprio usuário");
      return;
    }
    if (!window.confirm(`Excluir o usuário "${u.username}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.excluirUsuario(u.id);
      clearFormDraft(buildFormDraftKey("usuario", u.id));
      if (editUserModal?.id === u.id) setEditUserModal(null);
      await carregarUsuarios();
      toast.success("Usuário excluído");
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message || "Erro ao excluir usuário");
    }
  };

  /** Categorias com ao menos 1 item na subaba Estoque (insumos + produtos sem composição). */
  const estoqueCategoriasOpcoes = useMemo(() => {
    const names = new Set<string>();
    for (const i of insumos) {
      const cat = (i.categoria || "").trim();
      if (cat) names.add(cat);
    }
    for (const p of produtosSemComposicao) {
      const cat = (p.categoria || "").trim();
      if (cat) names.add(cat);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [insumos, produtosSemComposicao]);

  /** Categorias com ao menos 1 item na subaba Entradas. */
  const entradasCategoriasOpcoes = useMemo(() => {
    const names = new Set<string>();
    for (const i of entradasInsumos) {
      const cat = (i.categoria || "").trim();
      if (cat) names.add(cat);
    }
    for (const p of entradasProdutos) {
      const cat = (p.categoria || "").trim();
      if (cat) names.add(cat);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [entradasInsumos, entradasProdutos]);

  const categoriasFiltroEstoqueAtivo = useMemo(() => {
    // entradasCategoriasOpcoes permanece para a UI antiga de Entradas (oculta).
    void entradasCategoriasOpcoes;
    return estoqueCategoriasOpcoes;
  }, [estoqueCategoriasOpcoes, entradasCategoriasOpcoes]);

  // Filtrar insumos
  const insumosFilterados = useMemo(() => {
    return insumos.filter((insumo) => {
      const matchSearch = textIncludesSearch(insumo.nome, searchTerm);
      const matchCategory = matchCategoriasFiltro(insumo.categoria, selectedCategories);
      return matchSearch && matchCategory;
    });
  }, [insumos, searchTerm, selectedCategories]);

  const entradasInsumosFilterados = useMemo(() => {
    return entradasInsumos.filter((insumo) => {
      const matchSearch = textIncludesSearch(insumo.nome, searchTerm);
      const matchCategory = matchCategoriasFiltro(insumo.categoria, selectedCategories);
      return matchSearch && matchCategory;
    });
  }, [entradasInsumos, searchTerm, selectedCategories]);

  const entradasProdutosFilterados = useMemo(() => {
    return entradasProdutos.filter((p) => {
      if (!textIncludesSearch(p.nome, searchTerm)) return false;
      return matchCategoriasFiltro(p.categoria, selectedCategories);
    });
  }, [entradasProdutos, searchTerm, selectedCategories]);

  const linhasEstoqueLista = useMemo(() => {
    type L = { tipo: "insumo"; item: Insumo } | { tipo: "produto"; item: Produto };
    const list: L[] = [];
    for (const i of insumosFilterados) list.push({ tipo: "insumo", item: i });
    for (const p of produtosSemComposicao) {
      if (!textIncludesSearch(p.nome, searchTerm)) continue;
      if (!matchCategoriasFiltro(p.categoria, selectedCategories)) continue;
      list.push({ tipo: "produto", item: p });
    }
    const collator = new Intl.Collator("pt-BR", { numeric: true });
    list.sort((a, b) => {
      if (estoqueSortOrder === "name-asc") return collator.compare(a.item.nome, b.item.nome);
      if (estoqueSortOrder === "name-desc") return collator.compare(b.item.nome, a.item.nome);

      const stockA = Number(a.item.quantidadeAtual ?? 0);
      const stockB = Number(b.item.quantidadeAtual ?? 0);
      const stockDiff = estoqueSortOrder === "stock-asc" ? stockA - stockB : stockB - stockA;
      return stockDiff || collator.compare(a.item.nome, b.item.nome);
    });
    return list;
  }, [insumosFilterados, produtosSemComposicao, searchTerm, selectedCategories, estoqueSortOrder]);

  const estoqueTemPendencias = useMemo(() => {
    const toNumber = (value: number | string | null | undefined) => Number(String(value ?? 0).replace(",", "."));
    for (const insumo of insumos) {
      const draft = quantities[insumo.id] ?? insumo.quantidadeAtual;
      if (Math.abs(toNumber(draft) - toNumber(insumo.quantidadeAtual)) > 1e-9) return true;
    }
    for (const produto of produtosSemComposicao) {
      const draft = produtoQuantities[produto.id] ?? produto.quantidadeAtual;
      if (Math.abs(toNumber(draft) - toNumber(produto.quantidadeAtual)) > 1e-9) return true;
    }
    return false;
  }, [insumos, produtosSemComposicao, quantities, produtoQuantities]);

  useLayoutEffect(() => {
    estoqueHasPendingChangesRef.current = estoqueTemPendencias;
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    const route = location.startsWith("/") ? location : `/${location}`;
    estoqueCurrentUrlRef.current = `${base}${route}${window.location.search}${window.location.hash}` || "/";
  }, [estoqueTemPendencias, location]);

  useEffect(() => {
    const routePath = (path: string) => path.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
    const isStockRoute = (path: string) => path === "/estoque" || path === "/entradas";
    const handlePopState = () => {
      if (!estoqueHasPendingChangesRef.current || !isStockRoute(routePath(location))) return;

      const base = import.meta.env.BASE_URL.replace(/\/$/, "");
      const pathname = window.location.pathname;
      const relativePath = base && pathname === base
        ? "/"
        : base && pathname.startsWith(`${base}/`)
          ? pathname.slice(base.length)
          : pathname;
      if (isStockRoute(routePath(relativePath))) return;

      estoquePendingNavigationRef.current = {
        to: `${relativePath}${window.location.search}${window.location.hash}`,
        options: { replace: true },
        browserHistory: true,
      };
      window.history.pushState(window.history.state, "", estoqueCurrentUrlRef.current || `${base}/estoque`);
      setEstoqueConferenciaModalOpen(true);
    };

    window.addEventListener("popstate", handlePopState, true);
    return () => window.removeEventListener("popstate", handlePopState, true);
  }, [location]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!estoqueHasPendingChangesRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const linhasEntradasOrdenadas = useMemo(() => {
    type R = { tipo: "insumo"; insumo: Insumo } | { tipo: "produto"; produto: Produto };
    const rows: R[] = [];
    for (const insumo of entradasInsumosFilterados) rows.push({ tipo: "insumo", insumo });
    for (const produto of entradasProdutosFilterados) rows.push({ tipo: "produto", produto });
    rows.sort((a, b) => {
      const na = a.tipo === "insumo" ? a.insumo.nome : a.produto.nome;
      const nb = b.tipo === "insumo" ? b.insumo.nome : b.produto.nome;
      return na.localeCompare(nb, "pt-BR");
    });
    return rows;
  }, [entradasInsumosFilterados, entradasProdutosFilterados]);

  const handleSalvarEntradasPendentes = async () => {
    type ItemEntrada = {
      tipo: "produto" | "insumo";
      id: number;
      quantidadeLancada: number;
      unidadeLancamento: string;
      valorPago?: number | null;
    };
    const itens: ItemEntrada[] = [];
    const keysSalvos: string[] = [];
    let linhasComRascunhoInvalido = 0;

    for (const row of linhasEntradasOrdenadas) {
      const key = row.tipo === "insumo" ? `insumo-${row.insumo.id}` : `produto-${row.produto.id}`;
      const qRaw = (entradasQuantities[key] ?? "").trim();
      const vRaw = (entradasValores[key] ?? "").trim();
      if (qRaw === "" && vRaw === "") continue;
      if (qRaw === "" || vRaw === "") {
        linhasComRascunhoInvalido += 1;
        continue;
      }

      const qValStr = (entradasQuantities[key] || "").replace(",", ".");
      const qty = parseFloat(qValStr);
      if (Number.isNaN(qty) || qty <= 0) {
        linhasComRascunhoInvalido += 1;
        continue;
      }

      const vVal = parseMoneyMask(entradasValores[key] || "");
      if (vRaw.trim() === "" || Number.isNaN(vVal) || vVal < 0) {
        linhasComRascunhoInvalido += 1;
        continue;
      }

      if (row.tipo === "insumo") {
        const uCad = row.insumo.unidade?.trim() || "un";
        const id = row.insumo.id;
        itens.push({
          tipo: "insumo",
          id,
          quantidadeLancada: qty,
          unidadeLancamento: entradasUnidadeValorSelect(key, uCad, entradasUnidades),
          valorPago: vVal,
        });
      } else {
        const id = row.produto.id;
        itens.push({
          tipo: "produto",
          id,
          quantidadeLancada: qty,
          unidadeLancamento: entradasUnidades[key] || row.produto.unidade || "un",
          valorPago: vVal,
        });
      }
      keysSalvos.push(key);
    }

    if (itens.length === 0) {
      toast.error("Preencha Qtd entrada e Valor Pago (válidos) em pelo menos uma linha.");
      return;
    }

    try {
      setSavingEntradasPendentes(true);
      await api.registrarEntradas(itens);
      setEntradasQuantities((prev) => {
        const n = { ...prev };
        keysSalvos.forEach((k) => {
          delete n[k];
        });
        return n;
      });
      setEntradasValores((prev) => {
        const n = { ...prev };
        keysSalvos.forEach((k) => {
          delete n[k];
        });
        return n;
      });
      setEntradasUnidades((prev) => {
        const n = { ...prev };
        keysSalvos.forEach((k) => {
          delete n[k];
        });
        return n;
      });
      setEntradasUnidadeInteragida((prev) => {
        const n = { ...prev };
        keysSalvos.forEach((k) => {
          delete n[k];
        });
        return n;
      });
      const qAfter = { ...entradasQuantities };
      const vAfter = { ...entradasValores };
      keysSalvos.forEach((k) => {
        delete qAfter[k];
        delete vAfter[k];
      });
      let ultimaComRascunho: string | null = null;
      for (const r of linhasEntradasOrdenadas) {
        const k = r.tipo === "insumo" ? `insumo-${r.insumo.id}` : `produto-${r.produto.id}`;
        if ((qAfter[k] ?? "").trim() !== "" || (vAfter[k] ?? "").trim() !== "") ultimaComRascunho = k;
      }
      setEntradasUltimaLinhaInteracao(ultimaComRascunho);
      await carregarEntradas();
      await carregarDados();
      if (canViewTab("insumos")) {
        await carregarInsumosTab();
      }
      toast.success(itens.length === 1 ? "Entrada registrada." : `${itens.length} entradas registradas.`);
      if (linhasComRascunhoInvalido > 0) {
        toast.message(
          "Linhas incompletas não foram salvas (Qtd e Valor Pago válidos).",
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar entradas");
    } finally {
      setSavingEntradasPendentes(false);
    }
  };

  const estoqueOpcoesCategoriaSelect = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: "", label: "Todos" }];
    categoriasFiltroEstoqueAtivo.forEach((c) => opts.push({ value: c, label: c }));
    return opts;
  }, [categoriasFiltroEstoqueAtivo]);

  useEffect(() => {
    setSelectedCategories((prev) => pruneCategoriasFiltro(prev, categoriasFiltroEstoqueAtivo));
  }, [categoriasFiltroEstoqueAtivo]);

  /** Categorias presentes nos itens em baixa (Reposição). */
  const reposicaoCategoriasOpcoes = useMemo(() => {
    const s = new Set<string>();
    for (const i of insumosEstoqueBaixo ?? []) {
      const c = (i.categoria || "").trim();
      if (c) s.add(c);
    }
    for (const p of produtosBaixoEstoque ?? []) {
      const c = (p.categoria || "").trim();
      if (c) s.add(c);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [insumosEstoqueBaixo, produtosBaixoEstoque]);

  const linhasReposicao = useMemo(() => {
    type R = { key: string; tipo: "insumo"; item: Insumo } | { key: string; tipo: "produto"; item: Produto };
    const out: R[] = [];
    const matchCat = (cat: string | undefined) => matchCategoriasFiltro(cat, reposicaoCategoriasFiltro);
    for (const i of insumosEstoqueBaixo ?? []) {
      if (!matchCat(i.categoria)) continue;
      out.push({ key: `ins-${i.id}`, tipo: "insumo", item: i });
    }
    for (const p of produtosBaixoEstoque ?? []) {
      if (!matchCat(p.categoria)) continue;
      out.push({ key: `prod-${p.id}`, tipo: "produto", item: p });
    }
    out.sort((a, b) => a.item.nome.localeCompare(b.item.nome, "pt-BR"));
    return out;
  }, [insumosEstoqueBaixo, produtosBaixoEstoque, reposicaoCategoriasFiltro]);

  const reposicaoOpcoesCategoriaSelect = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: "", label: "Todos" }];
    reposicaoCategoriasOpcoes.forEach((c) => opts.push({ value: c, label: c }));
    return opts;
  }, [reposicaoCategoriasOpcoes]);

  useEffect(() => {
    setReposicaoCategoriasFiltro((prev) => pruneCategoriasFiltro(prev, reposicaoCategoriasOpcoes));
  }, [reposicaoCategoriasOpcoes]);

  useEffect(() => {
    setReposicaoSelecionados([]);
  }, [reposicaoCategoriasFiltro]);

  const reposicaoVisibleKeys = useMemo(
    () => linhasReposicao.map((linha) => linha.key),
    [linhasReposicao],
  );
  const reposicaoSelectionState = useMemo(
    () => getVisibleSelectionState(reposicaoVisibleKeys, reposicaoSelecionados),
    [reposicaoVisibleKeys, reposicaoSelecionados],
  );

  useEffect(() => {
    if (reposicaoHeaderCheckboxRef.current) {
      reposicaoHeaderCheckboxRef.current.indeterminate = reposicaoSelectionState.indeterminate;
    }
  }, [reposicaoSelectionState.indeterminate]);

  useEffect(() => {
    if (activeTab !== "compras") {
      setReposicaoSelecionados([]);
      setReposicaoCategoriasFiltro([]);
    }
  }, [activeTab]);

  const copiarReposicaoSelecionados = useCallback(async () => {
    const selected = linhasReposicao.filter((l) => reposicaoSelecionados.includes(l.key));
    if (selected.length === 0) {
      toast.error("Selecione ao menos um item");
      return;
    }
    const lines = selected.map((l) => {
      if (l.tipo === "insumo") {
        const i = l.item;
        const atual = Number(i.quantidadeAtual ?? 0);
        const u = (i.unidade || "").trim();
        return `${formatDisplayText(i.nome)} - Atual: ${formatReposicaoDecimal(atual)} ${u}`.trim();
      }
      const p = l.item;
      const atual = Number(p.quantidadeAtual ?? 0);
      const u = (p.unidade || "un").trim();
      return `${formatDisplayText(p.nome)} - Atual: ${formatReposicaoDecimal(atual)} ${u}`.trim();
    });
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Lista copiada para a área de transferência");
    } catch {
      toast.error("Não foi possível copiar. Verifique as permissões do navegador.");
    }
  }, [linhasReposicao, reposicaoSelecionados]);

  const handleQuantidadeChange = (id: number, value: string) => {
    if (value !== "" && !/\d/.test(value)) return;
    const num = parseFloat(value.replace(",", ".")) || 0;
    setQuantities((prev) => ({
      ...prev,
      [id]: num,
    }));
  };

  const handleQuantidadeProdutoChange = (produtoId: number, value: string) => {
    const normalizedValue = value.replace(",", ".");
    const num = normalizedValue === "" ? undefined : parseFloat(normalizedValue);
    setProdutoQuantities((prev) => {
      const next = { ...prev };
      if (num === undefined || isNaN(num)) delete next[produtoId];
      else next[produtoId] = num;
      return next;
    });
  };

  const formatEstoqueAtual = (value: number | string | null | undefined) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";

    const normalized = raw.replace(",", ".");
    const match = /^([+-]?\d+)(?:\.(\d+))?$/.exec(normalized);
    if (!match) return raw.replace(".", ",");

    const [, integer, fraction] = match;
    if (!fraction || /^0+$/.test(fraction)) return integer;
    return `${integer},${fraction.length === 1 ? fraction.padEnd(2, "0") : fraction}`;
  };

  const setEstoqueQuantityDraft = (key: string, value: string | null) => {
    setEstoqueQuantityDrafts((previous) => {
      const next = { ...previous };
      if (value === null) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const closeEstoqueConferenciaModal = () => {
    setEstoqueConferenciaModalOpen(false);
    estoquePendingNavigationRef.current = null;
  };

  const abrirConferenciaEstoquePendentes = () => {
    if (!estoqueTemPendencias) return;
    setEstoqueConferenciaModalOpen(true);
  };

  const handleConfirmarConferenciaEstoqueModal = async () => {
    if (savingConferencia) return;
    const alteracoes: ConferenciaAlteracao[] = [];
    const toNumber = (value: number | string | null | undefined) => Number(String(value ?? 0).replace(",", "."));
    for (const insumo of insumos) {
      const antes = toNumber(insumo.quantidadeAtual);
      const dep = toNumber(quantities[insumo.id] ?? insumo.quantidadeAtual);
      if (Math.abs(antes - dep) <= 1e-9) continue;
      alteracoes.push({
        tipo: "insumo",
        item_id: insumo.id,
        item_nome: insumo.nome,
        unidade: (insumo.unidade ?? "un").trim() || "un",
        quantidade_antes: antes,
        quantidade_depois: dep,
      });
    }
    for (const produto of produtosSemComposicao) {
      const antes = toNumber(produto.quantidadeAtual);
      const dep = toNumber(produtoQuantities[produto.id] ?? produto.quantidadeAtual);
      if (Math.abs(antes - dep) <= 1e-9) continue;
      alteracoes.push({
        tipo: "produto",
        item_id: produto.id,
        item_nome: produto.nome,
        unidade: "un",
        quantidade_antes: antes,
        quantidade_depois: dep,
      });
    }
    if (alteracoes.length === 0) {
      setEstoqueConferenciaModalOpen(false);
      const pendingNavigation = estoquePendingNavigationRef.current;
      estoquePendingNavigationRef.current = null;
      if (pendingNavigation) setLocationRaw(pendingNavigation.to, pendingNavigation.options);
      return;
    }
    try {
      setSavingConferencia(true);
      await api.registrarConferenciaEstoque(alteracoes);
      const alteracoesInsumos = new Map(alteracoes.filter((alt) => alt.tipo === "insumo").map((alt) => [alt.item_id, alt.quantidade_depois]));
      const alteracoesProdutos = new Map(alteracoes.filter((alt) => alt.tipo === "produto").map((alt) => [alt.item_id, alt.quantidade_depois]));
      setInsumos((previous) => previous.map((item) => alteracoesInsumos.has(item.id) ? { ...item, quantidadeAtual: alteracoesInsumos.get(item.id)! } : item));
      setProdutosSemComposicao((previous) => previous.map((item) => alteracoesProdutos.has(item.id) ? { ...item, quantidadeAtual: alteracoesProdutos.get(item.id)! } : item));
      setQuantities((prev) => {
        const n = { ...prev };
        alteracoesInsumos.forEach((_quantity, id) => delete n[id]);
        return n;
      });
      setProdutoQuantities((prev) => {
        const n = { ...prev };
        alteracoesProdutos.forEach((_quantity, id) => delete n[id]);
        return n;
      });
      setEstoqueQuantityDrafts({});
      setEstoqueConferenciaModalOpen(false);
      estoqueHasPendingChangesRef.current = false;
      const pendingNavigation = estoquePendingNavigationRef.current;
      estoquePendingNavigationRef.current = null;
      toast.success(
        alteracoes.length === 1
          ? "Conferência registrada com sucesso."
          : `Conferência registrada (${alteracoes.length} itens).`,
      );
      void carregarDados();
      void carregarConferencias();
      if (pendingNavigation) setLocationRaw(pendingNavigation.to, pendingNavigation.options);
    } catch (error) {
      toast.error("Erro ao registrar conferência");
      console.error(error);
    } finally {
      setSavingConferencia(false);
    }
  };

  const handleSalvarProduto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!produtoNome.trim()) {
      alert("Nome do produto é obrigatório");
      return;
    }
    const precoVendaNum = produtoPrecoVenda.trim() === "" ? null : parseNumeroBR(produtoPrecoVenda);
    if (precoVendaNum !== null && (isNaN(precoVendaNum) || precoVendaNum < 0)) {
      alert("Preço de venda deve ser um número válido");
      return;
    }
    const qtdMinNum = produtoPossuiComposicao
      ? 0
      : parseQuantidadeInput(produtoQuantidadeMinima);
    if (qtdMinNum === null) {
      toast.error("Quantidade mínima é obrigatória e deve ser um número >= 0 quando o produto não possui composição.");
      return;
    }
    const categoriaFinal =
      produtoCategoriaSelect === OPCAO_CATEGORIA_OUTRA ? produtoCategoriaOutra.trim() : produtoCategoriaSelect.trim();
    if (!categoriaFinal) {
      toast.error("Selecione ou informe a categoria.");
      return;
    }
    if (produtoCategoriaSelect === OPCAO_CATEGORIA_OUTRA && !isAdmin) {
      toast.error("Apenas administradores podem cadastrar uma categoria nova. Escolha uma categoria existente ou peça a um admin.");
      return;
    }
    try {
      setSavingProduto(true);
      const composicao =
        produtoPossuiComposicao && produtoComposicaoItems.length > 0
          ? produtoComposicaoItems
              .filter((i) => i.quantidade !== "" && parseFloat(i.quantidade) > 0)
              .map((i) => {
                const mult = FRACAO_MULT[i.unidade?.trim() ?? "un"] ?? 1;
                const qty = (parseFloat(i.quantidade) || 0) * mult;
                const unidadeEnviar = mult !== 1 ? "un" : (i.unidade?.trim() || "un");
                return {
                  insumo_id: i.insumo_id,
                  quantidade: qty,
                  unidade: unidadeEnviar,
                };
              })
          : undefined;
      if (editingProdutoId) {
        await api.atualizarProduto(editingProdutoId, {
          nome: produtoNome.trim(),
          categoria: categoriaFinal,
          possui_composicao: produtoPossuiComposicao,
          preco_venda: precoVendaNum,
          quantidade_minima: qtdMinNum,
          composicao,
        });
      } else {
        await api.criarProduto({
          nome: produtoNome.trim(),
          categoria: categoriaFinal,
          possui_composicao: produtoPossuiComposicao,
          preco_venda: precoVendaNum,
          quantidade_minima: qtdMinNum,
          composicao,
        });
      }
      fecharModalProduto();
      await carregarProdutos();
      await carregarDados();
      toast.success(editingProdutoId ? "Produto atualizado." : "Produto criado.");
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : (editingProdutoId ? "Erro ao atualizar produto" : "Erro ao criar produto");
      toast.error(msg);
    } finally {
      setSavingProduto(false);
    }
  };

  const adicionarInsumoNaComposicao = () => {
    const id = produtoInsumoToAdd === "" ? 0 : Number(produtoInsumoToAdd);
    if (id <= 0) return;
    const insumo = insumos.find((i) => i.id === id);
    if (!insumo || produtoComposicaoItems.some((c) => c.insumo_id === id)) return;
    const base = (insumo.unidade ?? "un").trim() || "un";
    const unidadeInicial = unidadeCanonicaParaBase(base);
    setProdutoComposicaoItems((prev) => [...prev, { insumo_id: id, insumo_nome: insumo.nome, unidade: unidadeInicial, unidade_base: base, quantidade: "" }]);
    setProdutoInsumoToAdd("");
  };

  const removerInsumoDaComposicao = (insumoId: number) => {
    setProdutoComposicaoItems((prev) => prev.filter((c) => c.insumo_id !== insumoId));
  };

  const formatarData = (data: Date | string) => {
    if (typeof data === "string") {
      const onlyDate = data.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (onlyDate) {
        return `${onlyDate[3]}/${onlyDate[2]}/${onlyDate[1]}`;
      }
      const mysqlDateTime = data.match(/^(\d{4})-(\d{2})-(\d{2})[\sT]\d{2}:\d{2}:\d{2}/);
      if (mysqlDateTime) {
        return formatarDataLocal(parseMySQLDateLocal(data));
      }
    }
    const d = data instanceof Date ? data : new Date(data);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatarHora = (data: Date | string) => {
    const d = new Date(data);
    return d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const path =
        window.location.pathname === "/" || window.location.pathname === ""
          ? "/dashboard"
          : `${window.location.pathname}${window.location.search}`;
      savePostLoginRedirect(path);
    }
  }, [loading, isAuthenticated]);

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        error={loginError}
        loading={loginLoading}
        onSubmit={async (username, password) => {
          setLoginError(null);
          setLoginLoading(true);
          try {
            await loginWithCredentials(username, password);
          } catch (err: unknown) {
            setLoginError(err instanceof Error ? err.message : "Erro ao entrar");
          } finally {
            setLoginLoading(false);
          }
        }}
      />
    );
  }
  const navTabs = [
    { id: "dashboard" as const, label: "Dashboard", icon: HomeIcon },
    { id: "estoque" as const, label: "Estoque", icon: Package },
    { id: "compras" as const, label: "Reposição", icon: ShoppingCart },
    { id: "desperdicios" as const, label: "Desperdícios", icon: Trash },
    { id: "financeiro" as const, label: "Financeiro", icon: Receipt },
    { id: "fornecedores" as const, label: "Pagamentos", icon: Users },
    { id: "conferencia" as const, label: "Conferência", icon: ClipboardList },
    { id: "marketing" as const, label: "Marketing", icon: Megaphone },
    { id: "itens" as const, label: "Itens", icon: Box, subItems: [{ id: "produtos" as const, label: "Produtos", icon: Box }, { id: "insumos" as const, label: "Insumos", icon: Layers }] },
    { id: "cadastros" as const, label: "Cadastros", icon: Building2, subItems: [{ id: "parceiros" as const, label: "Empresas", icon: Briefcase }] },
  ].filter((t) => typeof canViewTab === 'function' && (t.id === 'itens' ? canViewTab('itens') : t.id === 'cadastros' ? canViewTab('cadastros') : t.id === 'conferencia' ? canViewTab('conferencia') : t.id === 'marketing' ? canViewTab('marketing') : t.id === 'estoque' ? canViewTab('estoque') : canViewTab(t.id)));

  const navItemClick = (id: string) => {
    goToTab(id as HomeActiveTab);
    setMobileSidebarOpen(false);
  };

  const openCaixa = () => {
    window.open("/caixa", "caixa");
  };

  const openCozinha = () => {
    window.open("/kds", "kds");
  };

  const openCardapio = () => {
    window.open("/cardapio", "cardapio");
  };

  const openGarcom = () => {
    window.open("/garcom", "garcom");
  };

  const semPermissaoNenhuma =
    Boolean(isAuthenticated && user && !isAdmin && (!allowedTabs || allowedTabs.length === 0));

  if (semPermissaoNenhuma) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] p-4">
        <Card className="max-w-md w-full border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Sem permissão de acesso</CardTitle>
            <CardDescription>
              Sua conta não está liberada para nenhuma área do sistema. Peça ao administrador para marcar as abas permitidas no seu usuário.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full bg-accent hover:bg-accent/90" onClick={() => void logout()}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#F4F6F8] overflow-x-hidden">
      {/* Sidebar - Desktop: menu vertical fixo à esquerda (sem botão Sair) */}
      <aside
        className={`erp-sidebar relative hidden min-[1201px]:flex min-[1201px]:flex-col min-[1201px]:fixed min-[1201px]:top-0 min-[1201px]:left-0 z-40 bg-[#F4F6F8] text-gray-900 transition-[width] duration-200 ease-in-out ${
          desktopSidebarCollapsed ? "min-[1201px]:w-[4.5rem]" : "min-[1201px]:w-56"
        }`}
        style={DESKTOP_SIDEBAR_DOTTED_BORDER_STYLE}
      >
        <button
          type="button"
          onClick={toggleDesktopSidebar}
          className="absolute -right-3 top-[4.25rem] z-[100] flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50"
          aria-label={desktopSidebarCollapsed ? "Expandir menu" : "Recolher menu"}
        >
          {desktopSidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden />
          )}
        </button>
        <div className="erp-sidebar-body">
        <div
          className={`erp-sidebar-header flex shrink-0 items-center ${
            desktopSidebarCollapsed ? "justify-center p-3" : "gap-2 p-4"
          }`}
        >
          <ClientLogo
            variant="menu"
            className={
              desktopSidebarCollapsed
                ? "h-9 w-9 object-contain"
                : "h-15 w-auto max-w-full object-contain"
            }
          />
        </div>
        <nav
          className={`${SIDEBAR_NAV_LIST_GAP} py-3 ${
            desktopSidebarCollapsed ? "px-1.5" : "px-2"
          }`}
        >
          {navTabs.map((tab) => {
            if ("subItems" in tab && tab.subItems) {
              const subItemsVisible = tab.subItems.filter((s) => canViewTab(s.id));
              if (subItemsVisible.length === 0) return null;
              const Icon = tab.icon;
              const isCadastrosGroup = tab.id === "cadastros";
              const submenuOpen = isCadastrosGroup ? submenuCadastrosAberto : submenuItensAberto;
              const toggleSubmenu = () => {
                if (isCadastrosGroup) setSubmenuCadastrosAberto(!submenuCadastrosAberto);
                else setSubmenuItensAberto(!submenuItensAberto);
              };
              const groupAtivo = isCadastrosGroup
                ? activeTab === "parceiros"
                : activeTab === "produtos" || activeTab === "insumos";
              return (
                <div key={tab.id} className="erp-sidebar-group min-w-0">
                  <SidebarNavButton
                    title={desktopSidebarCollapsed ? tab.label : undefined}
                    onClick={toggleSubmenu}
                    className={`erp-sidebar-item w-full max-w-full shrink-0 text-left text-sm font-medium transition-colors duration-200 rounded-lg ${
                      desktopSidebarCollapsed
                        ? "flex flex-col items-center justify-center gap-0.5 px-2 py-2.5"
                        : "flex items-center gap-3 px-4 py-3"
                    } ${groupAtivo ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE}`}
                  >
                    <Icon
                      className={`h-5 w-5 shrink-0 ${groupAtivo ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`}
                    />
                    {!desktopSidebarCollapsed ? (
                      <>
                        <span className="min-w-0 flex-1">{tab.label}</span>
                        <span className="shrink-0">
                          {submenuOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </>
                    ) : submenuOpen ? (
                      <ChevronDown className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
                    ) : null}
                  </SidebarNavButton>
                  {submenuOpen && (
                    <div
                      className={
                        desktopSidebarCollapsed
                          ? `erp-sidebar-group ${SIDEBAR_NAV_SUBMENU_GAP} items-center`
                          : `erp-sidebar-group ${SIDEBAR_NAV_SUBMENU_GAP} border-l-2 border-client-primary-30 ml-4 pl-1`
                      }
                    >
                      {subItemsVisible.map((sub) => {
                        const SubIcon = sub.icon;
                        return (
                          <SidebarNavButton
                            key={sub.id}
                            title={desktopSidebarCollapsed ? sub.label : undefined}
                            onClick={() => goToTab(sub.id as HomeActiveTab)}
                            className={`erp-sidebar-item max-w-full shrink-0 transition-colors duration-200 rounded-lg ${
                              desktopSidebarCollapsed
                                ? "flex w-full items-center justify-center px-2 py-2"
                                : "flex w-full items-center gap-3 py-2.5 pl-3 pr-4 text-left text-sm font-medium"
                            } ${
                              activeTab === sub.id ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE
                            }`}
                          >
                            <SubIcon
                              className={`shrink-0 ${
                                desktopSidebarCollapsed ? "h-4 w-4" : "h-4 w-4"
                              } ${activeTab === sub.id ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`}
                            />
                            {!desktopSidebarCollapsed && <span>{sub.label}</span>}
                          </SidebarNavButton>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const { id, label, icon: Icon, badge } = tab as {
              id: string;
              label: string;
              icon: typeof Package;
              badge?: number;
            };
            const isActive = activeTab === id;
            return (
              <SidebarNavButton
                key={id}
                title={desktopSidebarCollapsed ? label : undefined}
                onClick={() => goToTab(id as HomeActiveTab)}
                className={`erp-sidebar-item w-full shrink-0 flex items-center text-left text-sm font-medium transition-colors duration-200 rounded-lg ${
                  desktopSidebarCollapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-3"
                } ${isActive ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE}`}
              >
                <span className="relative shrink-0">
                  <Icon className={`h-5 w-5 ${isActive ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`} />
                  {desktopSidebarCollapsed && badge != null && badge > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </span>
                {!desktopSidebarCollapsed && (
                  <>
                    <span className="min-w-0 flex-1">{label}</span>
                    {badge != null && badge > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                        {badge}
                      </span>
                    )}
                  </>
                )}
              </SidebarNavButton>
            );
          })}
        </nav>
        </div>
      </aside>

      {/* Área principal: top bar + conteúdo (com margem à esquerda no desktop por causa da sidebar) */}
      <div
        className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ease-in-out ${
          desktopSidebarCollapsed ? "min-[1201px]:ml-[4.5rem]" : "min-[1201px]:ml-56"
        }`}
      >
      {/* Top bar: só avatar no desktop; hamburger + logo + avatar no mobile */}
      <header className={`fixed inset-x-0 top-0 z-30 h-[60px] bg-[#F4F6F8] text-gray-900 transition-[height] duration-200 ease-out min-[768px]:h-[90px] ${desktopSidebarCollapsed ? "min-[1201px]:left-[4.5rem]" : "min-[1201px]:left-56"} ${headerScrolled ? "min-[768px]:!h-[60px]" : ""}`}>
        <div className="flex h-full items-center justify-between gap-2 px-3 sm:px-4 min-[1201px]:justify-end">
          {/* Mobile: hamburger */}
          <button
            type="button"
            className="min-[1201px]:hidden flex h-10 w-10 items-center justify-center rounded-lg p-2 hover:bg-accent-foreground/10 max-[767px]:!p-0"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-6 w-6 max-[767px]:h-8 max-[767px]:w-8" />
          </button>
          {/* Logo: só no mobile (centro) */}
          <ClientLogo
            variant="menu"
            className="h-10 w-auto object-contain shrink-0 min-[1201px]:hidden flex-1"
          />
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 md:mr-2">
            {isAuthenticated && (
              <button
                type="button"
                className="monthly-goal-widget flex min-w-0 max-w-[100px] flex-col items-end gap-0.5 rounded-md px-1 py-0.5 text-left transition hover:bg-black/5 focus:outline-none focus-visible:ring-2 ring-client-primary-40 focus-visible:ring-2 sm:max-w-[152px] sm:px-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  setMetaMesModalOpen(true);
                }}
                aria-label="Meta de faturamento mensal"
              >
                <span className="block w-full truncate text-right text-[10px] font-bold leading-tight text-black sm:text-[11px]">
                  {barraMetaMes.textoLinha}
                </span>
                <div
                  className="h-1.5 w-full min-w-[72px] overflow-hidden rounded-full sm:min-w-[120px]"
                  style={{ backgroundColor: CLIENT_META_TRACK }}
                >
                  <div
                    className="h-full max-w-full rounded-full transition-[width] duration-300"
                    style={{
                      width: `${barraMetaMes.progressoPct}%`,
                      backgroundColor: CLIENT_META_ORANGE,
                    }}
                  />
                </div>
              </button>
            )}
            {/* Avatar: desktop e mobile */}
            <div className="relative shrink-0" ref={avatarMenuRef}>
            <button
              type="button"
              onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-0 text-black shadow-md transition hover:shadow-lg focus:outline-none focus-visible:outline-none"
              style={{ backgroundColor: clientConfig.primary_color }}
              aria-label="Menu do usuário"
            >
              <span className="text-base font-semibold leading-none select-none" aria-hidden>
                {(user?.name && user.name.trim())
                  ? user.name.trim().charAt(0).toUpperCase()
                  : (user?.username && user.username.trim())
                    ? user.username.trim().charAt(0).toUpperCase()
                    : "U"}
              </span>
            </button>
            {avatarMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 min-w-[220px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 text-gray-900 shadow-lg">
                {(user?.name || user?.username) && (
                  <div className="border-b border-gray-100 px-4 py-3">
                    {user?.name && <p className="truncate text-sm font-medium text-gray-900">{user.name}</p>}
                    {user?.username && <p className="truncate text-xs text-gray-500">{user.username}</p>}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    openCaixa();
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Receipt className="h-4 w-4 shrink-0 text-accent" />
                  Caixa
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    openCozinha();
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <UtensilsCrossed className="h-4 w-4 shrink-0 text-accent" />
                  Cozinha
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    openCardapio();
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <BookOpen className="h-4 w-4 shrink-0 text-accent" />
                  Cardápio
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    openGarcom();
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <ConciergeBell className="h-4 w-4 shrink-0 text-accent" />
                  Garçom
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    goToTab("config", { configSubTab: "geral" });
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="h-4 w-4 shrink-0 text-accent" />
                  Configurações
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    void logout();
                  }}
                  className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  Sair
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </header>
      <div
        aria-hidden="true"
        className={`h-[60px] transition-[height] duration-200 ease-out min-[768px]:h-[90px] ${headerScrolled ? "min-[768px]:!h-[60px]" : ""}`}
      />

      {metaMesModalOpen && (
        <div
          className="gs-modal-overlay fixed inset-0 z-[70] flex items-center justify-center bg-[#F4F6F8]/95 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="meta-mes-title"
          onClick={() => setMetaMesModalOpen(false)}
        >
          <Card
            className={`gs-modal-panel-max flex w-full max-w-[calc(100vw-0.5rem)] flex-col overflow-hidden sm:max-w-lg ${MODAL_PANEL_CLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="!space-y-0 border-0 bg-transparent !p-0">
              <div className={MODAL_HEADER_ROW_CLASS}>
                <CardTitle id="meta-mes-title" className={`flex min-w-0 flex-1 items-center gap-2 ${MODAL_TITLE_CLASS}`}>
                  Meta de Faturamento Mensal
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => setMetaMesModalOpen(false)}
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className={`${MODAL_BODY_CLASS} min-h-0 flex-1 overflow-y-auto !pt-4`}>
              <div className="overflow-x-auto -mx-1 px-1">
                <table className="gs-management-table gs-management-table-specialized w-full min-w-[280px] text-sm">
                  <thead className={TABLE_HEAD_CLASS}>
                    <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <th className="pb-2 pr-3">Data</th>
                      <th className="pb-2 pr-3">Faturamento</th>
                      <th className="pb-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasMetaMesModal.map((row) => (
                      <tr key={row.meta} className="border-b border-gray-100 last:border-0">
                        <td className="py-2.5 pr-3 text-foreground">{row.dataConquista ?? "---"}</td>
                        <td className="py-2.5 pr-3 font-medium text-foreground">{row.metaLabel}</td>
                        <td className="py-2.5">
                          <span
                            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              row.conquistado ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {row.conquistado ? "Conquistado" : "Não conquistado"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sidebar mobile: overlay + drawer */}
      {mobileSidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 min-[1201px]:hidden"
            aria-hidden
            onClick={() => setMobileSidebarOpen(false)}
          />
          <aside className="erp-sidebar erp-mobile-sidebar fixed top-0 left-0 z-50 flex w-64 flex-col overflow-hidden bg-[#F4F6F8] text-gray-900 shadow-xl min-[1201px]:hidden">
            <div className="erp-sidebar-header flex shrink-0 items-center justify-between border-b border-gray-200 p-4">
              <ClientLogo variant="menu" className="erp-mobile-sidebar-logo h-8 w-auto object-contain" />
              <button type="button" onClick={() => setMobileSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-200 text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="erp-sidebar-body">
            <nav className={`${SIDEBAR_NAV_LIST_GAP} py-3 px-2`}>
              {navTabs.map((tab) => {
                if ('subItems' in tab && tab.subItems) {
                  const subItemsVisible = tab.subItems.filter((s) => canViewTab(s.id));
                  if (subItemsVisible.length === 0) return null;
                  const Icon = tab.icon;
                  const isCadastrosGroup = tab.id === 'cadastros';
                  const submenuOpen = isCadastrosGroup ? submenuCadastrosAberto : submenuItensAberto;
                  const toggleSubmenu = () => {
                    if (isCadastrosGroup) setSubmenuCadastrosAberto(!submenuCadastrosAberto);
                    else setSubmenuItensAberto(!submenuItensAberto);
                  };
                  const groupAtivo = isCadastrosGroup
                    ? activeTab === 'parceiros'
                    : activeTab === 'produtos' || activeTab === 'insumos';
                  return (
                    <div key={tab.id} className="erp-sidebar-group">
                      <SidebarNavButton
                        onClick={toggleSubmenu}
                        className={`erp-sidebar-item w-full shrink-0 flex items-center gap-3 px-4 py-3 text-left rounded-lg text-sm font-medium transition-colors ${
                          groupAtivo ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 shrink-0 ${groupAtivo ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`}
                        />
                        {tab.label}
                        <span className="ml-auto">{submenuOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
                      </SidebarNavButton>
                      {submenuOpen && (
                        <div className={`erp-sidebar-group ${SIDEBAR_NAV_SUBMENU_GAP} border-l-2 border-client-primary-30 ml-4 pl-1`}>
                          {subItemsVisible.map((sub) => {
                            const SubIcon = sub.icon;
                            return (
                              <SidebarNavButton
                                key={sub.id}
                                onClick={() => navItemClick(sub.id)}
                                className={`erp-sidebar-item w-full shrink-0 flex items-center gap-3 py-2.5 pl-3 pr-4 text-left rounded-lg text-sm font-medium transition-colors ${
                                  activeTab === sub.id ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE
                                }`}
                              >
                                <SubIcon
                                  className={`w-4 h-4 shrink-0 ${activeTab === sub.id ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`}
                                />
                                {sub.label}
                              </SidebarNavButton>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const { id, label, icon: Icon, badge } = tab as { id: string; label: string; icon: typeof Package; badge?: number };
                const isActive = activeTab === id;
                return (
                  <SidebarNavButton
                    key={id}
                    onClick={() => navItemClick(id)}
                    className={`erp-sidebar-item w-full shrink-0 flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                      isActive ? SIDEBAR_NAV_ITEM_ACTIVE : SIDEBAR_NAV_ITEM_INACTIVE
                    }`}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${isActive ? "opacity-100" : SIDEBAR_NAV_ICON_INACTIVE}`} />
                    <span>{label}</span>
                    {badge != null && badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-5 h-5 flex items-center justify-center px-1">
                        {badge}
                      </span>
                    )}
                  </SidebarNavButton>
                );
              })}
            </nav>
            </div>
          </aside>
        </>
      )}

      <main className="flex-1 min-h-screen">
        <div className="mx-auto w-full max-w-[1200px] px-3 sm:px-4 py-4 sm:py-6 bg-[#F4F6F8]">
        {produtoFormRoute && canViewTab("produtos") ? (
          <ProdutoFormPage
            produtoId={produtoFormRoute.mode === "editar" ? produtoFormRoute.id : null}
            isAdmin={isAdmin}
            onCancel={() => setLocation("/itens/produtos")}
            onSaved={async () => {
              await carregarProdutos();
              await carregarDados();
              setLocation("/itens/produtos");
            }}
          />
        ) : (
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            const t = v as HomeActiveTab;
            goToTab(t);
          }}
          className="w-full"
        >
          {/* Aba Dashboard */}
          {canViewTab('dashboard') && (
            <TabsContent value="dashboard" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {dashboardLoading ? (
                <PageContentLoader />
              ) : dashboardErro ? (
                <div role="alert" className="space-y-3">
                  <p>Erro ao carregar dashboard. Os dados deste periodo nao estao disponiveis.</p>
                  <Button onClick={() => void carregarDashboard()}>Tentar novamente</Button>
                </div>
              ) : (
              <>
              <div className="flex max-w-full min-w-0 flex-nowrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <h1 className={PAGE_TITLE_CLASS}>
                    Olá, <span className="text-client-primary">{user?.name || "Usuário"}</span>
                  </h1>

                  <span className="text-[12px] text-muted-foreground">
                    Bem-vindo de volta ao seu painel
                  </span>
                </div>
                <div className="flex min-w-0 shrink-0 items-center gap-2">
                  <button
                    type="button"
                    className={KPI_VALUE_EYE_BTN}
                    onClick={() => setOcultarValoresDashboard((v) => !v)}
                    title={ocultarValoresDashboard ? "Mostrar valores" : "Ocultar valores"}
                    aria-label={ocultarValoresDashboard ? "Mostrar valores" : "Ocultar valores"}
                  >
                    {ocultarValoresDashboard ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <PeriodFilterSelect
                    id="dashboard-periodo"
                    value={dashboardPeriodo}
                    onChange={(e) => {
                      const v = e.target.value as DashboardPeriodo;
                      dashboardRequest.current?.abort();
                      if (v !== dashboardPeriodo && v !== "personalizado") setDashboardLoading(true);
                      setDashboardPeriodo(v);
                      if (v === 'personalizado') setShowModalPeriodoPersonalizadoDashboard(true);
                    }}
                    className="h-10 w-[min(180px,calc(100vw-10rem))] min-w-[120px] max-w-[180px] sm:w-[180px]"
                  >
                    <option value="hoje">Hoje</option>
                    <option value="ontem">Ontem</option>
                    <option value="7">Últimos 7 dias</option>
                    <option value="30">Últimos 30 dias</option>
                    <option value="personalizado">Personalizado</option>
                  </PeriodFilterSelect>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Card className={DASHBOARD_KPI_VALUE_CARD}>
                  <CardHeader className={DASHBOARD_KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>Faturamento</CardTitle>
                  </CardHeader>
                  <CardContent className={DASHBOARD_KPI_VALUE_CARD_CONTENT}>
                    <div className="min-w-0">
                      <div className={KPI_VALUE_TEXT}>
                        {formatDashboardValorMonetario(dashboardTotalVendas, ocultarValoresDashboard)}
                      </div>
                      <DashboardKpiComparacaoLine
                        comparacao={dashboardComparativoPeriodo.faturamento}
                        oculto={ocultarValoresDashboard}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className={DASHBOARD_KPI_VALUE_CARD}>
                  <CardHeader className={DASHBOARD_KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>Quantidade de Vendas</CardTitle>
                  </CardHeader>
                  <CardContent className={DASHBOARD_KPI_VALUE_CARD_CONTENT}>
                    <div className="min-w-0">
                      <div className={KPI_VALUE_TEXT}>
                        {formatDashboardValorNumerico(dashboardQtdVendas, ocultarValoresDashboard)}
                      </div>
                      <DashboardKpiComparacaoLine
                        comparacao={dashboardComparativoPeriodo.qtdVendas}
                        oculto={ocultarValoresDashboard}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className={`${DASHBOARD_KPI_VALUE_CARD} md:col-span-2 lg:col-span-1`}>
                  <CardHeader className={DASHBOARD_KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>Ticket Médio</CardTitle>
                  </CardHeader>
                  <CardContent className={DASHBOARD_KPI_VALUE_CARD_CONTENT}>
                    <div className="min-w-0">
                      <div className={KPI_VALUE_TEXT}>
                        {formatDashboardValorMonetario(dashboardTicketMedio, ocultarValoresDashboard)}
                      </div>
                      <DashboardKpiComparacaoLine
                        comparacao={dashboardComparativoPeriodo.ticketMedio}
                        oculto={ocultarValoresDashboard}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card className={DASHBOARD_KPI_VALUE_CARD}>
                  <CardHeader className={DASHBOARD_KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>Lucro</CardTitle>
                  </CardHeader>
                  <CardContent className={DASHBOARD_KPI_VALUE_CARD_CONTENT}>
                    <div className="min-w-0">
                      <div className={KPI_VALUE_TEXT}>
                        {dashboardLucro == null ? "Indisponível" : formatDashboardLucroMonetario(dashboardLucro, ocultarValoresDashboard)}
                      </div>
                      <DashboardKpiComparacaoLine
                        comparacao={dashboardComparativoPeriodo.lucro}
                        oculto={ocultarValoresDashboard}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className={DASHBOARD_KPI_VALUE_CARD}>
                  <CardHeader className={DASHBOARD_KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>Custo de Produtos Vendidos</CardTitle>
                  </CardHeader>
                  <CardContent className={DASHBOARD_KPI_VALUE_CARD_CONTENT}>
                    <div className={KPI_VALUE_TEXT} title={dashboardCustoProdutosVendidos == null ? "Custo incompleto: produto, composicao ou custo de insumo indisponivel neste periodo." : undefined}>
                      {dashboardCustoProdutosVendidos == null ? "Indisponível" : formatDashboardValorMonetario(dashboardCustoProdutosVendidos, ocultarValoresDashboard)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
                <Card className="flex h-full flex-col bg-white">
                  <CardContent className="grid h-full grid-cols-1 gap-5 p-0 px-6 py-6 !pt-6 text-sm leading-none lg:grid-cols-[minmax(0,1fr)_190px] lg:items-stretch lg:gap-6">
                    <div className="flex min-w-0 flex-col gap-6">
                      <CardTitle className="text-sm font-medium">Meios de Pagamento</CardTitle>
                      <div className="flex min-w-0 flex-col gap-5">
                        {dashboardMeiosPagamentoRows.map((row) => {
                          const iconSrc = {
                            pix: "/imagens/pix.svg",
                            dinheiro: "/imagens/Dinheiro.svg",
                            credito: "/imagens/Credito.svg",
                            debito: "/imagens/Debito.svg",
                          }[row.key];

                          return (
                            <div key={row.key} className="flex items-center justify-between gap-4 leading-none">
                              <span className="flex items-center gap-2 leading-none">
                                <img src={iconSrc} alt="" aria-hidden className="size-4 shrink-0 object-contain m-0 p-0 block" />
                                <span>{row.label}</span>
                              </span>
                              <span className="leading-none tabular-nums">
                                {formatDashboardValorMonetario(row.valor, ocultarValoresDashboard)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <ResponsiveChartFrame
                      className={`dashboard-meios-pagamento-donut hidden h-[168px] w-[190px] shrink-0 items-center justify-center self-center lg:flex ${
                        dashboardMeiosPagamentoTemDados ? "" : "pointer-events-none select-none"
                      }`}
                    >
                      {({ width, height }) => (
                        <PieChart width={width} height={height}>
                          {dashboardMeiosPagamentoTemDados ? (
                            <Tooltip content={<DashboardMeiosPagamentoTooltip />} cursor={false} />
                          ) : null}
                          <Pie
                            data={
                              dashboardMeiosPagamentoTemDados
                                ? dashboardMeiosPagamentoChartRows
                                : DASHBOARD_MEIOS_PAGAMENTO_EMPTY_DONUT_ROWS
                            }
                            dataKey="valor"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius={36}
                            outerRadius={78}
                            rootTabIndex={-1}
                            stroke="none"
                            isAnimationActive={false}
                          >
                            {(dashboardMeiosPagamentoTemDados
                              ? dashboardMeiosPagamentoChartRows
                              : DASHBOARD_MEIOS_PAGAMENTO_EMPTY_DONUT_ROWS
                            ).map((row) => (
                              <Cell key={row.key} fill={row.fill} />
                            ))}
                          </Pie>
                        </PieChart>
                      )}
                    </ResponsiveChartFrame>
                  </CardContent>
                </Card>
                <Card className="flex h-full flex-col bg-white">
                  <CardHeader className="space-y-0 p-0 px-6 pt-6 pb-0">
                    <CardTitle className="text-sm font-medium">Produtos Mais Vendidos</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col p-0 px-6 pb-6 pt-2">
                    {dashboardProdutosTop.length === 0 ? (
                      <p className="text-muted-foreground text-sm">Sem vendas no período.</p>
                    ) : (
                      <div className="relative w-full space-y-2">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute left-[162px] top-0 bottom-[21px] w-[2px] bg-[#A8A8A8]"
                        />
                        {dashboardProdutosTopChart.rows.map((row, idx) => {
                          const pct =
                            dashboardProdutosTopChart.xMax > 0
                              ? (row.quantidade / dashboardProdutosTopChart.xMax) * 100
                              : 0;
                          return (
                            <div
                              key={`${row.nome}-${idx}`}
                              className="grid grid-cols-[150px_1fr] items-center gap-3"
                            >
                              <span
                                className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm text-foreground"
                                title={row.nome}
                              >
                                {row.nome}
                              </span>
                              <DashboardProdutosBarWithTooltip
                                nome={row.nome}
                                quantidade={row.quantidade}
                                pct={pct}
                                animationDelayMs={idx * 100}
                              />
                            </div>
                          );
                        })}
                        <div className="grid grid-cols-[150px_1fr] gap-3">
                          <div aria-hidden className="min-w-0" />
                          <div className="min-w-0">
                            <div className="h-[2px] w-full bg-[#A8A8A8]" />
                            <div className="relative h-[22px] w-full">
                              {dashboardProdutosTopChart.xTicks.map((tick) => {
                                const atStart = tick === 0;
                                const atEnd = tick === dashboardProdutosTopChart.xMax;
                                const leftPct =
                                  dashboardProdutosTopChart.xMax > 0
                                    ? (tick / dashboardProdutosTopChart.xMax) * 100
                                    : 0;
                                const tickPosStyle: React.CSSProperties = {
                                  left: atStart ? "0%" : atEnd ? "100%" : `${leftPct}%`,
                                  transform: atStart
                                    ? "none"
                                    : atEnd
                                      ? "translateX(-100%)"
                                      : "translateX(-50%)",
                                };
                                return (
                                  <div
                                    key={tick}
                                    className={`absolute top-0 flex flex-col ${atStart ? "items-start" : "items-center"}`}
                                    style={tickPosStyle}
                                  >
                                    <div
                                      className="h-[6px] w-[2px] shrink-0 bg-[#A8A8A8]"
                                      aria-hidden
                                    />
                                    <span
                                      className={`mt-0.5 text-[11px] leading-none text-gray-500 ${
                                        atStart ? "ml-px -translate-x-1/2" : ""
                                      }`}
                                    >
                                      {tick}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      className="mt-1 block self-start p-0 text-left text-sm font-bold text-foreground bg-transparent border-0 shadow-none cursor-pointer hover:underline underline-offset-2"
                      onClick={() => {
                        setDashboardModalProdutosBusca("");
                        setDashboardModalProdutosCategoria("__todos__");
                        setDashboardModalProdutosOpen(true);
                      }}
                    >
                      Ver todos
                    </button>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Vendas por Hora</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveChartFrame className="dashboard-vendas-hora-chart w-full h-[320px]">
                    {({ width, height }) => (
                    <ResponsiveContainer width={width} height={height}>
                      <AreaChart data={dashboardVendasPorHora} margin={{ top: 8, right: 32, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="fillDashboardVendasPorHora" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={clientConfig.primary_color} stopOpacity={0.35} />
                            <stop offset="40%" stopColor={clientConfig.primary_color} stopOpacity={0.2} />
                            <stop offset="70%" stopColor={clientConfig.primary_color} stopOpacity={0.1} />
                            <stop offset="100%" stopColor={clientConfig.primary_color} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                          dataKey="hora"
                          interval={0}
                          ticks={dashboardVendasPorHoraTicks}
                          minTickGap={8}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          type="number"
                          width={28}
                          allowDecimals={false}
                          domain={[0, dashboardVendasPorHoraYAxisMax]}
                          allowDataOverflow
                          ticks={dashboardVendasPorHoraAllZero ? [0, 1] : undefined}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip formatter={(value) => [`${Number(value ?? 0)}`, "Vendas"]} labelFormatter={(label) => `Horário: ${label}`} />
                        <Area
                          type="monotone"
                          dataKey="vendas"
                          stroke={clientConfig.primary_color}
                          strokeWidth={2}
                          fill="url(#fillDashboardVendasPorHora)"
                          dot={false}
                          activeDot={false}
                          animationDuration={1500}
                        />
                        <Line
                          type="monotone"
                          dataKey="vendas"
                          stroke="none"
                          fill="none"
                          tooltipType="none"
                          dot={{ r: 3, fill: clientConfig.primary_color, stroke: clientConfig.primary_color, clipDot: false }}
                          activeDot={{ r: 5, fill: clientConfig.primary_color, stroke: clientConfig.primary_color, clipDot: false }}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    )}
                  </ResponsiveChartFrame>
                </CardContent>
              </Card>

              {dashboardModalProdutosOpen && (
                <StandardModal
                  open={dashboardModalProdutosOpen}
                  title="Produtos mais vendidos"
                  panelClassName="gs-modal-panel-max max-w-xl max-h-[85vh]"
                  onClose={() => {
                    setDashboardModalProdutosOpen(false);
                    setDashboardModalProdutosBusca("");
                    setDashboardModalProdutosCategoria("__todos__");
                  }}
                >
                  <div className={`max-h-[65vh] overflow-y-auto max-sm:max-h-none ${MODAL_BODY_CLASS}`}>
                    {dashboardProdutosVendidos.length === 0 ? (
                      <p className="text-muted-foreground text-sm py-4">Sem vendas no período.</p>
                    ) : (
                      <>
                        <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-3">
                          <div className="min-w-0 flex-1">
                            <Label htmlFor="dashboard-modal-produtos-busca" className="sr-only">
                              Pesquisar produto
                            </Label>
                            <Input
                              id="dashboard-modal-produtos-busca"
                              placeholder="Pesquisar"
                              value={dashboardModalProdutosBusca}
                              onChange={(e) => setDashboardModalProdutosBusca(e.target.value)}
                              className="h-10 w-full"
                            />
                          </div>
                          <div className="w-[min(11rem,42%)] shrink-0 sm:w-44">
                            <Label htmlFor="dashboard-modal-produtos-categoria" className="sr-only">
                              Categoria
                            </Label>
                            <FilterSelect
                              id="dashboard-modal-produtos-categoria"
                              value={dashboardModalProdutosCategoria}
                              onChange={(e) => setDashboardModalProdutosCategoria(e.target.value)}
                              className="h-10 w-full border-gray-200 bg-white"
                              aria-label="Filtrar por categoria"
                            >
                              <option value="__todos__">Todos</option>
                              {dashboardModalCategoriasOpcoes.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </FilterSelect>
                          </div>
                        </div>
                        {dashboardProdutosVendidosModalFiltrados.length === 0 ? (
                          <p className="text-muted-foreground text-sm py-2">Nenhum produto encontrado.</p>
                        ) : (
                          <div className="space-y-2">
                            {dashboardProdutosVendidosModalFiltrados.map((p) => (
                              <div key={p.nome} className="flex items-center justify-between border-b border-gray-100 py-2 text-sm">
                                <span className="truncate pr-3">{formatDisplayText(p.nome)}</span>
                                <span className="tabular-nums">{p.quantidade}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </StandardModal>
              )}

              </>
              )}

              <ModalPeriodoPersonalizado
                open={showModalPeriodoPersonalizadoDashboard}
                onClose={() => setShowModalPeriodoPersonalizadoDashboard(false)}
                inicio={dashboardPersonalizadoInicio}
                fim={dashboardPersonalizadoFim}
                onInicioChange={setDashboardPersonalizadoInicio}
                onFimChange={setDashboardPersonalizadoFim}
                onApply={() => {
                  if (dashboardPersonalizadoInicio && dashboardPersonalizadoFim) {
                    setShowModalPeriodoPersonalizadoDashboard(false);
                    setDashboardPersonalizadoAplicado({ start: dashboardPersonalizadoInicio, end: dashboardPersonalizadoFim });
                  } else toast.error("Informe data início e fim.");
                }}
              />
            </TabsContent>
          )}

          {canViewTab("estoque") && (
          <TabsContent value="estoque" className="min-w-0 space-y-4 transition-opacity duration-200 ease-in-out">
            <StandardModal
              open={estoqueConferenciaModalOpen}
              title="Registrar Conferência"
              titleId="estoque-conf-title"
              onClose={closeEstoqueConferenciaModal}
              closeDisabled={savingConferencia}
              panelClassName="max-w-md"
            >
              <div className={MODAL_BODY_CLASS}>
                <p className="text-sm text-muted-foreground">
                  Confirme a conferência do estoque com sua assinatura
                </p>
                <div className="grid grid-cols-1 gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Data</p>
                    <p className="mt-1 flex items-center gap-2 font-medium text-foreground">
                      <Calendar className="h-4 w-4 text-accent" />
                      {formatarData(new Date())}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Horário</p>
                    <p className="mt-1 flex items-center gap-2 font-medium text-foreground">
                      <Clock className="h-4 w-4 text-accent" />
                      {formatarHora(new Date())}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-muted-foreground">Conferido por</p>
                    <p className="mt-1 flex items-center gap-2 font-medium text-foreground">
                      <User className="h-4 w-4 text-accent" />
                      {user?.name || user?.username || "Usuário"}
                    </p>
                  </div>
                </div>
                <ModalPrimaryAction
                  show
                  type="button"
                  loading={savingConferencia}
                  disabled={savingConferencia}
                  onClick={() => void handleConfirmarConferenciaEstoqueModal()}
                >
                  {savingConferencia ? "Registrando..." : "Confirmar Conferência"}
                </ModalPrimaryAction>
              </div>
            </StandardModal>

            {insumosLoading ? (
              <PageContentLoader />
            ) : (
            <>
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
              <h2 className={`${PAGE_TITLE_CLASS} min-w-0`}>Estoque</h2>
              {canViewTab("entradas") && clientConfig.ai_stock_enabled === true ? (
                <AiStockEntradaFlow
                  enabled
                  onSuccess={() => {
                    void carregarDados();
                    void carregarEntradas();
                  }}
                />
              ) : null}
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="gs-admin-table-toolbar gs-stock-toolbar flex flex-col items-stretch gap-3 border-b border-gray-100 min-[768px]:flex-row min-[768px]:items-center min-[768px]:justify-between min-[768px]:gap-4">
                  <TableSearchInput
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                    className="gs-stock-search w-full max-w-full min-[768px]:w-[360px]"
                  />
                  <div className="gs-stock-filter-container min-w-0">
                    <div className="gs-stock-filter-group">
                    <CategoryLabeledFilterSelect
                      id="estoque-categoria"
                      value={selectedCategories}
                      onChange={setSelectedCategories}
                      labelBgClass="bg-white"
                      fieldClassName="w-full min-[768px]:w-[220px]"
                      className="h-10 w-full min-w-0 bg-white min-[768px]:w-[220px]"
                    >
                      {estoqueOpcoesCategoriaSelect.map((o) => (
                        <option key={o.value === "" ? "__todos__" : o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </CategoryLabeledFilterSelect>
                    <LabeledFilterSelect
                      id="estoque-ordenar-por"
                      label="Ordenar por"
                      labelBgClass="bg-white"
                      value={estoqueSortOrder}
                      onChange={(event) => setEstoqueSortOrder(event.target.value as typeof estoqueSortOrder)}
                      className="h-10 w-full min-w-0 bg-white min-[768px]:w-[220px]"
                    >
                      <option value="name-asc">Nome: A–Z</option>
                      <option value="name-desc">Nome: Z–A</option>
                      <option value="stock-asc">Menor Estoque</option>
                      <option value="stock-desc">Maior Estoque</option>
                    </LabeledFilterSelect>
                    </div>
                  </div>
                </div>
                {canViewTab("inventario") ? (
                    <div className="w-full min-w-0 overflow-x-hidden">
                      <table className="gs-management-table gs-stock-inventory-table w-full min-w-0 table-fixed border-collapse text-sm">
                        <thead className={TABLE_HEAD_CLASS}>
                          <tr>
                            <th className={`min-w-0 w-[52%] px-2 py-2.5 text-left min-[767px]:w-[42%] ${TABLE_TH_CLASS}`}>Nome</th>
                            <th className={`min-w-0 w-[48%] whitespace-nowrap px-2 py-2.5 text-center min-[767px]:w-[33%] ${TABLE_TH_CLASS}`}>Estoque Atual</th>
                            <th className={`hidden w-[25%] px-2 py-2.5 text-right min-[767px]:table-cell ${TABLE_TH_CLASS}`}>Valor em Estoque</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linhasEstoqueLista.length === 0 ? (
                            <>
                              <FinanceiroEmptyRow colSpan={2} className="min-[767px]:hidden" />
                              <FinanceiroEmptyRow colSpan={3} className="hidden min-[767px]:table-row" />
                            </>
                          ) : (
                            linhasEstoqueLista.map((row) => {
                              if (row.tipo === "insumo") {
                                const insumo = row.item;
                                const isEstoqueBaixo = (insumosEstoqueBaixo ?? []).some((i) => i.id === insumo.id);
                                const currentQty = quantities[insumo.id] ?? insumo.quantidadeAtual;
                                const valorEmEstoque = formatValorEmEstoque(insumo.valorAtual, currentQty);
                                const rowKey = `ins-${insumo.id}`;
                                return (
                                  <tr key={rowKey} className="border-t border-gray-200">
                                    <td className="max-w-0 min-w-0 px-2 py-2.5 align-middle">
                                      <div className="flex min-w-0 items-center gap-1.5 font-medium text-foreground">
                                        <span className="min-w-0 whitespace-normal break-words">{formatDisplayText(insumo.nome)}</span>
                                        {isEstoqueBaixo ? <AlertCircle className="h-4 w-4 shrink-0 text-destructive" /> : null}
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2.5 align-middle">
                                      <div className="flex justify-center">
                                        <div className="inline-flex max-w-full flex-nowrap items-center gap-2">
                                          <Input
                                            type="text"
                                            inputMode="decimal"
                                            value={estoqueQuantityDrafts[rowKey] ?? formatEstoqueAtual(currentQty)}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (!/^\d*(?:[.,]\d*)?$/.test(value)) return;
                                              setEstoqueQuantityDraft(rowKey, value);
                                              handleQuantidadeChange(insumo.id, value);
                                            }}
                                            onFocus={(e) => {
                                              setEstoqueQuantityDraft(rowKey, formatEstoqueAtual(currentQty));
                                              e.currentTarget.select();
                                            }}
                                            onBlur={() => setEstoqueQuantityDraft(rowKey, null)}
                                            className="!h-9 !w-[3.75rem] max-w-[3.75rem] shrink-0 px-2 text-center text-sm tabular-nums min-[767px]:!w-[4.75rem] min-[767px]:max-w-[4.75rem]"
                                            aria-label={`Estoque atual ${insumo.nome}`}
                                          />
                                          <span className="shrink-0 text-sm font-medium text-muted-foreground">{insumo.unidade}</span>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="hidden whitespace-nowrap px-2 py-2.5 text-right align-middle tabular-nums min-[767px]:table-cell">
                                      {valorEmEstoque}
                                    </td>
                                  </tr>
                                );
                              }
                              const produto = row.item;
                              const currentQty = produtoQuantities[produto.id] ?? produto.quantidadeAtual;
                              const valorEmEstoque = formatValorEmEstoque(produto.valorAtual, currentQty);
                              const un = (produto.unidade ?? "un").trim() || "un";
                              const rowKey = `prod-${produto.id}`;
                              return (
                                <tr key={rowKey} className="border-t border-gray-200">
                                  <td className="max-w-0 min-w-0 px-2 py-2.5 align-middle">
                                    <div className="min-w-0 whitespace-normal break-words font-medium text-foreground">{formatDisplayText(produto.nome)}</div>
                                  </td>
                                  <td className="whitespace-nowrap px-2 py-2.5 align-middle">
                                    <div className="flex justify-center">
                                      <div className="inline-flex max-w-full flex-nowrap items-center gap-2">
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          value={estoqueQuantityDrafts[rowKey] ?? formatEstoqueAtual(currentQty)}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (!/^\d*(?:[.,]\d*)?$/.test(value)) return;
                                            setEstoqueQuantityDraft(rowKey, value);
                                            handleQuantidadeProdutoChange(produto.id, value);
                                          }}
                                          onFocus={(e) => {
                                            setEstoqueQuantityDraft(rowKey, formatEstoqueAtual(currentQty));
                                            e.currentTarget.select();
                                          }}
                                          onBlur={() => setEstoqueQuantityDraft(rowKey, null)}
                                          className="!h-9 !w-[3.75rem] max-w-[3.75rem] shrink-0 px-2 text-center text-sm tabular-nums min-[767px]:!w-[4.75rem] min-[767px]:max-w-[4.75rem]"
                                          aria-label={`Estoque atual ${produto.nome}`}
                                        />
                                        <span className="shrink-0 text-sm font-medium text-muted-foreground">{un}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="hidden whitespace-nowrap px-2 py-2.5 text-right align-middle tabular-nums min-[767px]:table-cell">
                                    {valorEmEstoque}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                ) : null}

                {estoqueTemPendencias && activeTab === "estoque" && canViewTab("inventario") ? (
                  <Button
                    type="button"
                    className="gs-stock-save-fab"
                    style={{ backgroundColor: "var(--client-primary)", color: estoqueFabIconColor }}
                    aria-label="Registrar conferência do estoque"
                    title="Registrar conferência do estoque"
                    disabled={savingConferencia}
                    onClick={abrirConferenciaEstoquePendentes}
                  >
                    <Check className="h-7 w-7" strokeWidth={2.5} aria-hidden="true" />
                  </Button>
                ) : null}

                {/* Subaba Entradas removida da UI — lógica/API mantidas para o fluxo por IA. */}
                {false && canViewTab("entradas") && (!canViewTab("inventario") || estoqueSubTab === "entradas") ? (
                  loadingEntradas ? (
                    <PageContentLoader />
                  ) : (
                    <div className="w-full min-w-0 overflow-x-auto">
                      <table className="gs-management-table w-full min-w-[780px] table-fixed border-collapse text-sm">
                        <colgroup>
                          <col className="min-w-0" style={{ width: "34%" }} />
                          <col style={{ width: "16%" }} />
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "16%" }} />
                          <col style={{ width: "12%" }} />
                        </colgroup>
                        <thead className={TABLE_HEAD_CLASS}>
                          <tr>
                            <th className={`min-w-0 whitespace-nowrap py-2.5 pl-3 pr-2 text-left ${TABLE_TH_CLASS}`}>Nome</th>
                            <th className={`whitespace-nowrap px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Custo</th>
                            <th className={`whitespace-nowrap px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Qtd entrada</th>
                            <th className={`whitespace-nowrap px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Valor Pago</th>
                            <th className="min-w-[5.5rem] whitespace-nowrap py-2.5 pr-3" aria-hidden />
                          </tr>
                        </thead>
                        <tbody>
                          {linhasEntradasOrdenadas.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                                {entradasProdutos.length === 0 && entradasInsumos.length === 0
                                  ? "Nenhum produto (sem composição) ou insumo cadastrado."
                                  : "Nenhum item encontrado para o filtro selecionado."}
                              </td>
                            </tr>
                          ) : (
                            linhasEntradasOrdenadas.map((row) => {
                              if (row.tipo === "insumo") {
                                const insumo = row.insumo;
                                const key = `insumo-${insumo.id}`;
                                const qStr = entradasQuantities[key] ?? "";
                                const unidade = insumo.unidade?.trim() || "un";
                                const opcoesUnidadeEntrada = entradasOpcoesUnidade(unidade);
                                const valorUnidadeEntrada = entradasUnidadeValorSelect(key, unidade, entradasUnidades);
                                const mostrarSalvar =
                                  entradasUltimaLinhaInteracao === key &&
                                  entradasLinhaQtdEValorCompletos(key, entradasQuantities, entradasValores) &&
                                  entradasLinhaUnidadeSatisfeita(key, row, entradasUnidadeInteragida);
                                return (
                                  <tr key={key} className="border-t border-gray-200">
                                    <td className="min-w-0 py-2.5 pl-3 pr-2 align-middle">
                                      <span className="block truncate font-medium text-foreground" title={formatDisplayText(insumo.nome)}>
                                        {formatDisplayText(insumo.nome)}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-1 py-2.5 align-middle">
                                      {insumo.valorAtual != null ? (
                                        <span className="inline-flex items-center gap-1 font-normal tabular-nums text-foreground">
                                          <span className="text-sm">{formatBRL(insumo.valorAtual)}</span>
                                          <span className="text-sm font-normal text-muted-foreground">{unidade}</span>
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </td>
                                    <td className="whitespace-nowrap px-1 py-2.5 align-middle">
                                      <div className="inline-flex max-w-full flex-nowrap items-center gap-1">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={qStr}
                                          onChange={(e) => {
                                            setEntradasQuantities((prev) => ({ ...prev, [key]: e.target.value }));
                                            setEntradasUltimaLinhaInteracao(key);
                                          }}
                                          placeholder="Qtd"
                                          className="!h-9 !w-[3.25rem] max-w-[3.25rem] shrink-0 px-1 text-sm"
                                        />
                                        {opcoesUnidadeEntrada ? (
                                          <Select
                                            size="compact"
                                            value={valorUnidadeEntrada}
                                            onChange={(e) => {
                                              setEntradasUnidades((prev) => ({ ...prev, [key]: e.target.value }));
                                              setEntradasUnidadeInteragida((prev) => ({ ...prev, [key]: true }));
                                              setEntradasUltimaLinhaInteracao(key);
                                            }}
                                            className="border-border bg-muted font-medium text-muted-foreground"
                                            aria-label={`Unidade ${insumo.nome}`}
                                          >
                                            {opcoesUnidadeEntrada.map((opt) => (
                                              <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                              </option>
                                            ))}
                                          </Select>
                                        ) : entradasUnidadeEhUn(unidade) ? null : (
                                          <span className="inline-flex h-9 min-w-[2rem] shrink-0 items-center justify-center rounded-md border border-border bg-muted px-1.5 text-sm font-medium text-muted-foreground">
                                            {unidadeCanonicaParaBase(unidade)}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="min-w-0 whitespace-nowrap px-1 py-2.5 align-middle">
                                      <MoneyInput
                                        value={entradasValores[key] ?? ""}
                                        onChange={(v) => {
                                          setEntradasValores((prev) => ({ ...prev, [key]: v }));
                                          setEntradasUltimaLinhaInteracao(key);
                                        }}
                                        placeholder="0,00"
                                        className="!h-9 !w-[7.5rem] max-w-[7.5rem] shrink-0 text-sm"
                                      />
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 pl-0 pr-3 text-right align-middle">
                                      {mostrarSalvar ? (
                                        <Button
                                          type="button"
                                          className={`inline-flex h-9 shrink-0 items-center justify-center rounded-md px-3 text-sm font-medium ${BTN_PAGE_PRIMARY_APPEARANCE}`}
                                          disabled={savingEntradasPendentes}
                                          onClick={() => void handleSalvarEntradasPendentes()}
                                        >
                                          {savingEntradasPendentes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                                        </Button>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              }
                              const produto = row.produto;
                              const key = `produto-${produto.id}`;
                              const qStr = entradasQuantities[key] ?? "";
                              const mostrarSalvar =
                                entradasUltimaLinhaInteracao === key &&
                                entradasLinhaQtdEValorCompletos(key, entradasQuantities, entradasValores) &&
                                entradasLinhaUnidadeSatisfeita(key, row, entradasUnidadeInteragida);
                              return (
                                <tr key={key} className="border-t border-gray-200">
                                  <td className="min-w-0 py-2.5 pl-3 pr-2 align-middle">
                                    <span className="block truncate font-medium text-foreground" title={formatDisplayText(produto.nome)}>
                                      {formatDisplayText(produto.nome)}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2.5 align-middle">
                                    {produto.valorAtual != null ? (
                                      <span className="inline-flex items-center gap-1 font-normal tabular-nums text-foreground">
                                        <span className="text-sm">{formatBRL(produto.valorAtual)}</span>
                                        <span className="text-sm font-normal text-muted-foreground">un</span>
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2.5 align-middle">
                                    <div className="inline-flex max-w-full flex-nowrap items-center gap-1">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={qStr}
                                        onChange={(e) => {
                                          setEntradasQuantities((prev) => ({ ...prev, [key]: e.target.value }));
                                          setEntradasUltimaLinhaInteracao(key);
                                        }}
                                        placeholder="Qtd"
                                        className="!h-9 !w-[3.25rem] max-w-[3.25rem] shrink-0 px-1 text-sm"
                                      />
                                    </div>
                                  </td>
                                  <td className="min-w-0 whitespace-nowrap px-1 py-2.5 align-middle">
                                    <MoneyInput
                                      value={entradasValores[key] ?? ""}
                                      onChange={(v) => {
                                        setEntradasValores((prev) => ({ ...prev, [key]: v }));
                                        setEntradasUltimaLinhaInteracao(key);
                                      }}
                                      placeholder="0,00"
                                      className="!h-9 !w-[7.5rem] max-w-[7.5rem] shrink-0 text-sm"
                                    />
                                  </td>
                                  <td className="whitespace-nowrap py-2.5 pl-0 pr-3 text-right align-middle">
                                    {mostrarSalvar ? (
                                      <Button
                                        type="button"
                                        className={`inline-flex h-9 shrink-0 items-center justify-center rounded-md px-3 text-sm font-medium ${BTN_PAGE_PRIMARY_APPEARANCE}`}
                                        disabled={savingEntradasPendentes}
                                        onClick={() => void handleSalvarEntradasPendentes()}
                                      >
                                        {savingEntradasPendentes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                                      </Button>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : null}
            </div>
            </>
            )}
          </TabsContent>
          )}

          {/* Aba Reposição (permissão: compras) — layout alinhado a Produtos / Insumos */}
          <TabsContent value="compras" className="space-y-4 transition-opacity duration-200 ease-in-out">
            {insumosLoading ? (
              <PageContentLoader />
            ) : (
            <div className="space-y-4">
              <div className="flex max-w-full min-w-0 flex-nowrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className={PAGE_TITLE_CLASS}>Reposição</h2>
                </div>
                <div className="flex min-w-0 shrink-0 flex-nowrap items-end justify-end gap-2 sm:gap-3">
                  {reposicaoSelecionados.length > 0 ? (
                    <Button
                      type="button"
                      onClick={() => void copiarReposicaoSelecionados()}
                      className={`inline-flex h-10 shrink-0 items-center justify-center gap-1 rounded-md px-2 text-sm font-medium sm:gap-2 sm:px-4 ${BTN_PAGE_PRIMARY_APPEARANCE}`}
                      aria-label="Copiar itens selecionados"
                    >
                      <Copy className="h-4 w-4 shrink-0" />
                      <span className="hidden min-[400px]:inline min-[767px]:hidden">Copiar</span>
                      <span className="hidden min-[767px]:inline">Copiar Itens</span>
                    </Button>
                  ) : null}
                  <CategoryLabeledFilterSelect
                    id="reposicao-categoria"
                    value={reposicaoCategoriasFiltro}
                    onChange={setReposicaoCategoriasFiltro}
                    className="h-10 min-w-0 w-[min(140px,calc(100vw-8rem))] min-[767px]:w-[220px]"
                  >
                    {reposicaoOpcoesCategoriaSelect.map((o) => (
                      <option key={o.value === "" ? "__todos__" : o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </CategoryLabeledFilterSelect>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-hidden min-[767px]:overflow-x-auto">
                  <table className="gs-management-table w-full min-w-0 text-sm min-[767px]:min-w-[640px]">
                    <thead className={TABLE_HEAD_CLASS}>
                      <tr className="border-b border-gray-200">
                        <th className="w-11 px-2 py-3 text-left align-middle">
                          <ReposicaoCheckbox
                            inputRef={reposicaoHeaderCheckboxRef}
                            checked={reposicaoSelectionState.allSelected}
                            indeterminate={reposicaoSelectionState.indeterminate}
                            disabled={reposicaoVisibleKeys.length === 0}
                            onChange={() => {
                              const selectVisible = !reposicaoSelectionState.allSelected;
                              setReposicaoSelecionados((previous) =>
                                toggleVisibleSelection(previous, reposicaoVisibleKeys, selectVisible),
                              );
                            }}
                            ariaLabel="Selecionar todos os itens visíveis"
                          />
                        </th>
                        <th className={`w-full min-w-0 px-2 py-3 text-left min-[767px]:w-auto min-[767px]:px-4 ${TABLE_TH_CLASS}`}>Itens a repor</th>
                        <th className={`hidden min-w-[220px] px-4 py-3 text-left min-[767px]:table-cell ${TABLE_TH_CLASS}`}>Estoque</th>
                        <th className={`w-[90px] min-w-[90px] whitespace-nowrap px-2 py-3 text-right min-[767px]:w-28 min-[767px]:min-w-0 min-[767px]:px-4 min-[767px]:text-left ${TABLE_TH_CLASS}`}>Faltam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasReposicao.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="p-0 min-[767px]:hidden">
                            <div className="flex flex-col items-center justify-center px-4 py-4 text-center min-[768px]:py-6">
                              <img
                                src="/imagens/erro.svg"
                                alt=""
                                className="mb-5 h-auto w-[180px] max-w-full object-contain min-[768px]:w-[240px]"
                              />
                              <p className="gs-empty-state-message text-base font-semibold text-foreground">Nenhum registro localizado</p>
                            </div>
                          </td>
                          <td colSpan={4} className="hidden p-0 min-[767px]:table-cell">
                            <div className="flex flex-col items-center justify-center px-4 py-4 text-center min-[768px]:py-6">
                              <img
                                src="/imagens/erro.svg"
                                alt=""
                                className="mb-5 h-auto w-[180px] max-w-full object-contain min-[768px]:w-[240px]"
                              />
                              <p className="gs-empty-state-message text-base font-semibold text-foreground">Nenhum registro localizado</p>
                            </div>
                          </td>
                        </tr>
                      ) : linhasReposicao.map((row) => {
                          const isInsumo = row.tipo === "insumo";
                          const atual = Number(row.item.quantidadeAtual ?? 0);
                          const minimo = isInsumo
                            ? Number(row.item.estoqueMinimo ?? 0)
                            : Number(row.item.quantidade_minima ?? 0);
                          const un = isInsumo
                            ? (row.item.unidade || "").trim() || "un"
                            : (row.item.unidade || "un").trim();
                          const faltam = Math.max(0, minimo - atual);
                          return (
                            <tr key={row.key} className="border-b border-gray-100 last:border-0 hover:bg-muted/20">
                              <td className="px-2 py-3 align-middle">
                                <div className="flex items-center justify-start">
                                  <ReposicaoCheckbox
                                    checked={reposicaoSelecionados.includes(row.key)}
                                    onChange={(e) => {
                                      const on = e.target.checked;
                                      setReposicaoSelecionados((prev) => {
                                        if (on) return prev.includes(row.key) ? prev : [...prev, row.key];
                                        return prev.filter((k) => k !== row.key);
                                      });
                                    }}
                                    ariaLabel={`Selecionar ${row.item.nome}`}
                                  />
                                </div>
                              </td>
                              <td className="w-full min-w-0 px-2 py-3 align-middle min-[767px]:w-auto min-[767px]:px-4">
                                <span className="block whitespace-normal break-words font-medium text-foreground">{formatDisplayText(row.item.nome)}</span>
                              </td>
                              <td className="hidden px-4 py-3 align-middle text-sm text-muted-foreground tabular-nums min-[767px]:table-cell">
                                Atual: {formatReposicaoDecimal(atual)} {un} | Mínimo: {formatReposicaoDecimal(minimo)} {un}
                              </td>
                              <td className="w-[90px] min-w-[90px] whitespace-nowrap px-2 py-3 align-middle text-right text-sm text-muted-foreground tabular-nums min-[767px]:w-28 min-[767px]:min-w-0 min-[767px]:px-4 min-[767px]:text-left">
                                {formatReposicaoFaltam(faltam)} {un}
                              </td>
                            </tr>
                          );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            )}
          </TabsContent>

          {/* Aba Desperdícios */}
          <TabsContent value="desperdicios" className="space-y-4 transition-opacity duration-200 ease-in-out">
            {loadingDesperdicios ? (
              <PageContentLoader />
            ) : (
            <div className="space-y-4">
              <div className="min-w-0">
                <h2 className={PAGE_TITLE_CLASS}>Desperdícios</h2>
              </div>

              {/* Card total de desperdício: full no mobile; no desktop igual a "Valor a pagar" (max-w-md) */}
              <Card className={`w-full max-w-md ${KPI_VALUE_CARD}`}>
                <CardHeader className={KPI_VALUE_CARD_HEADER}>
                  <CardTitle className={KPI_VALUE_CARD_TITLE}>Desperdício</CardTitle>
                </CardHeader>
                <CardContent className={KPI_VALUE_CARD_CONTENT}>
                  <div className="min-w-0">
                    <div className={KPI_VALUE_TEXT}>
                      {ocultarValoresDesperdicios ? "••••••" : formatBRL(totalDesperdiciosVisivel)}
                    </div>
                    {desperdiciosBuscaTrim ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        Total dos lançamentos filtrados pela busca (nome).
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={KPI_VALUE_EYE_BTN}
                    onClick={() => setOcultarValoresDesperdicios((v) => !v)}
                    title={ocultarValoresDesperdicios ? "Mostrar valores" : "Ocultar valores"}
                  >
                    {ocultarValoresDesperdicios ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </CardContent>
              </Card>

              <ModalPeriodoPersonalizado
                open={showModalDesperdiciosPeriodo}
                onClose={() => setShowModalDesperdiciosPeriodo(false)}
                inicio={desperdiciosPersonalizadoInicio}
                fim={desperdiciosPersonalizadoFim}
                onInicioChange={setDesperdiciosPersonalizadoInicio}
                onFimChange={setDesperdiciosPersonalizadoFim}
                onApply={() => {
                  if (desperdiciosPersonalizadoInicio && desperdiciosPersonalizadoFim) {
                    aplicarPeriodoDesperdicios();
                  } else {
                    toast.error("Informe data início e fim.");
                  }
                }}
              />

              <div>
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="gs-admin-table-toolbar flex flex-col gap-3 border-b border-gray-100 min-[767px]:flex-row min-[767px]:items-center min-[767px]:justify-between">
                      <div className="w-full min-w-0 min-[767px]:max-w-[360px]">
                      <Autocomplete
                        requireQueryForSuggestions
                        showSearchIcon
                        showClearButton
                        options={desperdiciosBuscaOpcoes}
                        value={desperdiciosBuscaNome}
                        onValueChange={(value) => {
                          setDesperdiciosBuscaNome(value);
                          setDesperdiciosProdutoSelecionado(null);
                        }}
                        onSelect={(opt) => {
                          const optionValue = String(opt.value);
                          setDesperdiciosProdutoSelecionado(optionValue.startsWith("produto:") ? Number(optionValue.slice("produto:".length)) : null);
                          setDesperdiciosBuscaNome(String(opt.label ?? opt.value));
                        }}
                        placeholder="Pesquisar responsável ou produto"
                        className="h-10 w-full rounded-md border border-gray-200 bg-white pl-9 shadow-sm"
                      />
                      </div>
                      <div className="flex w-full min-w-0 items-center gap-2 min-[767px]:w-auto min-[767px]:justify-end [&>div]:min-w-0 [&>div]:flex-1 min-[767px]:[&>div]:flex-none">
                        <LabeledFilterSelect
                          id="desperdicios-motivos"
                          label="Motivos"
                          labelBgClass="bg-white"
                          value={desperdiciosTipo}
                          onChange={(e) => setDesperdiciosTipo(e.target.value as typeof desperdiciosTipo)}
                          className="h-10 w-full min-w-0 rounded-md border border-gray-200 bg-white min-[767px]:w-[220px]"
                        >
                          <option value="todos">Todos</option>
                          <option value="consumo_interno">Consumo interno</option>
                          <option value="vencimento">Vencimento</option>
                          <option value="brinde_propaganda">Brinde/propaganda</option>
                          <option value="cortesia_avaliacao">Cortesia/avaliação</option>
                          <option value="avaria">Avaria</option>
                        </LabeledFilterSelect>
                        <PeriodFilterSelect
                          id="desperdicios-periodo"
                          labelBgClass="bg-white"
                          value={desperdiciosPeriodo}
                          onChange={(e) => {
                            const v = e.target.value as 'hoje' | 'ontem' | '7' | '30' | 'personalizado';
                            setDesperdiciosPeriodo(v);
                            if (v === 'personalizado') setShowModalDesperdiciosPeriodo(true);
                          }}
                          className="h-10 w-full min-w-0 rounded-md border border-gray-200 bg-white min-[767px]:w-[180px]"
                        />
                      </div>
                    </div>
                    <div className="w-full min-w-0">
                    <table className="gs-management-table w-full min-w-0 table-fixed text-sm">
                      <thead className={TABLE_HEAD_CLASS}>
                        <tr>
                          <th className={`w-[30%] min-w-0 text-left min-[767px]:w-[18%] ${TABLE_TH_CLASS}`}>Nome</th>
                          <th className={`hidden whitespace-nowrap text-left min-[767px]:table-cell min-[767px]:w-[14%] ${TABLE_TH_CLASS}`}>Data</th>
                          <th className={`hidden whitespace-nowrap text-left min-[767px]:table-cell min-[767px]:w-[12%] ${TABLE_TH_CLASS}`}>Horário</th>
                          <th className={`hidden whitespace-nowrap text-left min-[767px]:table-cell min-[767px]:w-[14%] ${TABLE_TH_CLASS}`}>Valor</th>
                          <th className={`min-w-0 text-left min-[767px]:w-[32%] ${TABLE_TH_CLASS}`}>Motivo</th>
                          <th className={`w-16 whitespace-nowrap text-right min-[767px]:w-[10%] ${TABLE_TH_CLASS}`}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {desperdiciosError ? (
                          <tr>
                            <td colSpan={6} className="p-4 text-sm text-destructive">{desperdiciosError}</td>
                          </tr>
                        ) : desperdiciosFiltrados.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="p-0 min-[767px]:hidden">
                              <div className="flex flex-col items-center px-4 py-4 text-center min-[767px]:py-6">
                                <img src="/imagens/erro.svg" alt="" className="mb-5 h-auto w-[180px] max-w-full object-contain min-[767px]:w-[240px]" />
                                <p className="gs-empty-state-message text-base font-semibold text-foreground">Nenhum registro localizado</p>
                              </div>
                            </td>
                            <td colSpan={6} className="hidden p-0 min-[767px]:table-cell">
                              <div className="flex flex-col items-center px-4 py-4 text-center min-[767px]:py-6">
                                <img src="/imagens/erro.svg" alt="" className="mb-5 h-auto w-[180px] max-w-full object-contain min-[767px]:w-[240px]" />
                                <p className="gs-empty-state-message text-base font-semibold text-foreground">Nenhum registro localizado</p>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                        {!desperdiciosError && desperdiciosFiltrados.map((d) => {
                          const expandido = expandedDesperdicioId === d.id;
                          const detalhesDesperdicio = d.itens && d.itens.length > 0 ? (
                            <ul className="list-none space-y-1">
                              {d.itens.map((it, idx) => (
                                <li key={idx} className="whitespace-normal break-words">
                                  {formatDisplayText(it.nome_produto)} — {Number(it.quantidade)} un
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="whitespace-normal break-words text-muted-foreground">
                              {d.tipo_item === "carrinho" ? "Nenhum item detalhado." : "Registro antigo (sem lista de itens)."}
                            </p>
                          );
                          const toggleExpandido = () => {
                            setDesperdicioActionMenu(null);
                            setExpandedDesperdicioId(expandido ? null : d.id);
                          };
                          return (
                          <React.Fragment key={d.id}>
                            <tr
                              role="button"
                              tabIndex={0}
                              className={`border-t border-gray-200 hover:bg-muted/30 cursor-pointer ${expandido ? "bg-muted/40" : ""}`}
                              onClick={toggleExpandido}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpandido(); } }}
                            >
                              <td className="min-w-0 font-medium"><span className="block min-w-0 whitespace-normal break-words">{d.responsavel_nome ? formatDisplayText(d.responsavel_nome) : "—"}</span></td>
                              <td className="hidden text-muted-foreground min-[767px]:table-cell">{formatarData(parseDateTimeLocal(d.createdAt))}</td>
                              <td className="hidden text-muted-foreground min-[767px]:table-cell">{formatarHora(parseDateTimeLocal(d.createdAt))}</td>
                              <td className="hidden font-medium min-[767px]:table-cell">{formatBRL(Number(d.total))}</td>
                              <td className="min-w-0 text-muted-foreground"><span className="block min-w-0 whitespace-normal break-words">
                                {getMotivoLabel(d.motivo)}
                                {d.observacao ? ` · ${formatDisplayText(d.observacao)}` : ""}
                              </span></td>
                              <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label={`Ações do desperdício de ${d.responsavel_nome ? formatDisplayText(d.responsavel_nome) : "registro"}`}
                                  onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                    setDesperdicioActionMenu((cur) => (cur?.id === d.id ? null : { id: d.id, rect }));
                                  }}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                            {expandido && (
                              <tr className="bg-muted/20">
                                <td colSpan={3} className="p-0 text-sm min-[767px]:hidden">
                                  <div className="!px-2 !py-2.5">{detalhesDesperdicio}</div>
                                </td>
                                <td colSpan={6} className="hidden p-0 text-sm min-[767px]:table-cell">
                                  <div className="!px-4 !py-2.5">{detalhesDesperdicio}</div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {desperdicioActionMenu
                ? (() => {
                    const d = desperdiciosFiltrados.find((x) => x.id === desperdicioActionMenu.id);
                    if (!d) return null;
                    const expandido = expandedDesperdicioId === d.id;
                    return (
                      <ActionMenuPortal
                        open
                        anchorRect={desperdicioActionMenu.rect}
                        onClose={() => setDesperdicioActionMenu(null)}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setExpandedDesperdicioId(expandido ? null : d.id);
                            setDesperdicioActionMenu(null);
                          }}
                        >
                          <Eye className="w-4 h-4" /> Ver itens
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                            onClick={async () => {
                              setDesperdicioActionMenu(null);
                              if (!confirm("Deseja desfazer este desperdício? O estoque será reposto.")) return;
                              try {
                                await api.excluirDesperdicio(d.id);
                                await carregarDesperdicios();
                                if (expandedDesperdicioId === d.id) setExpandedDesperdicioId(null);
                                toast.success("Desperdício desfeito.");
                              } catch (err) {
                                toast.error((err as Error).message || "Erro ao desfazer.");
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" /> Excluir
                          </button>
                        )}
                      </ActionMenuPortal>
                    );
                  })()
                : null}
            </div>
            )}
          </TabsContent>

          {/* Aba Produtos */}
          {canViewTab('produtos') && (
            <TabsContent value="produtos" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {produtosLoading ? (
                <PageContentLoader />
              ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:max-w-full sm:min-w-0 sm:items-center sm:justify-between sm:gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className={PAGE_TITLE_CLASS}>Produtos</h2>
                    <Button
                      type="button"
                      onClick={abrirModalNovoProduto}
                      className={`${BTN_PAGE_PRIMARY_ACTION} sm:hidden`}
                      aria-label="Adicionar produto"
                    >
                      <Plus className="h-4 w-4 shrink-0" />
                      Novo
                    </Button>
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-0 sm:shrink-0 sm:flex-row sm:flex-nowrap sm:items-end sm:justify-end sm:gap-3">
                    <div className="w-full sm:w-auto [&>div]:w-full">
                      <CategoryLabeledFilterSelect
                        id="produtos-categoria"
                        value={produtosCategoriasFiltro}
                        onChange={setProdutosCategoriasFiltro}
                        className="h-10 w-full max-w-none sm:w-[220px] sm:max-w-[220px]"
                      >
                        <option value="">Todos</option>
                        {produtosCategoriasOpcoes.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </CategoryLabeledFilterSelect>
                    </div>
                    <div className="w-full sm:w-auto [&>div]:w-full">
                      <LabeledFilterSelect
                        id="produtos-ordenacao"
                        label="Ordenar por"
                        value={produtosOrdenacao}
                        onChange={(e) => setProdutosOrdenacao(e.target.value as ProdutosOrdenacao)}
                        className="h-10 w-full max-w-none sm:w-[220px] sm:max-w-[220px]"
                      >
                        <option value="nome-az">Nome A-Z</option>
                        <option value="nome-za">Nome Z-A</option>
                        <option value="mais-vendidos">Mais Vendidos</option>
                        <option value="menos-vendidos">Menos Vendidos</option>
                      </LabeledFilterSelect>
                    </div>
                    <Button
                      type="button"
                      onClick={abrirModalNovoProduto}
                      className={`${BTN_PAGE_PRIMARY_ACTION} hidden sm:inline-flex`}
                      aria-label="Adicionar produto"
                    >
                      <Plus className="h-4 w-4 shrink-0" />
                      Adicionar Produto
                    </Button>
                  </div>
                </div>

                {produtosLista.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-white py-16 text-center shadow-sm">
                    <Package className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
                    <p className="text-muted-foreground">Nenhum produto cadastrado.</p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="gs-admin-table-toolbar border-b border-gray-100">
                      <TableSearchInput
                        value={searchProdutosTab}
                        onValueChange={setSearchProdutosTab}
                      />
                    </div>
                    {produtosTabFiltrados.length === 0 ? (
                      <div className="py-12 text-center text-muted-foreground">Nenhum produto corresponde ao filtro.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="gs-management-table w-full min-w-[780px] text-sm">
                          <thead className={TABLE_HEAD_CLASS}>
                            <tr className="border-b border-gray-200">
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Produto</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Custo</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Valor</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Categoria</th>
                              <th className={`w-14 px-4 py-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {produtosTabFiltrados.map((produto) => (
                              <tr key={produto.id} className="border-b border-gray-100 last:border-0 hover:bg-muted/20">
                                <td className="px-4 py-3 font-medium text-foreground">{produto.nome}</td>
                                <td className="px-4 py-3 tabular-nums">{formatProdutoCustoExibicao(produto.valorAtual)}</td>
                                <td className="px-4 py-3 tabular-nums">{formatBRL(produto.preco_venda)}</td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex max-w-full rounded-full px-2.5 py-0.5 text-xs font-medium ${categoriaInsumoTagClass(produto.categoria || "")}`}
                                  >
                                    {produto.categoria?.trim() ? formatDisplayText(produto.categoria.trim()) : "—"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label="Ações do produto"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setProdutoActionMenu({ id: produto.id, rect: e.currentTarget.getBoundingClientRect() });
                                    }}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </TabsContent>
          )}

          {/* Aba Insumos */}
          {canViewTab('insumos') && (
            <TabsContent value="insumos" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {loadingInsumosTab ? (
                <PageContentLoader />
              ) : (
              <div className="space-y-4">
                <div className="flex max-w-full min-w-0 flex-nowrap items-center justify-between gap-2">
                  <h2 className={PAGE_TITLE_CLASS}>Insumos</h2>
                  <div className="flex min-w-0 shrink-0 flex-nowrap items-end justify-end gap-2 sm:gap-3">
                    <CategoryLabeledFilterSelect
                      id="insumos-categoria"
                      value={insumosTabCategoriasFiltro}
                      onChange={setInsumosTabCategoriasFiltro}
                      className="h-10 min-w-0 max-w-[min(220px,calc(100vw-9rem))] sm:w-[220px]"
                    >
                      <option value="">Todos</option>
                      {insumosCategoriasOpcoes.map((nome) => (
                        <option key={nome} value={nome}>
                          {nome}
                        </option>
                      ))}
                    </CategoryLabeledFilterSelect>
                    <Button
                      type="button"
                      onClick={abrirModalNovoInsumo}
                      className={BTN_PAGE_PRIMARY_ACTION}
                      aria-label="Adicionar insumo"
                    >
                      <Plus className="h-4 w-4 shrink-0" />
                      <span className="sm:hidden">Novo</span>
                      <span className="hidden sm:inline">Adicionar Insumo</span>
                    </Button>
                  </div>
                </div>

                {insumosTabList.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-white py-16 text-center shadow-sm">
                    <Layers className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
                    <p className="text-muted-foreground">Nenhum insumo cadastrado.</p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="gs-admin-table-toolbar border-b border-gray-100">
                      <TableSearchInput
                        value={searchInsumosTab}
                        onValueChange={setSearchInsumosTab}
                      />
                    </div>
                    {insumosTabFiltrados.length === 0 ? (
                      <div className="py-12 text-center text-muted-foreground">Nenhum insumo corresponde ao filtro.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="gs-management-table w-full min-w-[680px] text-sm">
                          <thead className={TABLE_HEAD_CLASS}>
                            <tr className="border-b border-gray-200">
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Insumo</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Custo</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Medida</th>
                              <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Categoria</th>
                              <th className={`w-14 px-4 py-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {insumosTabFiltrados.map((insumo) => {
                              const u = (insumo.unidade || "").trim();
                              return (
                                <tr key={insumo.id} className="border-b border-gray-100 last:border-0 hover:bg-muted/20">
                                  <td className="px-4 py-3 font-medium text-foreground">{formatDisplayText(insumo.nome)}</td>
                                  <td className="px-4 py-3 tabular-nums text-foreground">
                                    {insumo.valorAtual != null ? formatBRL(insumo.valorAtual) : "—"}
                                  </td>
                                  <td className="px-4 py-3">{u || "—"}</td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`inline-flex max-w-full rounded-full px-2.5 py-0.5 text-xs font-medium ${categoriaInsumoTagClass(insumo.categoria || "")}`}
                                    >
                                      {insumo.categoria ? formatDisplayText(insumo.categoria) : "—"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label="Ações do insumo"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInsumoActionMenu({ id: insumo.id, rect: e.currentTarget.getBoundingClientRect() });
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </TabsContent>
          )}

          {/* Aba Financeiro (unifica Vendas + Despesas) */}
          {canViewTab('financeiro') && (
            <TabsContent value="financeiro" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {(financeiroSubTab === "vendas" ? loadingVendas : loadingDespesasFinanceiro) ? (
                <PageContentLoader />
              ) : (
              <div className="flex flex-col gap-4">
                {/* 1) Título da aba + período */}
                <div className="flex max-w-full min-w-0 shrink-0 flex-nowrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <h2 className={PAGE_TITLE_CLASS}>Financeiro</h2>
                    <button
                      type="button"
                      className={`${KPI_VALUE_EYE_BTN} hidden min-[767px]:inline-flex`}
                      onClick={() => setOcultarValoresFinanceiro((current) => {
                        const ocultarTodos = !Object.values(current).every(Boolean);
                        return { vendas: ocultarTodos, despesas: ocultarTodos, compras: ocultarTodos, outros: ocultarTodos };
                      })}
                      title={Object.values(ocultarValoresFinanceiro).every(Boolean) ? "Mostrar valores" : "Ocultar valores"}
                      aria-label={Object.values(ocultarValoresFinanceiro).every(Boolean) ? "Mostrar valores financeiros" : "Ocultar valores financeiros"}
                    >
                      {Object.values(ocultarValoresFinanceiro).every(Boolean)
                        ? <EyeOff className="w-5 h-5" />
                        : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="flex min-w-0 shrink-0 items-center">
                    <PeriodFilterSelect
                      id="financeiro-periodo"
                      value={financeiroPeriodo}
                      onChange={(e) => {
                        const v = e.target.value as 'hoje' | 'ontem' | '7' | '30' | 'personalizado';
                        setFinanceiroPeriodo(v);
                        if (v === 'personalizado') setShowModalPeriodoPersonalizadoFinanceiro(true);
                      }}
                      className="h-10 w-[min(180px,calc(100vw-10rem))] min-w-[120px] max-w-[180px] sm:w-[180px]"
                    />
                  </div>
                </div>

                {/* 2) Cards de resumo conforme sub-aba */}
                <div className="grid w-full max-w-full shrink-0 grid-cols-1 gap-3 min-[767px]:grid-cols-4">
                  <FinanceiroResumoCard
                    title="Vendas"
                    loading={loadingVendas}
                    value={financeiroTotalVendasCard}
                    ocultarValores={ocultarValoresFinanceiro.vendas}
                    onToggleOcultar={() => setOcultarValoresFinanceiro((current) => ({ ...current, vendas: !current.vendas }))}
                    className="w-full"
                    compact
                  />
                  <FinanceiroResumoCard
                    title="Despesas"
                    loading={loadingDespesasFinanceiro}
                    value={financeiroTotalDespesasCard}
                    ocultarValores={ocultarValoresFinanceiro.despesas}
                    onToggleOcultar={() => setOcultarValoresFinanceiro((current) => ({ ...current, despesas: !current.despesas }))}
                    className="w-full"
                    compact
                  />
                  <FinanceiroResumoCard
                    title="Compras"
                    loading={loadingDespesasFinanceiro}
                    value={financeiroTotalComprasCard}
                    ocultarValores={ocultarValoresFinanceiro.compras}
                    onToggleOcultar={() => setOcultarValoresFinanceiro((current) => ({ ...current, compras: !current.compras }))}
                    className="w-full"
                    compact
                  />
                  <FinanceiroResumoCard
                    title="Outros"
                    loading={loadingDespesasFinanceiro}
                    value={financeiroTotalOutrosCard}
                    ocultarValores={ocultarValoresFinanceiro.outros}
                    onToggleOcultar={() => setOcultarValoresFinanceiro((current) => ({ ...current, outros: !current.outros }))}
                    className="w-full"
                    compact
                  />
                </div>

                {/* 3) Sub-abas (sempre abaixo do card) */}
                <div className="mt-2 min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div
                  className="gs-financeiro-tabs-scroll shrink-0 overflow-x-auto border-b border-gray-200"
                  role="tablist"
                  aria-label="Sub-abas do financeiro"
                >
                  <div className="gs-financeiro-tabs flex min-h-[3.5rem] min-w-max flex-nowrap items-center justify-start gap-1 px-2 sm:px-4">
                    {(
                      [
                        { id: "vendas" as const, label: "Vendas" },
                        { id: "despesas" as const, label: "Despesas" },
                        { id: "compras" as const, label: "Compras" },
                        { id: "outros" as const, label: "Outros" },
                      ] as const
                    ).map(({ id, label }) => {
                      const active = financeiroSubTab === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => goToTab("financeiro", { financeiroSubTab: id })}
                          className="gs-financeiro-tab relative inline-flex shrink-0 px-2 focus:outline-none focus-visible:outline-none sm:px-5"
                        >
                          <span
                            className={`whitespace-nowrap text-sm ${
                              active ? "font-semibold text-foreground" : "font-medium text-muted-foreground"
                            }`}
                          >
                            {label}
                          </span>
                          {active ? (
                            <span
                              className="pointer-events-none absolute inset-x-0 top-full mt-1.5 h-0.5 rounded-full"
                              style={{ backgroundColor: clientConfig.primary_color }}
                            />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 4) Conteúdo da sub-aba */}
                <div className="min-h-0 min-w-0 flex flex-col gap-0">
                {financeiroSubTab === 'vendas' && (
                  loadingVendas ? (
                    <PageContentLoader />
                  ) : (
                  <div className="space-y-0">
                    <div className="gs-admin-table-toolbar gs-financeiro-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
                      <TableSearchInput
                        value={searchFinanceiroVendas}
                        onValueChange={setSearchFinanceiroVendas}
                        className={FINANCEIRO_SEARCH_WIDTH_CLASS}
                      />
                      <Button
                        type="button"
                        className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                        onClick={abrirModalExportarVendasFiscal}
                      >
                        <Download className="h-4 w-4 shrink-0" /> Exportar
                      </Button>
                    </div>
                  <div ref={financeiroVendasListRef} className="min-w-0">
                      <div className="gs-financial-sales-scroll">
                        <table className="gs-management-table gs-financial-sales-table w-full min-w-[560px] text-sm">
                          <thead className={TABLE_HEAD_CLASS}>
                            <tr>
                              <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS} gs-sales-time`}>Horário</th>
                              <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Data</th>
                              <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>
                                <span className="gs-sales-value-desktop">Valor Recebido</span>
                                <span className="gs-sales-value-mobile">Valor</span>
                              </th>
                              <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS} gs-sales-payment`}>Pagamento</th>
                              <th className={`whitespace-nowrap text-right py-3 px-3 ${TABLE_TH_CLASS} gs-sales-actions`}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendasListaFiltradas.length === 0 ? (
                              <>
                                <FinanceiroEmptyRow colSpan={3} className="gs-sales-empty-mobile" />
                                <FinanceiroEmptyRow colSpan={5} className="gs-sales-empty-desktop" />
                              </>
                            ) : vendasListaFiltradas.map((v) => {
                              const subtotalVenda = (v.itens ?? []).reduce((s, i) => s + Number(i.subtotal ?? 0), 0);
                              const totalFinal = Number(v.total);
                              const ajustes = v.pagamento?._ajustes;
                              const subtotalAjustado = Number(ajustes?.subtotal_base ?? subtotalVenda);
                              const descontoValor = Number(ajustes?.desconto_valor ?? 0);
                              const descontoPct = Number(ajustes?.desconto_percentual ?? 0);
                              const acrescimoValor = Number(ajustes?.acrescimo_valor ?? 0);
                              const acrescimoPct = Number(ajustes?.acrescimo_percentual ?? 0);
                              const temDesconto = Boolean(ajustes) && (descontoPct > 0 || descontoValor > 0.005);
                              const temAcrescimo = Boolean(ajustes) && (acrescimoPct > 0 || acrescimoValor > 0.005);
                              const temDetalhamento = temDesconto || temAcrescimo;
                              const percentualLabel = (value: number) => value.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
                              return (
                              <React.Fragment key={v.id}>
                                <tr
                                  role="button"
                                  tabIndex={0}
                                  className={`gs-sales-record-row border-t border-gray-200 hover:bg-muted/30 cursor-pointer ${expandedVendaId === v.id ? "bg-muted/40" : ""}`}
                                  onClick={() => setExpandedVendaId((current) => current === v.id ? null : v.id)}
                                  onKeyDown={(e) => {
                                    if ((e.target as HTMLElement).closest("button, a, input, select, textarea, [role='menu']")) return;
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setExpandedVendaId((current) => current === v.id ? null : v.id);
                                    }
                                  }}
                                >
                                  <td className="py-2 px-3 gs-sales-time">{formatarHora(parseDateTimeLocal(v.createdAt))}</td>
                                  <td className="py-2 px-3">{formatarData(parseDateTimeLocal(v.createdAt))}</td>
                                  <td className="py-2 px-3 font-medium">{formatBRL(totalFinal)}</td>
                                  <td className="py-2 px-3 gs-sales-payment">
                                    {[
                                      v.pagamento?.pix ? "Pix" : null,
                                      v.pagamento?.dinheiro ? "Dinheiro" : null,
                                      v.pagamento?.credito ? "Crédito" : null,
                                      v.pagamento?.debito ? "Débito" : null,
                                    ].filter(Boolean).join(" • ") || "—"}
                                  </td>
                                  <td className="py-2 px-3 text-right relative gs-sales-actions" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        const id = v.id as number;
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setVendaActionMenu((cur) => (cur?.id === id ? null : { id, rect }));
                                      }}
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                                {expandedVendaId === v.id && (
                                  <tr className="bg-muted/20">
                                    <td colSpan={5} className="gs-sales-expanded-cell">
                                      <div className="gs-sales-expanded-content">
                                        {v.itens?.length ? <div className="space-y-1 break-words">
                                          {v.itens.map((item, idx) => (
                                            <div key={idx} className="min-w-0 break-words">
                                              {formatDisplayText(item.nome_produto)} — {item.quantidade} x {formatBRL(Number(item.preco_unitario))} = {formatBRL(Number(item.subtotal))}
                                            </div>
                                          ))}
                                        </div> : <p className="text-muted-foreground">Nenhum item nesta venda.</p>}
                                        {temDetalhamento && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-gray-200 pt-3 text-sm">
                                          <span><strong>Subtotal:</strong> {formatBRL(subtotalAjustado)}</span>
                                          {temDesconto && <span><strong>Desconto{descontoPct > 0 ? ` (${percentualLabel(descontoPct)}%)` : ""}:</strong> −{formatBRL(descontoValor)}</span>}
                                          {temAcrescimo && <span><strong>Acréscimo{acrescimoPct > 0 ? ` (${percentualLabel(acrescimoPct)}%)` : ""}:</strong> +{formatBRL(acrescimoValor)}</span>}
                                        </div>}
                                        <div className="mt-2 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-t border-gray-200 pt-2 text-sm">
                                          <strong>Total: {formatBRL(totalFinal)}</strong>
                                          {v.atendimento?.identificacao && <span className="min-w-0 break-words">{v.atendimento.identificacao}</span>}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    {vendaActionMenu
                      ? (() => {
                          const v = vendasLista.find((x) => x.id === vendaActionMenu.id);
                          if (!v) return null;
                          return (
                            <ActionMenuPortal
                              open
                              anchorRect={vendaActionMenu.rect}
                              onClose={() => setVendaActionMenu(null)}
                            >
                              <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => {
                                  setExpandedVendaId((current) => current === v.id ? null : v.id);
                                  setVendaActionMenu(null);
                                }}
                              >
                                <Eye className="w-4 h-4" /> Ver itens
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                                  onClick={async () => {
                                    if (!confirm("Tem certeza que deseja excluir esta venda? Os itens vendidos serão retornados ao estoque.")) {
                                      setVendaActionMenu(null);
                                      return;
                                    }
                                    try {
                                      setDeletingVendaId(v.id as number);
                                      await api.excluirVenda(v.id as number);
                                      await aplicarPeriodoFinanceiro();
                                    } catch (e) {
                                      console.error(e);
                                      toast.error("Erro ao excluir venda");
                                    } finally {
                                      setDeletingVendaId(null);
                                      setVendaActionMenu(null);
                                    }
                                  }}
                                >
                                  {deletingVendaId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                  Excluir
                                </button>
                              )}
                              <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => handleImprimirVenda(v)}
                              >
                                <Printer className="w-4 h-4" /> Imprimir
                              </button>
                            </ActionMenuPortal>
                          );
                        })()
                      : null}
                  </div>
                  </div>
                  )
                )}

                {financeiroSubTab === "despesas" && (
                  loadingDespesasFinanceiro ? (
                    <PageContentLoader />
                  ) : (
                  <div className="space-y-0">
                    <div className="gs-admin-table-toolbar gs-financeiro-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
                      <TableSearchInput
                        value={searchFinanceiroDespesas}
                        onValueChange={setSearchFinanceiroDespesas}
                        className={FINANCEIRO_SEARCH_WIDTH_CLASS}
                      />
                      <div className="gs-financeiro-toolbar-actions flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        onClick={() => setModalImportOfxFinanceiro(true)}
                        className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                      >
                        <Upload className="h-4 w-4 shrink-0" /> Importar Extrato
                      </Button>
                      <Button
                        type="button"
                        className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                        onClick={() => setModalAddDespesaFinanceiro(true)}
                      >
                        Adicionar Despesa
                      </Button>
                      </div>
                    </div>
                    <div className="overflow-hidden">
                        <div className="gs-financeiro-table-scroll overflow-x-auto">
                          <table className="gs-management-table gs-financeiro-lancamentos-table gs-financeiro-editable-mobile-lines w-full min-w-[46rem] table-fixed text-sm">
                            <colgroup>
                              <col className="gs-financeiro-description-col min-w-0" style={{ width: "42%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "20%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-value-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-actions-col" style={{ width: "10%" }} />
                            </colgroup>
                            <thead className={TABLE_HEAD_CLASS}>
                              <tr>
                                <th className={`max-w-0 overflow-hidden py-2.5 pl-3 pr-2 text-left ${TABLE_TH_CLASS}`}>
                                  Descrição
                                </th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Saída</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Data</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Valor</th>
                                <th className={`py-2.5 pl-1 pr-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {despesasFinanceiroSoDespesasFiltradas.length === 0 ? <FinanceiroEmptyRow colSpan={5} /> : despesasFinanceiroSoDespesasFiltradas.map((d) => (
                                <tr key={d.id} className="border-t border-gray-200 hover:bg-muted/30">
                                  <td className="max-w-0 min-w-0 overflow-hidden py-2 pl-3 pr-2 align-middle">
                                    <FinanceiroDescricaoComOrigem despesa={d} onSaved={() => void carregarDespesasFinanceiro()} />
                                  </td>
                                  <td className="px-1 py-2 align-middle">
                                    <div className="flex justify-start">
                                      <FinanceiroSaidaSelect
                                        despesa={d}
                                        disabled={financeiroSaidaUpdatingId === d.id}
                                        onChange={handleFinanceiroSaidaChange}
                                      />
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums text-muted-foreground">
                                    {d.data ? formatarData(d.data) : "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums">
                                    {formatBRL(d.valor)}
                                  </td>
                                  <td className="relative py-2 pl-1 pr-3 text-right align-middle">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setDespesaActionMenu((cur) => (cur?.id === d.id ? null : { id: d.id, rect }));
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                    </div>
                  </div>
                  )
                )}

                {financeiroSubTab === "compras" && (
                  loadingDespesasFinanceiro ? (
                    <PageContentLoader />
                  ) : (
                  <div className="space-y-0">
                    <div className="gs-admin-table-toolbar gs-financeiro-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
                      <TableSearchInput
                        value={searchFinanceiroCompras}
                        onValueChange={setSearchFinanceiroCompras}
                        className={FINANCEIRO_SEARCH_WIDTH_CLASS}
                      />
                      <div className="gs-financeiro-toolbar-actions flex flex-wrap gap-2">
                        <Button
                          type="button"
                          className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                          onClick={() => setModalImportOfxFinanceiro(true)}
                        >
                          <Upload className="h-4 w-4 shrink-0" /> Importar Extrato
                        </Button>
                        <Button
                          type="button"
                          className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                          onClick={() => setModalAddCompraFinanceiro(true)}
                        >
                          Adicionar Compra
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-hidden">
                        <div className="gs-financeiro-table-scroll overflow-x-auto">
                          <table className="gs-management-table gs-financeiro-lancamentos-table gs-financeiro-editable-mobile-lines w-full min-w-[46rem] table-fixed text-sm">
                            <colgroup>
                              <col className="gs-financeiro-description-col min-w-0" style={{ width: "42%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "20%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-value-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-actions-col" style={{ width: "10%" }} />
                            </colgroup>
                            <thead className={TABLE_HEAD_CLASS}>
                              <tr>
                                <th className={`max-w-0 overflow-hidden py-2.5 pl-3 pr-2 text-left ${TABLE_TH_CLASS}`}>
                                  Descrição
                                </th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Saída</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Data</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Valor</th>
                                <th className={`py-2.5 pl-1 pr-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {despesasFinanceiroSoComprasFiltradas.length === 0 ? <FinanceiroEmptyRow colSpan={5} /> : despesasFinanceiroSoComprasFiltradas.map((d) => (
                                <tr key={d.id} className="border-t border-gray-200 hover:bg-muted/30">
                                  <td className="max-w-0 min-w-0 overflow-hidden py-2 pl-3 pr-2 align-middle">
                                    <FinanceiroDescricaoComOrigem despesa={d} onSaved={() => void carregarDespesasFinanceiro()} />
                                  </td>
                                  <td className="px-1 py-2 align-middle">
                                    <div className="flex justify-start">
                                      <FinanceiroSaidaSelect
                                        despesa={d}
                                        disabled={financeiroSaidaUpdatingId === d.id}
                                        onChange={handleFinanceiroSaidaChange}
                                      />
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums text-muted-foreground">
                                    {d.data ? formatarData(d.data) : "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums">
                                    {formatBRL(d.valor)}
                                  </td>
                                  <td className="relative py-2 pl-1 pr-3 text-right align-middle">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setDespesaActionMenu((cur) => (cur?.id === d.id ? null : { id: d.id, rect }));
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                    </div>
                  </div>
                  )
                )}
                {financeiroSubTab === "outros" && (
                  loadingDespesasFinanceiro ? (
                    <PageContentLoader />
                  ) : (
                  <div className="space-y-0">
                    <div className="gs-admin-table-toolbar gs-financeiro-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
                      <TableSearchInput
                        value={searchFinanceiroOutros}
                        onValueChange={setSearchFinanceiroOutros}
                        className={FINANCEIRO_SEARCH_WIDTH_CLASS}
                      />
                      <div className="gs-financeiro-toolbar-actions flex flex-wrap gap-2">
                        <Button
                          type="button"
                          className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                          onClick={() => setModalImportOfxFinanceiro(true)}
                        >
                          <Upload className="h-4 w-4 shrink-0" /> Importar Extrato
                        </Button>
                        <Button
                          type="button"
                          className={`${BTN_PAGE_PRIMARY_ACTION_ROW} !px-4 !py-1.5`}
                          onClick={() => setModalAddOutrosFinanceiro(true)}
                        >
                          Adicionar Outro
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-hidden">
                        <div className="gs-financeiro-table-scroll overflow-x-auto">
                          <table className="gs-management-table gs-financeiro-lancamentos-table gs-financeiro-editable-mobile-lines w-full min-w-[46rem] table-fixed text-sm">
                            <colgroup>
                              <col className="gs-financeiro-description-col min-w-0" style={{ width: "42%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "20%" }} />
                              <col className="gs-financeiro-hidden-mobile-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-value-col" style={{ width: "14%" }} />
                              <col className="gs-financeiro-actions-col" style={{ width: "10%" }} />
                            </colgroup>
                            <thead className={TABLE_HEAD_CLASS}>
                              <tr>
                                <th className={`max-w-0 overflow-hidden py-2.5 pl-3 pr-2 text-left ${TABLE_TH_CLASS}`}>
                                  Descrição
                                </th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Saída</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Data</th>
                                <th className={`px-1 py-2.5 text-left ${TABLE_TH_CLASS}`}>Valor</th>
                                <th className={`py-2.5 pl-1 pr-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {despesasFinanceiroSoOutrosFiltradas.length === 0 ? <FinanceiroEmptyRow colSpan={5} /> : despesasFinanceiroSoOutrosFiltradas.map((d) => (
                                <tr key={d.id} className="border-t border-gray-200 hover:bg-muted/30">
                                  <td className="max-w-0 min-w-0 overflow-hidden py-2 pl-3 pr-2 align-middle">
                                    <FinanceiroDescricaoComOrigem despesa={d} onSaved={() => void carregarDespesasFinanceiro()} />
                                  </td>
                                  <td className="px-1 py-2 align-middle">
                                    <div className="flex justify-start">
                                      <FinanceiroSaidaSelect
                                        despesa={d}
                                        disabled={financeiroSaidaUpdatingId === d.id}
                                        onChange={handleFinanceiroSaidaChange}
                                      />
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums text-muted-foreground">
                                    {d.data ? formatarData(d.data) : "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-1 py-2 align-middle tabular-nums">
                                    {formatBRL(d.valor)}
                                  </td>
                                  <td className="relative py-2 pl-1 pr-3 text-right align-middle">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setDespesaActionMenu((cur) => (cur?.id === d.id ? null : { id: d.id, rect }));
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                    </div>
                  </div>
                  )
                )}

                </div>
                </div>

                {despesaActionMenu
                  ? (() => {
                      const d = despesasFinanceiro.find((x) => x.id === despesaActionMenu.id);
                      if (!d) return null;
                      return (
                        <ActionMenuPortal
                          open
                          anchorRect={despesaActionMenu.rect}
                          onClose={() => setDespesaActionMenu(null)}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setEditingDespesaFinanceiro(d);
                              setDespesaActionMenu(null);
                            }}
                          >
                            <Pencil className="w-4 h-4" /> Editar
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                            onClick={() => {
                              setDeleteConfirmDespesaId(d.id);
                              setDespesaActionMenu(null);
                            }}
                          >
                            <Trash2 className="w-4 h-4" /> Excluir
                          </button>
                        </ActionMenuPortal>
                      );
                    })()
                  : null}
              </div>
              )}

              <ModalPeriodoPersonalizado
                open={showModalPeriodoPersonalizadoFinanceiro}
                onClose={() => setShowModalPeriodoPersonalizadoFinanceiro(false)}
                inicio={financeiroPersonalizadoInicio}
                fim={financeiroPersonalizadoFim}
                onInicioChange={setFinanceiroPersonalizadoInicio}
                onFimChange={setFinanceiroPersonalizadoFim}
                onApply={() => {
                  if (financeiroPersonalizadoInicio && financeiroPersonalizadoFim) {
                    setShowModalPeriodoPersonalizadoFinanceiro(false);
                    aplicarPeriodoFinanceiro();
                  } else toast.error("Informe data início e fim.");
                }}
              />

              <ModalExportarVendasFiscal
                open={showModalExportarVendasFiscal}
                initialInicio={exportarVendasInicio}
                initialFim={exportarVendasFim}
                onClose={() => setShowModalExportarVendasFiscal(false)}
                onExport={handleExportarVendasFiscal}
              />

              {modalAddDespesaFinanceiro && (
                <ModalAddDespesaFinanceiro
                  onClose={() => setModalAddDespesaFinanceiro(false)}
                  onSuccess={(saida) => {
                    setModalAddDespesaFinanceiro(false);
                    void carregarDespesasFinanceiro();
                    goToTab("financeiro", { financeiroSubTab: financeiroTabForSaida(saida) });
                  }}
                />
              )}
              <ModalImportarOfxFinanceiro
                open={modalImportOfxFinanceiro}
                onClose={() => setModalImportOfxFinanceiro(false)}
                defaultSaida={financeiroSubTab === "compras" ? "compra" : financeiroSubTab === "outros" ? "outro" : "despesa"}
                onSuccess={() => {
                  setModalImportOfxFinanceiro(false);
                  void carregarDespesasFinanceiro();
                }}
              />
              {modalAddCompraFinanceiro && (
                <ModalAddCompraFinanceiro
                  onClose={() => setModalAddCompraFinanceiro(false)}
                  onSuccess={(saida) => {
                    setModalAddCompraFinanceiro(false);
                    void carregarDespesasFinanceiro();
                    goToTab("financeiro", { financeiroSubTab: financeiroTabForSaida(saida) });
                  }}
                />
              )}
              {modalAddOutrosFinanceiro && (
                <ModalAddOutrosFinanceiro
                  onClose={() => setModalAddOutrosFinanceiro(false)}
                  onSuccess={(saida) => {
                    setModalAddOutrosFinanceiro(false);
                    void carregarDespesasFinanceiro();
                    goToTab("financeiro", { financeiroSubTab: financeiroTabForSaida(saida) });
                  }}
                />
              )}
              {editingDespesaFinanceiro && (
                <ModalEditDespesaFinanceiro
                  despesa={editingDespesaFinanceiro}
                  onClose={() => setEditingDespesaFinanceiro(null)}
                  onSuccess={(saida) => {
                    setEditingDespesaFinanceiro(null);
                    void carregarDespesasFinanceiro();
                    goToTab("financeiro", { financeiroSubTab: financeiroTabForSaida(saida) });
                  }}
                />
              )}
              {deleteConfirmDespesaId !== null && (
                <ModalExcluirDespesaFinanceiro
                  onClose={() => setDeleteConfirmDespesaId(null)}
                  onConfirm={async () => {
                    await api.excluirDespesa(deleteConfirmDespesaId);
                    setDeleteConfirmDespesaId(null);
                    toast.success("Despesa excluída.");
                    void carregarDespesasFinanceiro();
                  }}
                />
              )}
            </TabsContent>
          )}

          {/* Aba Conferência */}
          {canViewTab('conferencia') && (
            <TabsContent value="conferencia" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {loadingConferencias ? (
                <PageContentLoader />
              ) : (
              <div className="space-y-4">
                <div className="flex max-w-full min-w-0 flex-nowrap items-center justify-between gap-2">
                  <h2 className={PAGE_TITLE_CLASS}>Conferência</h2>
                  <div className="flex min-w-0 shrink-0 items-center">
                    <PeriodFilterSelect
                      id="conferencia-periodo"
                      value={conferenciaPeriodo}
                      onChange={(e) => {
                        const v = e.target.value as 'hoje' | 'ontem' | '7' | '30' | 'personalizado';
                        setConferenciaPeriodo(v);
                        if (v === 'personalizado') setShowModalPeriodoPersonalizadoConferencia(true);
                      }}
                      className="h-10 w-[min(180px,calc(100vw-10rem))] min-w-[120px] max-w-[180px] sm:w-[180px]"
                    />
                  </div>
                </div>

                <div>
                  <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="gs-management-table w-full min-w-[40rem] table-fixed text-sm">
                        <colgroup>
                          <col style={{ width: "16%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "30%" }} />
                          <col style={{ width: "30%" }} />
                          <col style={{ width: "10%" }} />
                        </colgroup>
                        <thead className={TABLE_HEAD_CLASS}>
                          <tr>
                            <th className={`text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Data</th>
                            <th className={`text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Horário</th>
                            <th className={`text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Usuário</th>
                            <th className={`text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Alteração</th>
                            <th className={`text-right py-3 px-3 ${TABLE_TH_CLASS}`}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conferenciasLista.length === 0 ? <FinanceiroEmptyRow colSpan={5} /> : conferenciasLista.map((conf) => {
                            const totalAlteracoes = conf.alteracoes?.length ?? 0;
                            const expandido = expandedConferenciaId === conf.id;
                            const toggleExpandido = () => {
                              setConferenciaActionMenu(null);
                              setExpandedConferenciaId(expandido ? null : conf.id);
                            };
                            return (
                              <React.Fragment key={conf.id}>
                                <tr
                                  role="button"
                                  tabIndex={0}
                                  className={`border-t border-gray-200 hover:bg-muted/30 cursor-pointer ${expandido ? "bg-muted/20 border-b-0" : ""}`}
                                  onClick={toggleExpandido}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleExpandido();
                                    }
                                  }}
                                >
                                  <td className={`px-3 align-middle ${expandido ? "pt-3 pb-1" : "py-3"}`}>
                                    {formatarDataLocal(parseMySQLDateLocal(conf.data_conferencia))}
                                  </td>
                                  <td className={`px-3 align-middle ${expandido ? "pt-3 pb-1" : "py-3"}`}>
                                    {formatarHoraLocal(parseMySQLDateLocal(conf.data_conferencia))}
                                  </td>
                                  <td className={`px-3 align-middle ${expandido ? "pt-3 pb-1" : "py-3"}`}>
                                    {conf.usuario_nome ? formatDisplayText(conf.usuario_nome) : "—"}
                                  </td>
                                  <td className={`px-3 align-middle ${expandido ? "pt-3 pb-1" : "py-3"}`}>
                                    {String(totalAlteracoes).padStart(2, "0")} Alterações
                                  </td>
                                  <td
                                    className={`px-3 text-right align-middle ${expandido ? "pt-3 pb-1" : "py-3"}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label={`Ações da conferência de ${formatarDataLocal(parseMySQLDateLocal(conf.data_conferencia))}`}
                                      onClick={(e) => {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setConferenciaActionMenu((cur) => (cur?.id === conf.id ? null : { id: conf.id, rect }));
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                                {expandido && (
                                  <tr className="bg-muted/20 border-b-0">
                                    <td colSpan={5} className="px-3 pt-0 pb-3">
                                      {conf.alteracoes && conf.alteracoes.length > 0 ? (
                                        <ul className="space-y-1.5 text-sm">
                                          {conf.alteracoes.map((alt, idx) => (
                                            <li key={idx}>
                                              {formatDisplayText(alt.item_nome)} {Number(alt.quantidade_antes)}{alt.unidade} {' ➝ '} {Number(alt.quantidade_depois)}{alt.unidade}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="text-sm text-muted-foreground">Nenhuma alteração registrada nesta conferência.</p>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                </div>
              </div>

              {conferenciaActionMenu
                ? (() => {
                    const conf = conferenciasLista.find((c) => c.id === conferenciaActionMenu.id);
                    if (!conf) return null;
                    const expandido = expandedConferenciaId === conf.id;
                    return (
                      <ActionMenuPortal
                        open
                        anchorRect={conferenciaActionMenu.rect}
                        onClose={() => setConferenciaActionMenu(null)}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setExpandedConferenciaId(expandido ? null : conf.id);
                            setConferenciaActionMenu(null);
                          }}
                        >
                          <Eye className="w-4 h-4" /> Ver itens
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                            onClick={() => void handleExcluirConferencia(conf.id)}
                            disabled={deletingConferenciaId === conf.id}
                          >
                            {deletingConferenciaId === conf.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            Excluir
                          </button>
                        )}
                      </ActionMenuPortal>
                    );
                  })()
                : null}

              <ModalPeriodoPersonalizado
                open={showModalPeriodoPersonalizadoConferencia}
                onClose={() => setShowModalPeriodoPersonalizadoConferencia(false)}
                inicio={conferenciaPersonalizadoInicio}
                fim={conferenciaPersonalizadoFim}
                onInicioChange={setConferenciaPersonalizadoInicio}
                onFimChange={setConferenciaPersonalizadoFim}
                onApply={() => {
                  if (conferenciaPersonalizadoInicio && conferenciaPersonalizadoFim) {
                    setShowModalPeriodoPersonalizadoConferencia(false);
                    void carregarConferencias();
                  } else {
                    toast.error("Informe data início e fim.");
                  }
                }}
              />
              </div>
              )}
            </TabsContent>
          )}

          {/* Aba Parceiros (Cadastros) */}
          {canViewTab("parceiros") && (
            <TabsContent value="parceiros" className="space-y-4 transition-opacity duration-200 ease-in-out">
              <ParceirosTab active={activeTab === "parceiros"} />
            </TabsContent>
          )}

          {/* Aba Marketing */}
          {canViewTab('marketing') && (
            <TabsContent value="marketing" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {loadingPromocoes ? (
                <PageContentLoader />
              ) : (
              <>
              <div className="flex max-w-full min-w-0 flex-nowrap items-center justify-between gap-2">
                <h2 className={PAGE_TITLE_CLASS}>Marketing</h2>
                  <Button
                  type="button"
                  className={BTN_PAGE_PRIMARY_ACTION_MARKETING}
                  onClick={() => {
                    const draft = loadFormDraft<{
                      nome?: string;
                      valor?: string;
                      tipo?: "fixa" | "categoria";
                      categoriasLinhas?: Array<{ categoria: string; quantidade: string }>;
                      itens?: Array<{ produto_id: number; nome: string; quantidade: number }>;
                    }>(buildFormDraftKey("promocao", "new"));
                    setEditingPromocaoId(null);
                    setPromocaoFormBaseline(null);
                    setShowFormPromocao(true);
                    setPromocaoNome(draft?.nome ?? "");
                    setPromocaoValor(draft?.valor ?? "");
                    setPromocaoTipo(draft?.tipo ?? "fixa");
                    setPromocaoCategoriasLinhas(Array.isArray(draft?.categoriasLinhas) ? draft.categoriasLinhas : []);
                    setPromocaoItens(Array.isArray(draft?.itens) ? draft.itens : []);
                    setPromocaoProdutoBusca("");
                    void carregarProdutos();
                  }}
                  aria-label="Criar promoção"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  <span className="sm:hidden">Nova</span>
                  <span className="hidden sm:inline">Criar Promoção</span>
                </Button>
              </div>

              <div ref={marketingPromocoesRef}>
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {promocoesLista.length === 0 ? (
                  <p className="text-muted-foreground text-center py-12 px-4">Nenhuma promoção cadastrada.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="gs-management-table w-full min-w-[480px] text-sm">
                      <thead className={TABLE_HEAD_CLASS}>
                        <tr className="border-b border-gray-200">
                          <th className={`whitespace-nowrap text-left py-3 px-4 ${TABLE_TH_CLASS}`}>Nome Da Promoção</th>
                          <th className={`whitespace-nowrap text-center py-3 px-4 w-[140px] ${TABLE_TH_CLASS}`}>Valor</th>
                          <th className={`whitespace-nowrap text-right py-3 px-4 w-[100px] ${TABLE_TH_CLASS}`}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promocoesLista.map((promo) => {
                          const expandido = expandedPromocaoId === promo.id;
                          return (
                            <Fragment key={promo.id}>
                              <tr
                                className={`border-b border-gray-100 transition-colors cursor-pointer ${expandido ? "bg-muted/25 border-b-0" : "hover:bg-muted/20"}`}
                                onClick={() => {
                                  setExpandedPromocaoId(expandido ? null : promo.id);
                                  setPromocaoActionMenu(null);
                                }}
                              >
                                <td className={`px-4 ${expandido ? "pt-3 pb-1" : "py-3"}`}>
                                  <span className="block truncate min-w-0">{formatDisplayText(promo.nome)}</span>
                                </td>
                                <td className={`px-4 text-center tabular-nums ${expandido ? "pt-3 pb-1" : "py-3"}`}>{formatBRL(promo.valor)}</td>
                                <td className={`px-4 text-right relative ${expandido ? "pt-3 pb-1" : "py-3"}`} onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9"
                                    aria-label="Ações"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                      setPromocaoActionMenu((cur) => (cur?.id === promo.id ? null : { id: promo.id, rect }));
                                    }}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                              {expandido && (
                                <tr className="border-b border-gray-100 bg-muted/25">
                                  <td colSpan={3} className="px-4 pt-0 pb-3 text-sm">
                                    {promo.tipo === "configuravel" && (promo.grupos ?? []).length > 0 ? (
                                      <div className="space-y-4 pl-6">
                                        {(promo.grupos ?? []).map((g, gi) => (
                                          <div key={gi}>
                                            <p className="text-foreground mb-2">Grupo: {formatDisplayText(g.nome_grupo)}</p>
                                            <p className="text-muted-foreground text-xs mb-1">Escolha {g.quantidade_obrigatoria} item(ns)</p>
                                            <ul className="list-none space-y-1 text-muted-foreground pl-2 border-l-2 border-gray-200">
                                              {(g.produtos ?? []).map((pr, pi) => (
                                                <li key={pi}>· {pr.produto_nome ? formatDisplayText(pr.produto_nome) : `Produto #${pr.produto_id}`}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        ))}
                                      </div>
                                    ) : promo.tipo === "categoria" ? (
                                      <div className="pl-6 text-sm text-muted-foreground space-y-2">
                                        {(Array.isArray(promo.categorias) && promo.categorias.length > 0
                                          ? promo.categorias
                                          : promo.categoria_produto
                                            ? [{ categoria: promo.categoria_produto, quantidade: 1 }]
                                            : []
                                        ).map((row, idx) => (
                                          <p key={idx}>
                                            <span className="text-foreground">{formatDisplayText(row.categoria)}</span>
                                            {" → "}
                                            {row.quantidade} item(ns)
                                          </p>
                                        ))}
                                        <p className="text-xs">
                                          No caixa, o operador escolhe os produtos em cada categoria antes de adicionar ao carrinho.
                                        </p>
                                      </div>
                                    ) : (
                                      <ul className="list-none space-y-1.5 pl-6">
                                        {(promo.itens ?? []).map((item, idx) => (
                                          <li key={idx} className="text-muted-foreground">
                                            <span className="text-foreground">{formatDisplayText(item.produto_nome)}</span>
                                            {" → "}
                                            {Number(item.quantidade)} un
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                </div>
                {promocaoActionMenu
                  ? (() => {
                      const promo = promocoesLista.find((p) => p.id === promocaoActionMenu.id);
                      if (!promo) return null;
                      const expandido = expandedPromocaoId === promo.id;
                      return (
                        <ActionMenuPortal
                          open
                          anchorRect={promocaoActionMenu.rect}
                          onClose={() => setPromocaoActionMenu(null)}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setExpandedPromocaoId(expandido ? null : promo.id);
                              setPromocaoActionMenu(null);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                            Ver itens
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setPromocaoActionMenu(null);
                              if (promo.tipo === "configuravel") {
                                toast.error("Promoções configuráveis legadas não podem ser editadas nesta tela.");
                                return;
                              }
                              setEditingPromocaoId(promo.id);
                              setShowFormPromocao(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
                            disabled={deletingPromocaoId === promo.id}
                            onClick={() => {
                              setPromocaoActionMenu(null);
                              if (window.confirm("Excluir esta promoção? Ela será removida do caixa.")) {
                                setDeletingPromocaoId(promo.id);
                                api
                                  .excluirPromocao(promo.id)
                                  .then(() => {
                                    clearFormDraft(buildFormDraftKey("promocao", promo.id));
                                    toast.success("Promoção excluída.");
                                    void carregarPromocoes();
                                    if (expandedPromocaoId === promo.id) setExpandedPromocaoId(null);
                                    return api.listarProdutosCaixa();
                                  })
                                  .then(setProdutosCaixa)
                                  .catch(() => toast.error("Erro ao excluir"))
                                  .finally(() => setDeletingPromocaoId(null));
                              }
                            }}
                          >
                            {deletingPromocaoId === promo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Excluir
                          </button>
                        </ActionMenuPortal>
                      );
                    })()
                  : null}
              </div>
              </>
              )}

              <ModalBackdrop open={showFormPromocao}>
                  <div
                    className={`gs-modal-panel-max relative flex w-full max-w-xl max-h-[min(90vh,720px)] flex-col !overflow-y-visible overflow-x-hidden ${MODAL_PANEL_CLASS} p-0`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={MODAL_HEADER_ROW_CLASS}>
                      <h2 className={MODAL_TITLE_CLASS}>
                        {editingPromocaoId != null ? "Editar promoção" : "Nova promoção"}
                      </h2>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-9 w-9"
                        aria-label="Fechar"
                        onClick={() => {
                          clearFormDraft(promocaoDraftKey);
                          setShowFormPromocao(false);
                          setEditingPromocaoId(null);
                          setPromocaoFormBaseline(null);
                          setPromocaoNome("");
                          setPromocaoValor("");
                          setPromocaoTipo("fixa");
                          setPromocaoCategoriasLinhas([]);
                          setPromocaoItens([]);
                          setPromocaoProdutoBusca("");
                        }}
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="space-y-4 p-6 pt-4">
                      <div>
                        <Label>Nome da promoção</Label>
                        <Input value={promocaoNome} onChange={(e) => setPromocaoNome(e.target.value)} placeholder="Ex: Combo Pastel + Refri" className="mt-1" />
                      </div>
                      <div>
                        <Label>Valor da promoção (R$)</Label>
                        <MoneyInput value={promocaoValor} onChange={setPromocaoValor} placeholder="12,00" className="mt-1" />
                      </div>
                      <div>
                        <Label>Tipo da promoção</Label>
                        <Select
                          value={promocaoTipo}
                          onChange={(e) => {
                            const v = e.target.value as "fixa" | "categoria";
                            setPromocaoTipo(v);
                            if (v === "categoria") {
                              void carregarProdutos();
                            }
                          }}
                          className="mt-1 h-9 w-full"
                        >
                          <option value="fixa">Produto</option>
                          <option value="categoria">Categoria</option>
                        </Select>
                      </div>
                      {promocaoTipo === "categoria" && (
                        <div className="space-y-2">
                          <Label>Categorias e quantidades</Label>
                          {produtosLoading && (
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                              Carregando categorias dos produtos…
                            </p>
                          )}
                          {!produtosLoading && produtosCategoriasOpcoes.length === 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-500">
                              Nenhuma categoria encontrada nos produtos. Informe a categoria em cada produto na aba Produtos para elas aparecerem aqui.
                            </p>
                          )}
                          <Select
                            value=""
                            onChange={(e) => {
                              const cat = e.target.value.trim();
                              if (!cat) return;
                              if (promocaoCategoriasLinhas.some((r) => r.categoria === cat)) return;
                              setPromocaoCategoriasLinhas((prev) => [...prev, { categoria: cat, quantidade: "1" }]);
                            }}
                            disabled={produtosLoading}
                            className="mt-0 h-9 w-full"
                          >
                            <option value="">
                              {produtosLoading ? "Carregando…" : "Selecione uma categoria"}
                            </option>
                            {produtosCategoriasOpcoes
                              .filter((nome) => !promocaoCategoriasLinhas.some((r) => r.categoria === nome))
                              .map((nome) => (
                                <option key={nome} value={nome}>
                                  {nome}
                                </option>
                              ))}
                          </Select>
                          {promocaoCategoriasLinhas.length > 0 && (
                            <ul className="mt-2 space-y-2">
                              {promocaoCategoriasLinhas.map((row, ri) => (
                                <li
                                  key={row.categoria}
                                  className="grid items-center gap-2 rounded border border-gray-100 bg-muted/20 p-3 sm:grid-cols-[1fr_5rem_2.5rem]"
                                >
                                  <span className="min-w-0 truncate text-sm font-medium">
                                    {formatDisplayText(row.categoria)}
                                  </span>
                                  <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="Qtd"
                                    value={row.quantidade}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === "") return;
                                      const n = parseInt(raw, 10);
                                      if (!Number.isNaN(n) && n >= 1) {
                                        setPromocaoCategoriasLinhas((prev) =>
                                          prev.map((r, i) => (i === ri ? { ...r, quantidade: String(n) } : r)),
                                        );
                                      }
                                    }}
                                    className="h-9 border-gray-200"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-destructive hover:text-destructive"
                                    aria-label={`Remover categoria ${row.categoria}`}
                                    onClick={() =>
                                      setPromocaoCategoriasLinhas((prev) => prev.filter((_, i) => i !== ri))
                                    }
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Apenas categorias que possuem produtos cadastrados (mesma base da aba Produtos). No caixa, o
                            operador escolhe os produtos em cada categoria antes de adicionar ao carrinho.
                          </p>
                        </div>
                      )}
                      {promocaoTipo === "fixa" && (
                        <div>
                          <Label>Itens da promoção</Label>
                          <div className="mt-1 w-full min-w-0 overflow-visible">
                            <Autocomplete
                              value={promocaoProdutoBusca}
                              onValueChange={setPromocaoProdutoBusca}
                              onSelect={(opt) => {
                                const p = produtosCaixa.find((x) => x.id === opt.value || x.nome === opt.label);
                                if (p) {
                                  const ja = promocaoItens.find((i) => i.produto_id === p.id);
                                  if (ja) {
                                    setPromocaoItens((prev) => prev.map((i) => i.produto_id === p.id ? { ...i, quantidade: i.quantidade + 1 } : i));
                                  } else {
                                    setPromocaoItens((prev) => [...prev, { produto_id: p.id, nome: p.nome, quantidade: 1 }]);
                                  }
                                  setPromocaoProdutoBusca("");
                                }
                              }}
                              options={produtosCaixa.filter((p) => !promocaoItens.some((i) => i.produto_id === p.id) && !promocoesLista.some((promo) => promo.produto_id === p.id)).map((p) => ({ value: p.id, label: p.nome, meta: `R$ ${(p.preco_venda ?? 0).toFixed(2)}` }))}
                              placeholder="Buscar produto..."
                              requireQueryForSuggestions
                              listLayout="inline"
                              maxVisibleItems={5}
                              className="h-10 w-full border-gray-200 bg-white"
                            />
                          </div>
                          {promocaoItens.length > 0 && (
                            <ul className="mt-2 space-y-2">
                              {promocaoItens.map((i) => (
                                <li
                                  key={i.produto_id}
                                  className="grid items-center gap-2 rounded border border-gray-100 bg-muted/20 p-3 sm:grid-cols-[1fr_5rem_2.5rem]"
                                >
                                  <span className="min-w-0 truncate text-sm font-medium">{formatDisplayText(i.nome)}</span>
                                  <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="Qtd"
                                    value={i.quantidade}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === "") return;
                                      const n = parseInt(raw, 10);
                                      if (!Number.isNaN(n) && n >= 1) {
                                        setPromocaoItens((prev) =>
                                          prev.map((it) =>
                                            it.produto_id === i.produto_id ? { ...it, quantidade: n } : it,
                                          ),
                                        );
                                      }
                                    }}
                                    className="h-9 border-gray-200"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-destructive hover:text-destructive"
                                    onClick={() =>
                                      setPromocaoItens((prev) => prev.filter((it) => it.produto_id !== i.produto_id))
                                    }
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      <ModalPrimaryAction
                        show={promocaoModalShowAction}
                        type="button"
                        loading={savingPromocao}
                        disabled={savingPromocao}
                        onClick={async () => {
                          const valor = parseMoneyMask(String(promocaoValor));
                          if (valor <= 0 || !promocaoNome.trim()) {
                            toast.error("Preencha nome e valor.");
                            return;
                          }
                          if (promocaoTipo === "fixa" && promocaoItens.length === 0) {
                            toast.error("Adicione pelo menos um item à promoção fixa.");
                            return;
                          }
                          let categoriasPayload: Array<{ categoria: string; quantidade: number }> | undefined;
                          if (promocaoTipo === "categoria") {
                            const linhasValidas = promocaoCategoriasLinhas
                              .map((row) => ({
                                categoria: row.categoria.trim(),
                                quantidade: Math.max(1, parseInt(String(row.quantidade).replace(",", "."), 10) || 0),
                              }))
                              .filter((r) => r.categoria !== "" && r.quantidade >= 1);
                            if (linhasValidas.length === 0) {
                              toast.error("Informe ao menos uma categoria com quantidade válida (mínimo 1).");
                              return;
                            }
                            const seen = new Set<string>();
                            for (const r of linhasValidas) {
                              const k = r.categoria.toLowerCase();
                              if (seen.has(k)) {
                                toast.error("Não repita a mesma categoria na promoção.");
                                return;
                              }
                              seen.add(k);
                            }
                            categoriasPayload = linhasValidas;
                          }
                          setSavingPromocao(true);
                          try {
                            const nomeTrim = promocaoNome.trim();
                            const categoriasSanitizadas =
                              categoriasPayload?.map((r) => ({
                                categoria: r.categoria.trim(),
                                quantidade: Math.max(1, Math.floor(Number(r.quantidade)) || 1),
                              })) ?? [];

                            if (promocaoTipo === "categoria" && categoriasSanitizadas.length === 0) {
                              toast.error("Selecione categorias e quantidades válidas.");
                              return;
                            }

                            if (editingPromocaoId != null) {
                              if (promocaoTipo === "categoria") {
                                await api.atualizarPromocao({
                                  id: editingPromocaoId,
                                  nome: nomeTrim,
                                  valor,
                                  tipo: "categoria",
                                  categorias: categoriasSanitizadas,
                                });
                              } else {
                                await api.atualizarPromocao({
                                  id: editingPromocaoId,
                                  nome: nomeTrim,
                                  valor,
                                  tipo: "fixa",
                                  itens: promocaoItens.map((i) => ({
                                    produto_id: i.produto_id,
                                    quantidade: i.quantidade,
                                  })),
                                });
                              }
                              toast.success("Promoção atualizada.");
                            } else if (promocaoTipo === "categoria") {
                              await api.criarPromocao({
                                nome: nomeTrim,
                                valor,
                                tipo: "categoria",
                                categorias: categoriasSanitizadas,
                              });
                              toast.success("Promoção criada. Ela já aparece no caixa.");
                            } else {
                              await api.criarPromocao({
                                nome: nomeTrim,
                                valor,
                                tipo: "fixa",
                                itens: promocaoItens.map((i) => ({
                                  produto_id: i.produto_id,
                                  quantidade: i.quantidade,
                                })),
                              });
                              toast.success("Promoção criada. Ela já aparece no caixa.");
                            }

                            try {
                              await carregarPromocoes();
                              const prods = await api.listarProdutosCaixa();
                              setProdutosCaixa(prods);
                            } catch (refreshErr) {
                              console.error(refreshErr);
                              toast.error(
                                refreshErr instanceof Error
                                  ? `Promoção salva, mas falhou ao atualizar as listas: ${refreshErr.message}`
                                  : "Promoção salva, mas falhou ao atualizar as listas. Atualize a página (F5)."
                              );
                            }

                            clearFormDraft(promocaoDraftKey);
                            setShowFormPromocao(false);
                            setEditingPromocaoId(null);
                            setPromocaoFormBaseline(null);
                            setPromocaoNome("");
                            setPromocaoValor("");
                            setPromocaoTipo("fixa");
                            setPromocaoCategoriasLinhas([]);
                            setPromocaoItens([]);
                            setPromocaoProdutoBusca("");
                          } catch (err) {
                            const msg =
                              err instanceof Error ? err.message : "Não foi possível salvar a promoção. Tente novamente.";
                            toast.error(msg);
                          } finally {
                            setSavingPromocao(false);
                          }
                        }}
                      >
                        {editingPromocaoId != null ? "Salvar" : "Salvar promoção"}
                      </ModalPrimaryAction>
                    </div>
                  </div>
              </ModalBackdrop>
            </TabsContent>
          )}

          {/* Aba Pagamentos */}
          {canViewTab('fornecedores') && (
            <TabsContent value="fornecedores" className="space-y-4 transition-opacity duration-200 ease-in-out">
              {loadingFornecedorLancamentos ? (
                <PageContentLoader />
              ) : (
              <>
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <h2 className={PAGE_TITLE_CLASS}>Pagamentos</h2>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    className={BTN_PAGE_PRIMARY_ACTION_ROW}
                    onClick={() => {
                      setFornecedorNfPrefill(null);
                      setModalFornecedorNfOpen(true);
                    }}
                  >
                    <Upload className="h-4 w-4 shrink-0" /> Importar Boleto
                  </Button>
                  <Button
                    type="button"
                    className={BTN_PAGE_PRIMARY_ACTION_ROW}
                    onClick={() => {
                      setFornecedorEditing(null);
                      setFornecedorNfPrefill(null);
                      setModalFornecedorOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 shrink-0" /> Lançar Pagamento
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Card
                  className={`${KPI_VALUE_CARD} w-full max-w-md`}
                >
                  <CardHeader className={KPI_VALUE_CARD_HEADER}>
                    <CardTitle className={KPI_VALUE_CARD_TITLE}>
                      {fornecedorStatusFiltro === "pago" ? "Valor pago" : "Valor a pagar"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className={KPI_VALUE_CARD_CONTENT}>
                    <div className={KPI_VALUE_TEXT}>
                      {ocultarValoresFornecedorCard
                        ? "••••••"
                        : formatBRL(
                            fornecedorStatusFiltro === "pago" ? fornecedorValorPagoSoma : fornecedorValorPendenteSoma,
                          )}
                    </div>
                    <button
                      type="button"
                      className={KPI_VALUE_EYE_BTN}
                      onClick={() => setOcultarValoresFornecedorCard((v) => !v)}
                      title={ocultarValoresFornecedorCard ? "Mostrar valores" : "Ocultar valores"}
                    >
                      {ocultarValoresFornecedorCard ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </CardContent>
                </Card>
              </div>

                <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                  <div className="gs-admin-table-toolbar flex flex-col items-stretch justify-between gap-2 min-[767px]:flex-row min-[767px]:items-center">
                    <TableSearchInput
                      value={searchFornecedorLancamentos}
                      onValueChange={setSearchFornecedorLancamentos}
                      className="w-full min-[767px]:max-w-[360px]"
                      id="pagamentos-pesquisar"
                    />
                    <LabeledFilterSelect
                      id="pagamentos-status"
                      label="Status"
                      labelBgClass="bg-white"
                      value={fornecedorStatusFiltro}
                      onChange={(e) => setFornecedorStatusFiltro(e.target.value as "pendente" | "pago")}
                      className="h-10 w-full min-w-0 min-[767px]:w-[200px] min-[767px]:shrink-0"
                    >
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                    </LabeledFilterSelect>
                  </div>
                  <div className="overflow-x-auto">
                      <table className="gs-management-table w-full min-w-[720px] text-sm">
                        <thead className={TABLE_HEAD_CLASS}>
                          <tr>
                            <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Descrição</th>
                            <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Lançamento</th>
                            <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Valor</th>
                            <th className={`whitespace-nowrap text-left py-3 px-3 ${TABLE_TH_CLASS}`}>Vencimento</th>
                            <th className={`whitespace-nowrap text-left py-3 px-3 min-w-[220px] ${TABLE_TH_CLASS}`}>Status</th>
                            <th className={`whitespace-nowrap text-right py-3 px-3 ${TABLE_TH_CLASS}`}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fornecedorLancamentosFiltrados.length === 0 ? <FinanceiroEmptyRow colSpan={6} /> : fornecedorLancamentosFiltrados.map((f) => {
                            const diasAteVencimento = diffCalendarDaysVencimentoHojeSP(f.data_vencimento);
                            return (
                            <tr key={f.id} className="border-t border-gray-200 hover:bg-muted/30">
                              <td className="py-2 px-3">
                                <span>{formatDisplayText(f.fornecedor)}</span>
                                {f.parcela_numero != null && f.parcela_total != null && f.parcela_total > 1 ? (
                                  <span className="ml-1.5 text-xs text-muted-foreground">
                                    Parcela {f.parcela_numero}/{f.parcela_total}
                                  </span>
                                ) : null}
                              </td>
                              <td className="py-2 px-3">{formatarData(f.data_lancamento)}</td>
                              <td className="py-2 px-3">{formatBRL(f.valor)}</td>
                              <td className="py-2 px-3">{formatarData(f.data_vencimento)}</td>
                              <td className="py-2 px-3 align-middle">
                                <div className="flex flex-nowrap items-center gap-2">
                                  {f.status === "pago" ? (
                                    <span className="inline-flex shrink-0 items-center rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800 whitespace-nowrap">
                                      Pago
                                    </span>
                                  ) : (
                                    <>
                                      <Select
                                        value={f.status}
                                        onChange={async (e) => {
                                          const status = e.target.value as "pendente" | "pago";
                                          try {
                                            await api.atualizarFornecedorLancamento({
                                              id: f.id,
                                              fornecedor: f.fornecedor,
                                              valor: Number(f.valor),
                                              data_lancamento: f.data_lancamento,
                                              data_vencimento: f.data_vencimento,
                                              status,
                                            });
                                            await carregarFornecedorLancamentos();
                                          } catch (err) {
                                            toast.error(err instanceof Error ? err.message : "Erro ao atualizar status");
                                          }
                                        }}
                                        className="!w-[100px] !min-w-[100px] !max-w-[100px] h-9 shrink-0 !px-1 py-2 text-sm"
                                      >
                                        <option value="pendente">Pendente</option>
                                        <option value="pago">Pago</option>
                                      </Select>
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${fornecedorCorTagDiasRestantes(
                                          diasAteVencimento,
                                        )}`}
                                        title="Prazo em relação a hoje (São Paulo)"
                                      >
                                        {fornecedorLabelTagDiasRestantes(diasAteVencimento)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-3 text-right relative">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                    setFornecedorActionMenu((cur) => (cur?.id === f.id ? null : { id: f.id, rect }));
                                  }}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                  </div>
                </div>
                {fornecedorActionMenu
                  ? (() => {
                      const f = fornecedorLancamentos.find((x) => x.id === fornecedorActionMenu.id);
                      if (!f) return null;
                      return (
                        <ActionMenuPortal
                          open
                          anchorRect={fornecedorActionMenu.rect}
                          onClose={() => setFornecedorActionMenu(null)}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setFornecedorEditing(f);
                              setModalFornecedorOpen(true);
                              setFornecedorActionMenu(null);
                            }}
                          >
                            <Pencil className="w-4 h-4" /> Editar
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                            onClick={() => {
                              setFornecedorDeleteId(f.id);
                              setFornecedorActionMenu(null);
                            }}
                          >
                            <Trash2 className="w-4 h-4" /> Excluir
                          </button>
                        </ActionMenuPortal>
                      );
                    })()
                  : null}
              </>
              )}

              {modalFornecedorOpen && (
                <ModalFornecedorLancamento
                  initial={fornecedorEditing}
                  prefill={fornecedorEditing ? null : fornecedorNfPrefill}
                  onClose={() => {
                    setModalFornecedorOpen(false);
                    setFornecedorEditing(null);
                    setFornecedorNfPrefill(null);
                  }}
                  onSuccess={() => {
                    setModalFornecedorOpen(false);
                    setFornecedorEditing(null);
                    setFornecedorNfPrefill(null);
                    void carregarFornecedorLancamentos();
                  }}
                />
              )}
              <ModalImportarNfFornecedor
                open={modalFornecedorNfOpen}
                onClose={() => setModalFornecedorNfOpen(false)}
                onParsed={(prefill) => {
                  setModalFornecedorNfOpen(false);
                  setFornecedorEditing(null);
                  setFornecedorNfPrefill(prefill);
                  setModalFornecedorOpen(true);
                }}
              />
              {fornecedorDeleteId !== null && (
                <ModalExcluirDespesaFinanceiro
                  onClose={() => setFornecedorDeleteId(null)}
                  onConfirm={async () => {
                    await api.excluirFornecedorLancamento(fornecedorDeleteId);
                    setFornecedorDeleteId(null);
                    toast.success("Lançamento excluído.");
                    void carregarFornecedorLancamentos();
                  }}
                />
              )}
            </TabsContent>
          )}


          {/* Configuração — Geral (todos) + Equipe (admin) */}
          <TabsContent value="config" className="space-y-4 transition-opacity duration-200 ease-in-out">
            <h2 className={PAGE_TITLE_CLASS}>Configuração</h2>
            <div
              className="rounded-lg border border-gray-200 bg-white shadow-sm"
              role="tablist"
              aria-label="Configuração"
            >
              <div className="flex min-h-[3.5rem] w-full flex-nowrap items-center justify-between gap-0 px-2 sm:justify-start sm:gap-0 sm:px-4">
                <button
                  type="button"
                  role="tab"
                  aria-selected={configSubTab === "geral"}
                  onClick={() => goToTab("config", { configSubTab: "geral" })}
                  className="relative inline-flex shrink-0 px-1.5 focus:outline-none focus-visible:outline-none sm:px-5"
                >
                  <span
                    className={`inline-flex items-center gap-1 text-sm sm:gap-2 ${
                      configSubTab === "geral"
                        ? "font-semibold text-foreground"
                        : "font-medium text-muted-foreground"
                    }`}
                  >
                    <User className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                    <span className="whitespace-nowrap">Geral</span>
                  </span>
                  {configSubTab === "geral" ? (
                    <span
                      className="pointer-events-none absolute inset-x-0 top-full mt-1.5 h-0.5 rounded-full"
                      style={{ backgroundColor: clientConfig.primary_color }}
                    />
                  ) : null}
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={configSubTab === "usuarios"}
                    onClick={() => goToTab("config", { configSubTab: "usuarios" })}
                    className="relative inline-flex min-w-0 shrink px-1.5 focus:outline-none focus-visible:outline-none sm:shrink-0 sm:px-5"
                  >
                    <span
                      className={`inline-flex min-w-0 items-center gap-1 text-sm sm:gap-2 ${
                        configSubTab === "usuarios"
                          ? "font-semibold text-foreground"
                          : "font-medium text-muted-foreground"
                      }`}
                    >
                      <Key className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                      <span className="whitespace-nowrap">Equipe</span>
                    </span>
                    {configSubTab === "usuarios" ? (
                      <span
                        className="pointer-events-none absolute inset-x-0 top-full mt-1.5 h-0.5 rounded-full"
                        style={{ backgroundColor: clientConfig.primary_color }}
                      />
                    ) : null}
                  </button>
                ) : null}
                <button
                  type="button"
                  role="tab"
                  aria-selected={configSubTab === "senha"}
                  onClick={() => goToTab("config", { configSubTab: "senha" })}
                  className="relative inline-flex shrink-0 px-1.5 focus:outline-none focus-visible:outline-none sm:px-5"
                >
                  <span
                    className={`inline-flex items-center gap-1 text-sm sm:gap-2 ${
                      configSubTab === "senha"
                        ? "font-semibold text-foreground"
                        : "font-medium text-muted-foreground"
                    }`}
                  >
                    <Key className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                    <span className="whitespace-nowrap">Alterar Senha</span>
                  </span>
                  {configSubTab === "senha" ? (
                    <span
                      className="pointer-events-none absolute inset-x-0 top-full mt-1.5 h-0.5 rounded-full"
                      style={{ backgroundColor: clientConfig.primary_color }}
                    />
                  ) : null}
                </button>
              </div>
            </div>
            {isAdmin && configSubTab === "usuarios" && !loadingUsuarios ? (
              <div className="flex justify-end">
                <Button
                  type="button"
                  className={BTN_PAGE_PRIMARY_ACTION}
                  onClick={openCreateUserModal}
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  Adicionar
                </Button>
              </div>
            ) : null}

            {configSubTab === "geral" ? (
              <MeuPerfilPage showEmpresaSection={isAdmin} embedded />
            ) : configSubTab === "senha" ? (
              <AlterarSenhaPage embedded />
            ) : isAdmin ? (
              loadingUsuarios ? (
                <PageContentLoader />
              ) : (
              <>
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                {(usuarios ?? []).length === 0 ? (
                  <p className="py-12 text-center text-muted-foreground">Nenhum usuário cadastrado.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="gs-management-table w-full min-w-[520px] text-sm">
                      <thead className={TABLE_HEAD_CLASS}>
                        <tr className="border-b border-gray-200">
                          <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Usuário</th>
                          <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Perfil</th>
                          <th className={`w-14 px-4 py-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(usuarios ?? []).map((u) => (
                          <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-muted/20">
                            <td className="px-4 py-3 font-medium text-foreground">
                              {formatDisplayText(usuarioNomeExibicao(u))}
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-foreground">
                                {ROLE_LABELS[u.role] ?? u.role}
                              </div>
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {usuarioAcessoResumo(u)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={`Ações do usuário ${usuarioNomeExibicao(u)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUsuarioActionMenu({ id: u.id, rect: e.currentTarget.getBoundingClientRect() });
                                }}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

                  {showCreateUserForm && (
                    <div
                      className="gs-modal-overlay fixed inset-0 z-[110] flex items-center justify-center bg-[#F4F6F8]/95 p-4"
                      onClick={() => closeCreateUserModal()}
                    >
                      <div
                        className={`gs-modal-panel-max flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-y-auto max-sm:min-h-0 max-sm:overflow-hidden ${MODAL_PANEL_CLASS} p-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={MODAL_HEADER_ROW_CLASS}>
                          <h2 className={MODAL_TITLE_CLASS}>Criar novo usuário</h2>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={closeCreateUserModal}
                            disabled={savingNewUser}
                            aria-label="Fechar"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </div>
                        <form onSubmit={handleCriarUsuario} className="min-h-0 space-y-4 p-6 pt-4 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain">
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="new-username">Usuário (login) *</Label>
                              <Input
                                id="new-username"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                required
                                className="w-full"
                                autoComplete="username"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="new-password">Senha *</Label>
                              <Input
                                id="new-password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={4}
                                className="w-full"
                                autoComplete="new-password"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-name">Nome</Label>
                            <Input
                              id="new-name"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-email">Email</Label>
                            <Input
                              id="new-email"
                              type="email"
                              value={newEmail}
                              onChange={(e) => setNewEmail(e.target.value)}
                              placeholder="ex: usuario@email.com"
                              className="w-full"
                              autoComplete="email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-telefone">Telefone</Label>
                            <UsuarioTelefoneMaskInput id="new-telefone" value={newTelefone} onChange={setNewTelefone} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-role">Tipo / perfil *</Label>
                            <Select
                              id="new-role"
                              value={newRole}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                const r = e.target.value as typeof newRole;
                                setNewRole(r);
                                if (r === "admin") setNewUserTabs([]);
                              }}
                              className="w-full"
                              required
                            >
                              <option value="funcionario">Funcionário</option>
                              <option value="gerente">Gerente</option>
                              <option value="cozinha">Cozinha</option>
                              <option value="marketing">Marketing</option>
                              <option value="admin">Admin</option>
                            </Select>
                          </div>
                          {newRole !== "admin" && (
                            <UserPermissionsField
                              value={newUserTabs}
                              onChange={setNewUserTabs}
                              showHint
                            />
                          )}
                          <ModalPrimaryAction
                            show={newUserModalShowAction}
                            type="submit"
                            loading={savingNewUser}
                            disabled={savingNewUser}
                          >
                            Criar usuário
                          </ModalPrimaryAction>
                        </form>
                      </div>
                    </div>
                  )}

                  {editUserModal && (
                    <div
                      className="gs-modal-overlay fixed inset-0 z-[110] flex items-center justify-center bg-[#F4F6F8]/95 p-4"
                      onClick={() => !savingEditUser && setEditUserModal(null)}
                    >
                      <div
                        className={`gs-modal-panel-max flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-y-auto max-sm:min-h-0 max-sm:overflow-hidden ${MODAL_PANEL_CLASS} p-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={MODAL_HEADER_ROW_CLASS}>
                          <h2 className={MODAL_TITLE_CLASS}>Editar usuário</h2>
                          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={closeEditUserModal} disabled={savingEditUser} aria-label="Fechar">
                            <X className="h-5 w-5" />
                          </Button>
                        </div>
                        <form onSubmit={handleSalvarEdicaoUsuario} className="min-h-0 space-y-4 p-6 pt-4 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain">
                          <div className="space-y-2">
                            <Label>Usuário (login)</Label>
                            <Input value={editUserModal.username} disabled className="bg-muted" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-name">Nome</Label>
                            <Input id="edit-name" value={editUserName} onChange={(e) => setEditUserName(e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-email">Email</Label>
                            <Input
                              id="edit-email"
                              type="email"
                              value={editUserEmail}
                              onChange={(e) => setEditUserEmail(e.target.value)}
                              placeholder="ex: usuario@email.com"
                              className="w-full"
                              autoComplete="email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-telefone">Telefone</Label>
                            <UsuarioTelefoneMaskInput
                              id="edit-telefone"
                              value={editUserTelefone}
                              onChange={setEditUserTelefone}
                              disabled={savingEditUser}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-role">Tipo / perfil</Label>
                            <Select
                              id="edit-role"
                              value={editUserRole}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                const r = e.target.value as typeof newRole;
                                setEditUserRole(r);
                                if (r === "admin") setEditUserTabs([]);
                              }}
                              className="w-full"
                            >
                              <option value="funcionario">Funcionário</option>
                              <option value="gerente">Gerente</option>
                              <option value="cozinha">Cozinha</option>
                              <option value="marketing">Marketing</option>
                              <option value="admin">Admin</option>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-pass">Nova senha (deixe em branco para não alterar)</Label>
                            <Input
                              id="edit-pass"
                              type="password"
                              value={editUserPassword}
                              onChange={(e) => setEditUserPassword(e.target.value)}
                              autoComplete="new-password"
                            />
                          </div>
                          {editUserRole !== "admin" && (
                            <UserPermissionsField value={editUserTabs} onChange={setEditUserTabs} />
                          )}
                          <ModalPrimaryAction
                            show={editUserModalShowAction}
                            type="submit"
                            loading={savingEditUser}
                            disabled={savingEditUser}
                          >
                            Salvar
                          </ModalPrimaryAction>
                        </form>
                      </div>
                    </div>
                  )}
              </>
              )
            ) : null}
          </TabsContent>
        </Tabs>
        )}

        {produtoActionMenu &&
          (() => {
            const produto = produtosLista.find((p) => p.id === produtoActionMenu.id);
            if (!produto) return null;
            return (
              <ActionMenuPortal
                open
                anchorRect={produtoActionMenu.rect}
                onClose={() => setProdutoActionMenu(null)}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setProdutoActionMenu(null);
                    setLocation(`/itens/produtos/${produto.id}/editar`);
                  }}
                >
                  <Pencil className="h-4 w-4" /> Editar
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                  onClick={async () => {
                    setProdutoActionMenu(null);
                    if (!confirm("Deseja realmente excluir este produto?")) return;
                    try {
                      await api.excluirProduto(produto.id);
                      await carregarProdutos();
                      await carregarDados();
                    } catch (e) {
                      alert("Erro ao excluir produto");
                      console.error(e);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Excluir
                </button>
              </ActionMenuPortal>
            );
          })()}

        {insumoActionMenu &&
          (() => {
            const insumo = insumosTabList.find((i) => i.id === insumoActionMenu.id);
            if (!insumo) return null;
            return (
              <ActionMenuPortal
                open
                anchorRect={insumoActionMenu.rect}
                onClose={() => setInsumoActionMenu(null)}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setInsumoActionMenu(null);
                    carregarInsumoParaEdicao(insumo);
                  }}
                >
                  <Pencil className="h-4 w-4" /> Editar
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    disabled={deletingInsumoId === insumo.id}
                    onClick={() => {
                      setInsumoActionMenu(null);
                      void handleExcluirInsumo(insumo.id);
                    }}
                  >
                    {deletingInsumoId === insumo.id ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Trash2 className="h-4 w-4 shrink-0" />}
                    Excluir
                  </button>
                )}
              </ActionMenuPortal>
            );
          })()}

        {usuarioActionMenu &&
          (() => {
            const usuario = (usuarios ?? []).find((u) => u.id === usuarioActionMenu.id);
            if (!usuario) return null;
            return (
              <ActionMenuPortal
                open
                anchorRect={usuarioActionMenu.rect}
                onClose={() => setUsuarioActionMenu(null)}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setUsuarioActionMenu(null);
                    openEditUserModal(usuario);
                  }}
                >
                  <Pencil className="h-4 w-4" /> Editar
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={user?.id === usuario.id}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10 disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => {
                    setUsuarioActionMenu(null);
                    void handleExcluirUsuario(usuario);
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Excluir
                </button>
              </ActionMenuPortal>
            );
          })()}

        {/* Modal Adicionar/Editar Produto */}
        <ModalBackdrop open={showProdutoModal}>
          <div
            className={`mx-auto flex w-full min-w-0 max-w-lg flex-col overflow-y-auto overflow-x-hidden max-sm:min-h-0 max-sm:overflow-hidden ${MODAL_PANEL_MAX_HEIGHT_CLASS} ${MODAL_PANEL_CLASS}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-produto-title"
          >
              <div className={MODAL_HEADER_ROW_CLASS}>
                <h2 id="modal-produto-title" className={MODAL_TITLE_CLASS}>
                  {editingProdutoId ? "Editar Produto" : "Adicionar Produto"}
                </h2>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={fecharModalProduto} aria-label="Fechar">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <form onSubmit={handleSalvarProduto} className="min-h-0 min-w-0 max-w-full space-y-4 bg-white p-6 pt-4 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain">
                <div className="space-y-2">
                  <Label htmlFor="produto_nome">Nome do produto *</Label>
                  <Input
                    id="produto_nome"
                    value={produtoNome}
                    onChange={(e) => setProdutoNome(e.target.value)}
                    placeholder="Ex: Café com leite"
                    required
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="produto_preco_venda">Preço de venda *</Label>
                  <MoneyInput
                    id="produto_preco_venda"
                    value={produtoPrecoVenda}
                    onChange={setProdutoPrecoVenda}
                    placeholder="0,00"
                    required
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="produto_categoria">Categoria *</Label>
                  <CategoryFormSelect
                    id="produto_categoria"
                    value={produtoCategoriaSelect}
                    onChange={setProdutoCategoriaSelect}
                    categories={categoriasList}
                    onDeleteCategory={handleExcluirCategoria}
                    showOutrasOption
                    outrasValue={OPCAO_CATEGORIA_OUTRA}
                    outrasLabel="Outras"
                    className="w-full"
                  />
                </div>
                {produtoCategoriaSelect === OPCAO_CATEGORIA_OUTRA && (
                  <div className="space-y-2">
                    <Label htmlFor="produto_categoria_outra">Nova categoria *</Label>
                    <Input
                      id="produto_categoria_outra"
                      value={produtoCategoriaOutra}
                      onChange={(e) => setProdutoCategoriaOutra(e.target.value)}
                      placeholder="Ex: Doces"
                      className="w-full"
                    />
                    {!isAdmin ? (
                      <p className="text-xs text-amber-800">Somente administrador pode cadastrar uma categoria nova no sistema.</p>
                    ) : null}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="produto_possui_composicao"
                    checked={produtoPossuiComposicao}
                    onChange={(e) => setProdutoPossuiComposicao(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="produto_possui_composicao">Possui composição (consome insumos)</Label>
                </div>
                {produtoPossuiComposicao ? (
                  <p className="text-xs text-muted-foreground">
                    Produtos com composição usam automaticamente o mínimo baseado nos insumos.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="produto_quantidade_minima">Quantidade mínima *</Label>
                    <Input
                      id="produto_quantidade_minima"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.,]?[0-9]*"
                      value={produtoQuantidadeMinima}
                      onChange={(e) => setProdutoQuantidadeMinima(e.target.value)}
                      onBlur={() => {
                        const value = parseQuantidadeInput(produtoQuantidadeMinima);
                        if (value !== null) setProdutoQuantidadeMinima(formatQuantidadeInput(value));
                      }}
                      placeholder="0"
                      required={!produtoPossuiComposicao}
                      className="w-full"
                    />
                  </div>
                )}

                {produtoPossuiComposicao && (
                  <div className="min-w-0 max-w-full space-y-3 border-t pt-2">
                    <Label>Composição (insumos e quantidades)</Label>
                    <p className="text-xs text-muted-foreground">
                      Selecione insumos já cadastrados e informe a quantidade de cada um. A unidade é exibida automaticamente.
                    </p>
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
                      <Select
                        value={produtoInsumoToAdd === "" ? "" : String(produtoInsumoToAdd)}
                        onChange={(e) => setProdutoInsumoToAdd(e.target.value === "" ? "" : Number(e.target.value))}
                        className="min-h-10 min-w-0 w-full flex-1 sm:min-w-0"
                      >
                        <option value="">Selecionar insumo...</option>
                        {insumos
                          .filter((i) => !produtoComposicaoItems.some((c) => c.insumo_id === i.id))
                          .map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.nome} ({i.unidade})
                            </option>
                          ))}
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-10 shrink-0 sm:self-stretch"
                        onClick={adicionarInsumoNaComposicao}
                      >
                        Adicionar
                      </Button>
                    </div>
                    {produtoComposicaoItems.length > 0 && (
                      <ul className="min-w-0 space-y-2">
                        {produtoComposicaoItems.map((item) => {
                          const opcoesUnidade = opcoesUnidadePorBase(item.unidade_base || item.unidade || "un");
                          return (
                          <li
                            key={item.insumo_id}
                            className="min-w-0 overflow-visible rounded border bg-muted/30 p-2"
                          >
                            <div
                              className="grid w-full min-w-0 items-center gap-x-2 gap-y-2 sm:gap-x-3"
                              style={{
                                gridTemplateColumns:
                                  "minmax(0, 1fr) minmax(3.25rem, 4rem) minmax(4.75rem, 6rem) 2.5rem",
                              }}
                            >
                              <span
                                className="min-w-0 text-sm font-medium leading-snug [word-break:break-word] sm:truncate sm:[word-break:normal]"
                                title={formatDisplayText(item.insumo_nome)}
                              >
                                {formatDisplayText(item.insumo_nome)}
                              </span>
                              <Input
                                type="number"
                                step="any"
                                min="0"
                                value={item.quantidade}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setProdutoComposicaoItems((prev) =>
                                    prev.map((c) => (c.insumo_id === item.insumo_id ? { ...c, quantidade: v } : c))
                                  );
                                }}
                                placeholder="0"
                                className="h-9 w-full min-w-0 max-w-[4rem] justify-self-stretch px-2 text-center text-sm tabular-nums"
                              />
                              <Select
                                value={item.unidade}
                                onChange={(e) =>
                                  setProdutoComposicaoItems((prev) =>
                                    prev.map((c) =>
                                      c.insumo_id === item.insumo_id ? { ...c, unidade: e.target.value } : c
                                    )
                                  )
                                }
                                className="h-9 w-full min-w-0 px-2 text-sm"
                              >
                                {opcoesUnidade.map((u) => (
                                  <option key={u} value={u}>{u}</option>
                                ))}
                              </Select>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 shrink-0 justify-self-end text-destructive hover:text-destructive"
                                onClick={() => removerInsumoDaComposicao(item.insumo_id)}
                                aria-label="Remover insumo da composição"
                              >
                                <Trash2 className="h-4 w-4 shrink-0" />
                              </Button>
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}

                <ModalPrimaryAction
                  show={produtoModalShowAction}
                  type="submit"
                  loading={savingProduto}
                  disabled={savingProduto}
                >
                  {editingProdutoId ? "Salvar" : "Cadastrar Produto"}
                </ModalPrimaryAction>
              </form>
          </div>
        </ModalBackdrop>

        {/* Modal Adicionar/Editar Insumo */}
        <ModalBackdrop open={showInsumoModal}>
          <div
            className={`mx-auto flex w-full max-w-lg flex-col overflow-y-auto max-sm:min-h-0 max-sm:overflow-hidden ${MODAL_PANEL_MAX_HEIGHT_CLASS} ${MODAL_PANEL_CLASS} text-gray-900`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-insumo-title"
          >
              <div className={MODAL_HEADER_ROW_CLASS}>
                <h2 id="modal-insumo-title" className={MODAL_TITLE_CLASS}>
                  {editingInsumoId ? "Editar Insumo" : "Adicionar Insumo"}
                </h2>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={fecharModalInsumo} aria-label="Fechar">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <form onSubmit={handleSalvarInsumo} className="min-h-0 space-y-4 bg-white p-6 pt-4 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain">
                <div className="space-y-2">
                  <Label htmlFor="insumo_nome">Nome do Insumo *</Label>
                  <Input
                    id="insumo_nome"
                    value={insumoNome}
                    onChange={(e) => setInsumoNome(e.target.value)}
                    placeholder="Ex: Farinha de trigo"
                    required
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insumo_categoria">Categoria *</Label>
                  <CategoryFormSelect
                    id="insumo_categoria"
                    value={insumoCategoria}
                    onChange={setInsumoCategoria}
                    categories={categoriasList}
                    onDeleteCategory={handleExcluirCategoria}
                    showOutrasOption
                    outrasValue={OPCAO_CATEGORIA_OUTRA}
                    outrasLabel="Outras"
                    className="w-full"
                  />
                </div>
                {insumoCategoria === OPCAO_CATEGORIA_OUTRA && (
                  <div className="space-y-2">
                    <Label htmlFor="insumo_categoria_outra">Nova categoria *</Label>
                    <Input
                      id="insumo_categoria_outra"
                      value={insumoCategoriaOutra}
                      onChange={(e) => setInsumoCategoriaOutra(e.target.value)}
                      placeholder="Ex: Doces"
                      className="w-full"
                    />
                    {!isAdmin ? (
                      <p className="text-xs text-amber-800">Somente administrador pode cadastrar uma categoria nova no sistema.</p>
                    ) : null}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="insumo_unidade">Unidade de Medida *</Label>
                  <Select
                    id="insumo_unidade"
                    value={insumoUnidade}
                    onChange={(e) => setInsumoUnidade(e.target.value)}
                    required
                    className="w-full"
                    aria-label="Unidade de medida"
                  >
                    <option value="">Selecione a unidade</option>
                    {INSUMO_UNIDADES_OPCOES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insumo_custo">Custo</Label>
                  <MoneyInput
                    id="insumo_custo"
                    value={insumoCusto}
                    onChange={setInsumoCusto}
                    placeholder="0,00"
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insumo_estoque_minimo">Estoque Mínimo</Label>
                  <Input
                    id="insumo_estoque_minimo"
                    type="number"
                    step="any"
                    min="0"
                    value={insumoEstoqueMinimo}
                    onChange={(e) => setInsumoEstoqueMinimo(e.target.value)}
                    placeholder="0"
                    className="w-full"
                  />
                </div>
                <ModalPrimaryAction
                  show={insumoModalShowAction}
                  type="submit"
                  loading={savingInsumo}
                  disabled={savingInsumo}
                >
                  {savingInsumo
                    ? "Salvando..."
                    : editingInsumoId
                      ? "Salvar"
                      : "Cadastrar Insumo"}
                </ModalPrimaryAction>
              </form>
          </div>
        </ModalBackdrop>

        </div>
      </main>
      </div>
    </div>
  );
}
